// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/sales/settings/ClientVisitForm.jsx (PART 1 OF 2)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  industryData, productCategories, visitTypeOptions, sourceTypeOptions, 
  currencyOptions, paymentStatusOptions, professionOptions, annualTurnoverOptions, 
  registrationOptions, taxTypeOptions, partnerTypeOptions 
} from './IndustryData';
import AllStatesAndDistricts from '../../helpers/AllStatesAndDistricts';
import { Country } from 'country-state-city';
import SelectDropdown from '../../customStyles/SelectDropdown';
import CustomDatePicker from '../../customStyles/CustomDatePicker';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import { Camera, Plus, X, Briefcase, GraduationCap, Package, CreditCard, Target, Layers } from 'lucide-react';
import { useSelector } from 'react-redux';

const ClientVisitForm = () => {
  const user = useSelector((state) => state?.user?.user);
  const { id, leadId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const isConvertMode = !!leadId;
  
  const [salesStages, setSalesStages] = useState([]);
  const [loadingVisit, setLoadingVisit] = useState(false);

  // Business Vertical (isVertical: true) and Product (isVertical: false)
  const [verticalList, setVerticalList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [ourBusinessCategoryList, setOurBusinessCategoryList] = useState([]);

  // Program & Plan States
  const [programList, setProgramList] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [showOtherProductCategory, setShowOtherProductCategory] = useState(false);
  const [otherProductCategory, setOtherProductCategory] = useState('');
  const [showOtherRequirement, setShowOtherRequirement] = useState(false);
  const [otherRequirements, setOtherRequirements] = useState({});
  const [locationImages, setLocationImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [executiveList, setExecutiveList] = useState([]);

  const [formData, setFormData] = useState({
    visitType: '',
    sourceType: '',
    expoYear: '',
    expoPlace: '',
    visitDate: new Date(),
    visitTime: new Date().toTimeString().slice(0, 5),
    nextFollowUpDate: null,
    nextFollowUpTime: '',

    divisionId: '',
    divisionName: '',

    clientName: '',
    industryType: '',
    industrySubType: '',

    // Business Vertical (Common to all divisions where isVertical === true)
    businessVerticalId: '',
    branchId: '',
    // Our Product (CUSO Division specific where isVertical === false)
    ourProductId: '',
    ourProductBusinessCategoryId: '',

    // CUSO Division Only Fields
    branchCount: '',
    employeeCount: '',
    annualTurnover: '',
    registrationType: 'unregistered',
    taxType: 'na',
    taxNumber: '',
    preferredPartnerType: '',

    // BRAWNY & DNBS Only Fields
    studentName: '',
    age: '',
    profession: '',

    // Program & Plan Mapping
    programId: '',
    selectedPlanName: '',

    contactName: '',
    contactDesignation: '',
    contactEmail: '',
    contactPhone: '',
    contactLandline: '',

    address: '',
    locality: '',
    district: '',
    state: '',
    country: 'IN',
    pincode: '',
    latitude: '',
    longitude: '',

    productCategory: [],
    requirement: [],
    estimatedValue: 0,
    expectedValue: 0,
    recurringValue: 0,
    currency: 'INR',

    stageId: '',
    expectedClosingDate: null,
    paymentStatus: 'na',

    remarks: '',
    ownerId: user?._id || ''
  });

  const divisionConfig = {
    RELDA: { icon: Package, title: "Products / Categories" },
    CUSO: { icon: CreditCard, title: "Subscription Plans" },
    DNBS: { icon: GraduationCap, title: "Courses / Programs" },
    BRAWNY: { icon: Target, title: "Programs" },
  };

  const isCusoDivision = Boolean(formData.divisionName?.toLowerCase().includes('cuso'));
  const isDnbsDivision = Boolean(formData.divisionName?.toLowerCase().includes('dnbs'));
  const isBrawnyDivision = Boolean(formData.divisionName?.toLowerCase().includes('brawny'));
  const isDigidaDivision = Boolean(formData.divisionName?.toLowerCase().includes('digida'));
  const isReldaDivision = Boolean(formData.divisionName?.toLowerCase().includes('relda'));
  const isEducationDivision = isBrawnyDivision || isDnbsDivision;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filteredSubIndustries, setFilteredSubIndustries] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [showOtherIndustry, setShowOtherIndustry] = useState(false);
  const [otherIndustry, setOtherIndustry] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [clientData, setClientData] = useState(null);

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const expoYearOptions = [];
  for (let y = currentYear; y >= startYear; y--) {
    expoYearOptions.push({ id: y.toString(), label: y.toString() });
  }  

  const divisionName = formData.divisionName?.trim().toUpperCase() || "";
  const config = divisionName.includes("RELDA")
    ? divisionConfig.RELDA
    : divisionName.includes("CUSO")
    ? divisionConfig.CUSO
    : divisionName.includes("DNBS")
    ? divisionConfig.DNBS
    : divisionName.includes("BRAWNY")
    ? divisionConfig.BRAWNY
    : { icon: Layers, title: "Program / Course / Plans" };

  const SectionIcon = config.icon;

const fetchBranchesForDivision = useCallback(async (divisionId) => {
  if (!divisionId) {
    setBranchList([]);
    return;
  }
  try {
    const response = await api.get(SummaryApi.getAllBranches?.url || '/api/branches', { 
      params: { divisionId, isActive: "true" } 
    });

    if (response.data?.success || Array.isArray(response.data?.data)) {
      const formatted = (response.data.data || []).map(b => ({
        id: b._id || b.id,
        label: b.name || b.branchName,
      }));
      setBranchList(formatted);
    } else {
      setBranchList([]);
    }
  } catch (error) {
    console.error('Error fetching branches:', error);
    setBranchList([]);
  }
}, []);

  // Sync divisionName automatically if divisionId is available but divisionName is missing
  useEffect(() => {
    if (formData.divisionId && !formData.divisionName && divisions.length > 0) {
      const matched = divisions.find(d => d.id === formData.divisionId);
      if (matched) {
        setFormData(prev => ({ ...prev, divisionName: matched.label }));
      }
    }
  }, [formData.divisionId, formData.divisionName, divisions]);

  // Fetch executives list for owner assignment
  const fetchExecutives = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.executiveUsers.url);
      const resData = response.data;
      let execs = [];
      if (Array.isArray(resData?.data)) {
        execs = resData.data;
      } else if (Array.isArray(resData?.users)) {
        execs = resData.users;
      } else if (Array.isArray(resData?.executives)) {
        execs = resData.executives;
      } else if (Array.isArray(resData)) {
        execs = resData;
      }

      const formattedList = execs.map((exec) => {
        const rawId = exec._id || exec.id || exec.userId || '';
        const nameStr = (
          exec.fullName
            ? `${exec.fullName} ${exec.lastName || ''}`.trim()
            : (exec.name || exec.clientName || exec.userName || exec.email || '')
        ) || 'Executive';

        return {
          id: String(rawId),
          _id: String(rawId),
          value: String(rawId),
          label: nameStr,
          name: nameStr
        };
      });

      setExecutiveList(formattedList);
    } catch (error) {
      console.error("Error fetching executives:", error);
    }
  }, []);

  useEffect(() => {
    fetchExecutives();
  }, [fetchExecutives]);

  // Fetch Programs for Selected Division
  const fetchProgramsForDivision = useCallback(async (divisionId) => {
    if (!divisionId) {
      setProgramList([]);
      setAvailablePlans([]);
      return;
    }
    try {
      const response = await api.get(SummaryApi.getAllPrograms.url, { 
        params: { divisionId, isActive: "true" } 
      });

      if (response.data?.success) {
        const formatted = (response.data.data || []).map(p => ({
          id: p._id,
          label: p.name,
          pricingPlans: p.pricingPlans || [],
          offeringType: p.offeringType,
        }));
        setProgramList(formatted);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  }, []);

  // Fetch and split into Business Verticals (isVertical === true) and Products (isVertical === false)
  const fetchProductsAndVerticalsForDivision = useCallback(async (divisionId) => {
    if (!divisionId) {
      setVerticalList([]);
      setProductList([]);
      return;
    }
    try {
      const response = await api.get(SummaryApi.getAllProducts.url, { 
        params: { divisionId, isActive: "true" } 
      });

      if (response.data?.success) {
        const allItems = response.data.data || [];
        
        // Business Vertical: isVertical === true
        const verticals = allItems
          .filter(item => item.isVertical === true)
          .map(item => ({ id: item._id, label: item.name }));

        // Products: isVertical === false / undefined
        const products = allItems
          .filter(item => !item.isVertical)
          .map(item => ({
            id: item._id,
            label: item.name,
            hasSubCategory: item.hasSubCategory || false,
          }));

        setVerticalList(verticals);
        setProductList(products);
      }
    } catch (error) {
      console.error('Error fetching products/verticals:', error);
      setVerticalList([]);
      setProductList([]);
    }
  }, []);

  // Fetch Business Categories based on Selected Product
  const fetchBusinessCategories = useCallback(async (productId) => {
    if (!productId) {
      setOurBusinessCategoryList([]);
      return;
    }
    try {
      const response = await api.get(SummaryApi.getAllBusinessCategories.url, { 
        params: { productId, isActive: "true" }
      });

      if (response.data?.success) {
        const formatted = (response.data.data || []).map(c => ({
          id: c._id,
          label: c.name,
        }));
        setOurBusinessCategoryList(formatted);
      }
    } catch (error) {
      console.error('Error fetching business categories:', error);
      setOurBusinessCategoryList([]);
    }
  }, []);

  // Handler for Business Vertical selection
  const handleBusinessVerticalChange = (verticalId) => {
    setFormData(prev => ({ ...prev, businessVerticalId: verticalId }));
    if (errors.businessVerticalId) setErrors(prev => ({ ...prev, businessVerticalId: '' }));
  };

  // Handler for Product selection in CUSO
  const handleProductChange = (productId) => {
    setFormData(prev => ({
      ...prev,
      ourProductId: productId,
      ourProductBusinessCategoryId: '',
    }));

    if (errors.ourProductId) {
      setErrors(prev => ({ ...prev, ourProductId: '' }));
    }

    fetchBusinessCategories(productId);
  };

  // Handle Program Selection & Load Pricing Plans
  const handleProgramChange = (selectedProgramId) => {
    const selectedProg = programList.find(p => p.id === selectedProgramId);
    const plans = selectedProg?.pricingPlans || [];

    const formattedPlans = plans.map(plan => ({
      id: plan.planName,
      label: `${plan.planName} - ₹${plan.totalAmount?.toLocaleString('en-IN')} (${plan.billingCycle?.replace('_', ' ')})`,
      amount: plan.totalAmount || 0,
      billingCycle: plan.billingCycle,
    }));

    setAvailablePlans(formattedPlans);

    let autoPlanName = '';
    let autoExpectedVal = formData.expectedValue;
    let autoRecurringVal = formData.recurringValue;

    if (formattedPlans.length === 1) {
      autoPlanName = formattedPlans[0].id;
      autoExpectedVal = formattedPlans[0].amount; 
      if (formattedPlans[0].billingCycle === 'monthly') {
        autoRecurringVal = formattedPlans[0].amount;
      }
    }

    setFormData(prev => ({
      ...prev,
      programId: selectedProgramId,
      selectedPlanName: autoPlanName,
      expectedValue: autoPlanName ? autoExpectedVal : prev.expectedValue,
      recurringValue: autoPlanName ? autoRecurringVal : prev.recurringValue,
    }));

    if (errors.programId) {
      setErrors(prev => ({ ...prev, programId: '' }));
    }
  };

  // Handle Pricing Plan Change & Auto-fill Deal Values
  const handlePlanChange = (planName) => {
    const chosenPlan = availablePlans.find(p => p.id === planName);
    
    setFormData(prev => ({
      ...prev,
      selectedPlanName: planName,
      expectedValue: chosenPlan ? chosenPlan.amount : prev.expectedValue,
      recurringValue: chosenPlan?.billingCycle === 'monthly' ? chosenPlan.amount : prev.recurringValue,
    }));
  };

  // Handler for Assigned Executive / Owner dropdown change
  const handleOwnerChange = (selectedVal) => {
    let idValue = selectedVal;
    if (selectedVal && typeof selectedVal === 'object') {
      idValue = selectedVal.id || selectedVal._id || selectedVal.value;
    }

    setFormData(prev => ({
      ...prev,
      ownerId: String(idValue || '')
    }));

    if (errors.ownerId) {
      setErrors(prev => ({ ...prev, ownerId: '' }));
    }
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      return file;
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    if (locationImages.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    const compressedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.size > 2 * 1024 * 1024) {
          const compressed = await compressImage(file);
          return compressed;
        }
        return file;
      })
    );

    setLocationImages(prev => [...prev, ...compressedFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    if (locationImages.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    const compressedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.size > 2 * 1024 * 1024) {
          const compressed = await compressImage(file);
          return compressed;
        }
        return file;
      })
    );

    setLocationImages(prev => [...prev, ...compressedFiles]);
  };

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'ClientVisitForm/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Reverse geocoding error:', error);

      try {
        const fallbackResponse = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
        );

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();

          let district = '';
          if (fallbackData.localityInfo && fallbackData.localityInfo.administrative) {
            const districtInfo = fallbackData.localityInfo.administrative.find(
              admin => admin.description?.toLowerCase().includes('district') || admin.adminLevel === 5
            );
            district = districtInfo ? districtInfo.name.replace(' district', '') : '';
          }

          return {
            display_name: fallbackData.locality || '',
            address: {
              road: fallbackData.street || '',
              neighbourhood: fallbackData.locality || '',
              suburb: fallbackData.suburb || '',
              city: fallbackData.city || '',
              state_district: district,
              state: fallbackData.principalSubdivision || '',
              country: fallbackData.countryName || '',
              country_code: fallbackData.countryCode?.toLowerCase() || '',
              postcode: fallbackData.postcode || ''
            }
          };
        }
      } catch (fallbackError) {
        console.error('Fallback geocoding also failed:', fallbackError);
      }

      return null;
    }
  };

  const setDistrictFromGeocode = (addressData, matchedState) => {
    let apiDistrictName = addressData.state_district || addressData.county || addressData.city || '';

    apiDistrictName = apiDistrictName
      .replace(/ district$/i, '')
      .replace(/ District$/i, '')
      .replace(/ Dist\.?$/i, '')
      .replace(/ Zilla$/i, '')
      .replace(/ Jilla$/i, '')
      .trim();

    const stateDistricts = AllStatesAndDistricts[matchedState] || [];
    let matchedDistrict = findClosestMatch(apiDistrictName, stateDistricts);

    if (!matchedDistrict && apiDistrictName) {
      if (addressData.city && addressData.city !== apiDistrictName) {
        matchedDistrict = findClosestMatch(addressData.city, stateDistricts);
      }
      if (!matchedDistrict && addressData.locality) {
        matchedDistrict = findClosestMatch(addressData.locality, stateDistricts);
      }
    }

    if (matchedDistrict) {
      setFormData(prev => ({ ...prev, district: matchedDistrict }));
    } else {
      setFormData(prev => ({ ...prev, district: '' }));
    }
  };

  const findClosestMatch = (searchTerm, list) => {
    if (!searchTerm || !list || list.length === 0) return null;

    const normalizedSearch = searchTerm.toLowerCase().trim();
    const exactMatch = list.find(item => item.toLowerCase().trim() === normalizedSearch);
    if (exactMatch) return exactMatch;

    const searchWithoutCommonWords = normalizedSearch
      .replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '')
      .trim();

    const matchWithoutCommonWords = list.find(item =>
      item.toLowerCase().replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '').trim() === searchWithoutCommonWords
    );
    if (matchWithoutCommonWords) return matchWithoutCommonWords;

    const partialMatch = list.find(item => {
      const normalizedItem = item.toLowerCase();
      return normalizedItem.includes(normalizedSearch) ||
        normalizedSearch.includes(normalizedItem) ||
        normalizedItem.includes(searchWithoutCommonWords) ||
        searchWithoutCommonWords.includes(normalizedItem);
    });
    if (partialMatch) return partialMatch;

    const firstWord = normalizedSearch.split(' ')[0];
    if (firstWord.length > 3) {
      const firstWordMatch = list.find(item => item.toLowerCase().startsWith(firstWord));
      if (firstWordMatch) return firstWordMatch;
    }

    return null;
  };

  const getDistrictsForState = (stateName) => {
    if (!stateName || !AllStatesAndDistricts) return [];
    
    if (AllStatesAndDistricts[stateName]) {
      return AllStatesAndDistricts[stateName].map(d => ({ id: d, label: d }));
    }
    
    const matchedStateKey = Object.keys(AllStatesAndDistricts).find(
      key => key.trim().toLowerCase() === stateName.trim().toLowerCase()
    );

    if (matchedStateKey && AllStatesAndDistricts[matchedStateKey]) {
      return AllStatesAndDistricts[matchedStateKey].map(d => ({ id: d, label: d }));
    }

    return [];
  };

  // 2. Country மாறும் போது:
  const handleCountryChange = (countryCode) => {
    let newStates = [];
    if (countryCode === 'IN') {
      newStates = Object.keys(AllStatesAndDistricts).map(state => ({
        id: state,
        label: state
      }));
    }
    setStates(newStates);
    setDistricts([]); 

    setFormData(prev => ({
      ...prev,
      country: countryCode,
      state: '',
      district: ''
    }));
  };

  const handleStateChange = (stateName) => {
    const stateDistricts = getDistrictsForState(stateName);
    setDistricts(stateDistricts);

    setFormData(prev => ({
      ...prev,
      state: stateName,
      district: '' 
    }));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      toast.info('Getting your location...');

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          setFormData(prev => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lon.toFixed(6)
          }));

          const addressData = await reverseGeocode(lat, lon);

          if (addressData) {
            if (addressData.address) {
              const addr = addressData.address;

              const addressParts = [];
              if (addr.house_number) addressParts.push(addr.house_number);
              if (addr.road || addr.street) addressParts.push(addr.road || addr.street);
              if (addr.neighbourhood) addressParts.push(addr.neighbourhood);
              if (addr.suburb) addressParts.push(addr.suburb);
              if (addr.city && addr.city !== addr.neighbourhood && addr.city !== addr.suburb) {
                addressParts.push(addr.city);
              }

              const fullAddress = addressParts.length > 0
                ? addressParts.filter(Boolean).join(', ')
                : addressData.display_name?.split(',').slice(0, 3).join(',') || '';

              const apiStateName = addr.state || addr.principalSubdivision || '';
              const matchedState = findClosestMatch(apiStateName, Object.keys(AllStatesAndDistricts));

              if (matchedState) {
                setFormData(prev => ({
                  ...prev,
                  address: fullAddress,
                  locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
                  pincode: addr.postcode || prev.pincode
                }));

                const countryCode = addr.country_code ? addr.country_code.toUpperCase() : 'IN';

                if (countryCode !== formData.country) {
                  handleCountryChange(countryCode);
                  setTimeout(() => {
                    handleStateChange(matchedState);
                    setTimeout(() => {
                      setDistrictFromGeocode(addr, matchedState);
                      setLocationLoading(false);
                    }, 300);
                  }, 200);
                } else {
                  handleStateChange(matchedState);
                  setTimeout(() => {
                    setDistrictFromGeocode(addr, matchedState);
                    setLocationLoading(false);
                  }, 300);
                }

                toast.success('Location and address captured successfully!');
              } else {
                setFormData(prev => ({
                  ...prev,
                  address: fullAddress,
                  locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
                  state: apiStateName,
                  pincode: addr.postcode || prev.pincode
                }));

                setLocationLoading(false);
                toast.warning('Location captured, but state/district may need manual adjustment');
              }
            }
          } else {
            setLocationLoading(false);
            toast.warning('Location captured, but could not fetch address details. Please fill manually.');
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationLoading(false);

          let errorMessage = 'Unable to get location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please allow location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage += 'Request timed out. Please try again.';
              break;
            default:
              errorMessage += 'An unknown error occurred.';
          }
          toast.error(errorMessage);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };


  const fetchDivisions = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllDivisions.url);
      const divisionsData = response.data.data || [];
      setDivisions(divisionsData.map(div => ({
        id: div._id,
        label: div.name
      })));
    } catch (error) {
      console.error('Error fetching divisions:', error);
      setDivisions([]);
    }
  }, []);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const fetchSalesStages = useCallback(async (selectedDivisionId = null) => {
    if (!selectedDivisionId) {
      setSalesStages([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        includeInactive: false,
        includeShared: true,
        divisionId: selectedDivisionId,
      };
      const response = await api.get(SummaryApi.getAllStages.url, { params });
      if (response.data.success) {
        setSalesStages(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch stages:', error);
      setSalesStages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.divisionId) {
      fetchSalesStages(formData.divisionId);
    }
  }, [formData.divisionId, fetchSalesStages]);

  // Convert Mode: Load Lead details
  const fetchOpenLeadDetails = useCallback(async () => {
    setLoadingVisit(true);
    try {
      let lead = null;

      if (SummaryApi.getOneExpoLead?.url) {
        try {
          const expoRes = await api.get(SummaryApi.getOneExpoLead.url(leadId));
          if (expoRes.data?.success) {
            lead = expoRes.data.data;
          }
        } catch {
          console.log('Expo Lead fetch fallback...');
        }
      }

      if (!lead) {
        try {
          const response = await api.get(SummaryApi.getOpenLeadById.url(leadId));
          if (response.data?.success) {
            lead = response.data.data;
          }
        } catch (err) {
          console.error('Lead lookup error:', err);
        }
      }

      if (lead) {
        let matchedIndustryCategory = '';
        let rawIndustryText = lead.industry || '';
        if (!rawIndustryText && lead.division) {
          rawIndustryText = Array.isArray(lead.division)
            ? lead.division.join(' ')
            : String(lead.division);
        }

        if (rawIndustryText) {
          const targetText = String(rawIndustryText).toLowerCase().trim();
          const foundIndustry = industryData.find(ind => {
            const catLower = ind.industryCategory.toLowerCase().trim();
            return catLower === targetText || catLower.includes(targetText) || targetText.includes(catLower);
          });
          if (foundIndustry) {
            matchedIndustryCategory = foundIndustry.industryCategory;
          }
        }

        let matchedProductCategories = [];
        let matchedRequirements = [];
        const rawRequirementText = lead.requirement || '';

        if (rawRequirementText) {
          productCategories.forEach(cat => {
            if (rawRequirementText.toLowerCase().includes(cat.productCategory.toLowerCase())) {
              matchedProductCategories.push(cat.productCategory);
            }
            cat.subCategories.forEach(sub => {
              if (rawRequirementText.toLowerCase().includes(sub.toLowerCase())) {
                if (!matchedProductCategories.includes(cat.productCategory)) {
                  matchedProductCategories.push(cat.productCategory);
                }
                matchedRequirements.push(sub);
              }
            });
          });
        }

        const parsedDivisionId = Array.isArray(lead.division)
          ? lead.division[0] || ''
          : lead.division || '';

        const parsedOwnerId = lead.ownerId
          ? String(typeof lead.ownerId === 'object' ? (lead.ownerId._id || lead.ownerId.id) : lead.ownerId)
          : '';

        const leadVerticalId = lead.businessVerticalId?._id || lead.businessVerticalId || '';
        const leadBranchId = lead.branchId?._id || lead.branchId || '';
        const leadProductId = lead.ourProductId?._id || lead.ourProductId || '';
        const leadCategoryId = lead.ourProductBusinessCategoryId?._id || lead.ourProductBusinessCategoryId || '';

        if (parsedDivisionId) {
          fetchProductsAndVerticalsForDivision(parsedDivisionId);
          fetchProgramsForDivision(parsedDivisionId);
          fetchSalesStages(parsedDivisionId);
        }

        if (leadProductId) {
          fetchBusinessCategories(leadProductId);
        }

        setFormData(prev => ({
          ...prev,
          clientName: lead.companyOrSchool || lead.fullName || '',
          industryType: matchedIndustryCategory || "Education & Training",
          contactName: lead.fullName || '',
          contactDesignation: lead.designation || '',
          contactEmail: lead.email || '',
          contactPhone: lead.mobile || '',
          address: lead.address || lead.city || '',
          locality: lead.locality || lead.city || '',
          divisionId: parsedDivisionId || prev.divisionId,
          businessVerticalId: leadVerticalId,
          branchId: leadBranchId,
          ourProductId: leadProductId,
          ourProductBusinessCategoryId: leadCategoryId,
          sourceType: lead.source === 'expo' || lead.expoId ? 'expo' : (lead.source || 'expo'),
          expoYear: lead.expoId?.expoYear ? String(lead.expoId.expoYear) : new Date().getFullYear().toString(),
          expoPlace: lead.expoId?.expoName || lead.source || '',
          productCategory: matchedProductCategories,
          requirement: matchedRequirements,
          remarks: lead.requirement || lead.remarks || '',
          ownerId: parsedOwnerId || prev.ownerId
        }));

        toast.success(`Loaded Lead: ${lead.fullName}`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load lead information');
    } finally {
      setLoadingVisit(false);
    }
  }, [leadId, fetchProductsAndVerticalsForDivision, fetchProgramsForDivision, fetchSalesStages, fetchBusinessCategories]);

  // Edit Mode: Load Visit Data with ALL fields restored
  const fetchVisitData = useCallback(async () => {
    setLoadingVisit(true);
    try {
      const response = await api.get(SummaryApi.getOneClientVisit.url(id));

      if (response.data.success) {
        const visit = response.data.data;
        setClientData(visit);

        if (visit.locationImages && visit.locationImages.length > 0) {
          setExistingImages(visit.locationImages);
        }

        const visitDivisionId = visit.divisionId?._id || visit.divisionId || '';
        const visitVerticalId = visit.businessVerticalId?._id || visit.businessVerticalId || '';
        const visitBranchId = visit.branchId?._id || visit.branchId || '';
        const visitProductId = visit.ourProductId?._id || visit.ourProductId || '';
        const visitCategoryId = visit.ourProductBusinessCategoryId?._id || visit.ourProductBusinessCategoryId || '';

        // Load division options on edit
        if (visitDivisionId) {
          fetchProductsAndVerticalsForDivision(visitDivisionId);
          fetchProgramsForDivision(visitDivisionId);
          fetchSalesStages(visitDivisionId);
          fetchBranchesForDivision(visitDivisionId);
        }

        if (visitProductId) {
          fetchBusinessCategories(visitProductId);
        }

        let industrySubType = visit.industrySubType || '';
        if (industrySubType.startsWith('Others - ')) {
          setShowOtherIndustry(true);
          setOtherIndustry(industrySubType.substring(9));
          industrySubType = 'Others';
        }
        
        let productCats = [...(visit.productCategory || [])];
        let otherCategoryText = "";
        
        const otherCategoryIndex = productCats.findIndex(cat => cat.startsWith("Others - "));
        if (otherCategoryIndex !== -1) {
          otherCategoryText = productCats[otherCategoryIndex].substring(9);
          productCats[otherCategoryIndex] = "Others - Mention that category";
          setOtherProductCategory(otherCategoryText);
          setShowOtherProductCategory(true);
        }

        let requirements = [...(visit.requirement || [])];
        const otherReqs = {};
        
        requirements = requirements.map(req => {
          if (req.startsWith('Others - ')) {
            const value = req.substring(9);
            const newKey = "Others - Mention that product";
            otherReqs[newKey] = value;
            return newKey;
          }
          return req;
        });
        const visitCountry = visit.country || 'IN';
        const visitState = visit.state || '';

        if (visitCountry === 'IN') {
          setStates(Object.keys(AllStatesAndDistricts).map(s => ({ id: s, label: s })));
        }

        if (visitState) {
          const loadedDistricts = getDistrictsForState(visitState);
          setDistricts(loadedDistricts);
        }
        setOtherRequirements(otherReqs);
        setShowOtherRequirement(Object.keys(otherReqs).length > 0);

        const parsedOwnerId = visit.ownerId
          ? String(typeof visit.ownerId === 'object' ? (visit.ownerId._id || visit.ownerId.id) : visit.ownerId)
          : '';

        setFormData({
          visitType: visit.visitHistory?.[0]?.visitType || '',
          sourceType: visit.sourceType || '',
          expoYear: visit?.expoYear || '',
          expoPlace: visit?.expoPlace || '',
          visitDate: visit.visitHistory?.[0]?.visitDate ? new Date(visit.visitHistory[0].visitDate) : null,
          visitTime: visit.visitHistory?.[0]?.visitTime || new Date().toTimeString().slice(0, 5),
          nextFollowUpDate: visit.nextFollowUpDate ? new Date(visit.nextFollowUpDate) : null,
          nextFollowUpTime: visit.nextFollowUpTime || '',
          divisionId: visitDivisionId,
          divisionName: visit.divisionId?.name || '',
          clientName: visit.clientName || '',
          industryType: visit.industryType || '',
          industrySubType: industrySubType || '',

          // Prefill Verticals and Products in Edit Mode
          businessVerticalId: visitVerticalId,
          branchId : visitBranchId,
          ourProductId: visitProductId,
          ourProductBusinessCategoryId: visitCategoryId,

          // Prefill CUSO Fields in Edit Mode
          branchCount: visit.branchCount !== undefined && visit.branchCount !== null ? String(visit.branchCount) : '',
          employeeCount: visit.employeeCount !== undefined && visit.employeeCount !== null ? String(visit.employeeCount) : '',
          annualTurnover: visit.annualTurnover || '',
          registrationType: visit.registrationType || 'unregistered',
          taxType: visit.taxType || 'na',
          taxNumber: visit.taxNumber || '',
          preferredPartnerType: visit.preferredPartnerType || '',

          // Prefill Brawny/DNBS Fields in Edit Mode
          studentName: visit.studentName || '',
          age: visit.age !== undefined && visit.age !== null ? String(visit.age) : '',
          profession: visit.profession || '',

          programId: visit.programId?._id || visit.programId || '',
          selectedPlanName: visit.selectedPlanName || '',
          contactName: visit.contactName || '',
          contactDesignation: visit.contactDesignation || '',
          contactEmail: visit.contactEmail || '',
          contactPhone: visit.contactPhone || '',
          contactLandline: visit.contactLandline || '',
          address: visit.address || '',
          locality: visit.locality || '',
          district: visit.district || '',
          state: visit.state || '',
          country: visit.country || 'IN',
          pincode: visit.pincode || '',
          latitude: visit.latitude || '',
          longitude: visit.longitude || '',
          productCategory: productCats || [],
          requirement: requirements || [],
          estimatedValue: visit.estimatedValue || 0,
          expectedValue: visit.expectedValue || 0,
          recurringValue: visit.recurringValue || 0,
          currency: visit.currency || 'INR',
          stageId: visit.stageId?._id || '',
          expectedClosingDate: visit.expectedClosingDate ? new Date(visit.expectedClosingDate) : null,
          paymentStatus: visit.paymentStatus || 'na',
          remarks: visit.visitHistory?.[0]?.remarks || '',
          ownerId: parsedOwnerId,
        });
      }
    } catch (error) {
      console.error('Error fetching visit data:', error);
      toast.error('Failed to load visit data. Please try again.');
      navigate(-1);
    } finally {
      setLoadingVisit(false);
    }
  }, [id, fetchProductsAndVerticalsForDivision, fetchProgramsForDivision, fetchSalesStages, fetchBranchesForDivision, fetchBusinessCategories, navigate]);

  useEffect(() => {
    if (isEditMode && id) {
      fetchVisitData();
    } else if (isConvertMode && leadId) {
      fetchOpenLeadDetails();
    }
  }, [isEditMode, isConvertMode, id, leadId, fetchVisitData, fetchOpenLeadDetails]);

  useEffect(() => {
    const allCountries = Country.getAllCountries().map(country => ({
      isoCode: country.isoCode,
      name: country.name
    }));
    setCountries(allCountries);
  }, []);

  useEffect(() => {
    if (formData.industryType) {
      const industry = industryData.find(ind => ind.industryCategory === formData.industryType);
      if (industry) {
        let subCategories = industry.subCategories.map(sub => ({
          id: sub,
          label: sub
        }));
        
        if (!subCategories.some(sub => sub.id === 'Others')) {
          subCategories.push({ id: 'Others', label: 'Others' });
        }
        
        setFilteredSubIndustries(subCategories);
      }
    } else {
      setFilteredSubIndustries([]);
      if (!isEditMode) {
        setFormData(prev => ({ ...prev, industrySubType: '' }));
      }
    }
  }, [formData.industryType, isEditMode]);

  useEffect(() => {
    setShowOtherIndustry(formData.industrySubType === 'Others');
  }, [formData.industrySubType]);

  useEffect(() => {
    if (isEditMode && clientData) {
      const otherCategoryMatch = clientData.productCategory?.find(cat => cat.includes('Others - '));
      if (otherCategoryMatch) {
        const categoryMatch = otherCategoryMatch.match(/Others - (.+)/);
        if (categoryMatch) {
          setOtherProductCategory(categoryMatch[1]);
          setShowOtherProductCategory(true);
        }
      }
    }
  }, [isEditMode, clientData]);

  useEffect(() => {
    if (formData.productCategory && formData.productCategory.length > 0) {
      const allRequirements = [];
      formData.productCategory.forEach(categoryId => {
        const category = productCategories.find(cat => cat.productCategory === categoryId);
        if (category) {
          category.subCategories.forEach(sub => {
            if (!allRequirements.find(req => req.id === sub)) {
              allRequirements.push({ id: sub, label: sub });
            }
          });
        }
      });
      setFilteredRequirements(allRequirements);
    } else {
      setFilteredRequirements([]);
      if (!isEditMode) {
        setFormData(prev => ({ ...prev, requirement: [] }));
      }
    }
  }, [formData.productCategory, isEditMode]);

  useEffect(() => {
    if (formData.state && AllStatesAndDistricts[formData.state]) {
      const stateDistricts = AllStatesAndDistricts[formData.state].map(district => ({
        id: district,
        label: district
      }));
      setDistricts(stateDistricts);
    } else {
      setDistricts([]);
      if (!isEditMode) {
        setFormData(prev => ({ ...prev, district: '' }));
      }
    }
  }, [formData.state, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let processedValue = value;

    if (type === "number") {
      if (value === "0") {
        processedValue = "0";
      } else {
        processedValue = value.replace(/^0+/, '');
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProductCategoryChange = (selectedCategories) => {
    setFormData(prev => ({ ...prev, productCategory: selectedCategories }));

    const hasOthers = selectedCategories.includes("Others - Mention that category");
    setShowOtherProductCategory(hasOthers);

    if (!hasOthers) {
      setOtherProductCategory('');
    }

    updateAvailableRequirements(selectedCategories);
  };

  const updateAvailableRequirements = (selectedCategories) => {
    if (!selectedCategories || selectedCategories.length === 0) {
      setFilteredRequirements([]);
      if (!isEditMode) {
        setFormData(prev => ({ ...prev, requirement: [] }));
      }
      return;
    }

    const allRequirements = [];
    selectedCategories.forEach(categoryId => {
      const category = productCategories.find(cat => cat.productCategory === categoryId);
      if (category) {
        category.subCategories.forEach(sub => {
          if (!allRequirements.find(req => req.id === sub)) {
            allRequirements.push({ id: sub, label: sub });
          }
        });
      }
    });
    
    setFilteredRequirements(allRequirements);

    const validRequirementIds = new Set(allRequirements.map(req => req.id));
    const updatedRequirements = formData.requirement.filter(req => 
      validRequirementIds.has(req) || req === "Others - Mention that product"
    );
    
    if (updatedRequirements.length !== formData.requirement.length) {
      setFormData(prev => ({ ...prev, requirement: updatedRequirements }));
    }
  };

  const handleIndustrySubTypeChange = (value) => {
    setFormData(prev => ({ ...prev, industrySubType: value }));
    setShowOtherIndustry(value === 'Others');
    if (value !== 'Others') {
      setOtherIndustry('');
    }
  };

  const handleRequirementChange = (selectedRequirements) => {
    setFormData(prev => ({ ...prev, requirement: selectedRequirements }));

    const selectedOthers = selectedRequirements.filter(req => 
      req.includes('Others - Mention that product')
    );
    setShowOtherRequirement(selectedOthers.length > 0);

    const newOtherRequirements = {};
    selectedOthers.forEach(req => {
      if (otherRequirements[req]) {
        newOtherRequirements[req] = otherRequirements[req];
      } else {
        newOtherRequirements[req] = '';
      }
    });
    setOtherRequirements(newOtherRequirements);
  };

  const handleOtherRequirementChange = (category, value) => {
    setOtherRequirements(prev => ({
      ...prev,
      [category]: value
    }));
  };

  // Division Change triggers fetching specific products and verticals
  const handleDivisionChange = (divisionId) => {
    const selectedDivision = divisions.find(div => div.id === divisionId);
    const divName = selectedDivision?.label || '';

    setFormData(prev => ({
      ...prev,
      divisionId: divisionId,
      divisionName: divName,
      stageId: '', 
      businessVerticalId: '',
      branchId: '',
      ourProductId: '',
      ourProductBusinessCategoryId: '',
      programId: '',
      selectedPlanName: '',
    }));

    setVerticalList([]);
    setProductList([]);
    setBranchList([]); // Reset branch list
    setOurBusinessCategoryList([]);
    if (errors.divisionId) setErrors(prev => ({ ...prev, divisionId: '' }));

    fetchProductsAndVerticalsForDivision(divisionId);
    fetchProgramsForDivision(divisionId);
    fetchSalesStages(divisionId);
    fetchBranchesForDivision(divisionId); // <-- Inga branch fetch panrom
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.divisionId) newErrors.divisionId = 'Division is required';
    // if (!formData.businessVerticalId) newErrors.businessVerticalId = 'Business Vertical is required';
    if (!formData.clientName) newErrors.clientName = 'Client / Parent name is required';

    if (isEducationDivision && formData.age && Number(formData.age) < 18 && !formData.studentName?.trim()) {
      newErrors.studentName = 'Student name is required for minors (<18)';
    }

    if (!isEducationDivision && !formData.industryType) {
      newErrors.industryType = 'Industry type is required';
    }

    if (!formData.contactName) newErrors.contactName = 'Contact name is required';

    if (isCusoDivision && !formData.ourProductId) {
      newErrors.ourProductId = 'Our ERP Product is required for CUSO division';
    }

    if (isDigidaDivision) {
      if (!formData.productCategory || formData.productCategory.length === 0) {
        newErrors.productCategory = 'Product Category is required for Digida division';
      }
      if (!formData.requirement || formData.requirement.length === 0) {
        newErrors.requirement = 'Requirement is required for Digida division';
      }
    }

    if (!formData.contactPhone && !formData.contactLandline) {
      newErrors.contactPhone = 'Either Contact Phone or Landline is required';
      newErrors.contactLandline = 'Either Contact Phone or Landline is required';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (formData.contactPhone && !phoneRegex.test(formData.contactPhone.replace(/\D/g, ''))) {
      newErrors.contactPhone = 'Invalid phone number (10 digits required)';
    }

    if (!formData.stageId) newErrors.stageId = 'Stage is required';
    
    if (formData.estimatedValue === '' || formData.estimatedValue === null || formData.estimatedValue === undefined) {
      newErrors.estimatedValue = 'Invoice Value is required';
    }

    if (!formData.visitDate && !isEditMode) newErrors.visitDate = 'Call date is required';
    if (!formData.visitType && !isEditMode) newErrors.visitType = 'Call type is required';
    if (!formData.sourceType && !isEditMode) newErrors.sourceType = 'Source type is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setSubmitSuccess(false);
    setSubmitMessage('');

    try {
      let processedIndustrySubType = formData.industrySubType;
      if (formData.industrySubType === 'Others' && otherIndustry.trim()) {
        processedIndustrySubType = `Others - ${otherIndustry}`;
      }

      const processedProductCategories = formData.productCategory.map(category => {
        if (category === "Others - Mention that category" && otherProductCategory.trim()) {
          return `Others - ${otherProductCategory}`;
        }
        return category;
      });

      const processedRequirements = formData.requirement.map(req => {
        if (req === "Others - Mention that product" && otherRequirements[req]) {
          return `Others - ${otherRequirements[req]}`;
        }
        return req;
      });

      const formDataToSend = new FormData();

      const submitData = {
        ...formData,
        programId: formData.programId || null,
        selectedPlanName: formData.selectedPlanName || '',
        
        // Vertical and Product IDs sent cleanly
        businessVerticalId: formData.businessVerticalId || null,
        ourProductId: formData.ourProductId || null,

        // CUSO Specific Fields
        branchCount: isCusoDivision && formData.branchCount ? Number(formData.branchCount) : null,
        employeeCount: isCusoDivision && formData.employeeCount ? Number(formData.employeeCount) : null,
        annualTurnover: isCusoDivision ? formData.annualTurnover : '',
        registrationType: isCusoDivision ? formData.registrationType : 'unregistered',
        taxType: isCusoDivision && formData.registrationType === 'registered' ? formData.taxType : 'na',
        taxNumber: isCusoDivision && formData.registrationType === 'registered' ? formData.taxNumber.trim().toUpperCase() : '',
        preferredPartnerType: isCusoDivision ? formData.preferredPartnerType : '',
        ourProductBusinessCategoryId: isCusoDivision ? (formData.ourProductBusinessCategoryId || null) : null,

        // BRAWNY & DNBS Specific Fields
        studentName: isEducationDivision ? (formData.studentName?.trim() || '') : '',
        age: isEducationDivision && formData.age ? Number(formData.age) : null,
        profession: isEducationDivision ? (formData.profession?.trim() || '') : '',

        expoYear: formData.sourceType === 'expo' ? formData.expoYear : '',
        expoPlace: formData.sourceType === 'expo' ? formData.expoPlace : '',
        industrySubType: processedIndustrySubType,
        productCategory: processedProductCategories,
        requirement: processedRequirements,
        divisionName: formData.divisionName,
        nextFollowUpDate: formData.nextFollowUpDate,
        estimatedValue: parseFloat(formData.estimatedValue) || 0,
        expectedValue: parseFloat(formData.expectedValue) || 0,
        recurringValue: parseFloat(formData.recurringValue) || 0,
      };

      if (isEditMode) {
        delete submitData.visitDate;
        delete submitData.visitTime;
        delete submitData.visitType;
        delete submitData.remarks;

        submitData.existingImages = existingImages;
      } else {
        submitData.initialVisitDate = formData.visitDate;
        submitData.initialVisitTime = formData.visitTime;
      }

      if (isConvertMode) {
        submitData.leadId = leadId;
      }

      Object.keys(submitData).forEach(key => {
        if (submitData[key] !== null && submitData[key] !== undefined) {
          if (Array.isArray(submitData[key])) {
            formDataToSend.append(key, JSON.stringify(submitData[key]));
          } else if (submitData[key] instanceof Date) {
            formDataToSend.append(key, submitData[key].toISOString());
          } else {
            formDataToSend.append(key, submitData[key]);
          }
        }
      });

      locationImages.forEach((image) => {
        formDataToSend.append('locationImages', image);
      });

      let targetUrl = '';
      if (isEditMode) {
        targetUrl = typeof SummaryApi.updateClientVisit.url === 'function' ? SummaryApi.updateClientVisit.url(id) : SummaryApi.updateClientVisit.url;
      } else if (isConvertMode) {
        const apiConfig = SummaryApi.convertExpoLeadToVisit || SummaryApi.convertLeadToVisit;
        targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(leadId) : apiConfig.url;
      } else {
        targetUrl = typeof SummaryApi.createClientVisit.url === 'function' ? SummaryApi.createClientVisit.url() : SummaryApi.createClientVisit.url;
      }

      const response = isEditMode
        ? await api.put(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (response.data.success) {
        setSubmitSuccess(true);
        const message = isEditMode
          ? 'Client visit updated successfully!'
          : `Client visit created successfully!\nClient ID: ${response.data.data.clientId}`;

        setSubmitMessage(message);
        toast.success(message);

        setTimeout(() => {
          resetForm();
          setSubmitSuccess(false);
          setSubmitMessage('');
          navigate(-1);
        }, 1500);
      } else {
        toast.error(response.data.message || 'Unexpected response from server.');
      }
    } catch (error) {
      console.error('Error submitting form details:', error);
      toast.error(error.response?.data?.message || 'Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      visitType: '',
      sourceType: '',
      expoYear: '',
      expoPlace: '',
      visitDate: new Date(),
      visitTime: new Date().toTimeString().slice(0, 5),
      nextFollowUpDate: null,
      nextFollowUpTime: '',
      divisionId: '',
      divisionName: '',
      clientName: '',
      studentName: '',
      age: '',
      profession: '',
      industryType: '',
      industrySubType: '',
      businessVerticalId: '',
      branchId: '',
      ourProductId: '',
      ourProductBusinessCategoryId: '',
      branchCount: '',
      employeeCount: '',
      annualTurnover: '',
      registrationType: 'unregistered',
      taxType: 'na',
      taxNumber: '',
      preferredPartnerType: '',
      programId: '',
      selectedPlanName: '',
      contactName: '',
      contactDesignation: '',
      contactEmail: '',
      contactPhone: '',
      contactLandline: '',
      address: '',
      locality: '',
      district: '',
      state: '',
      country: 'IN',
      pincode: '',
      latitude: '',
      longitude: '',
      productCategory: [],
      requirement: [],
      estimatedValue: 0,
      expectedValue: 0,
      recurringValue: 0,
      currency: 'INR',
      stageId: '',
      expectedClosingDate: null,
      paymentStatus: 'na',
      remarks: '',
      ownerId: user?._id || ''
    });
    setOurBusinessCategoryList([]);
    setVerticalList([]);
    setProductList([]);
    setProgramList([]);
    setAvailablePlans([]);
    setErrors({});
    setOtherIndustry('');
    setOtherRequirements({});
    setShowOtherRequirement(false);
    setLocationImages([]);
    setExistingImages([]);
  };

  const industryOptions = industryData.map(ind => ({
    id: ind.industryCategory,
    label: `${ind.icon} ${ind.industryCategory}`
  }));

  useEffect(() => {
    if (formData.country === 'IN') {
      setStates(Object.keys(AllStatesAndDistricts).map(state => ({
        id: state,
        label: state
      })));
    }
  }, [formData.country]);

  const productCategoryOptions = productCategories.map(cat => ({
    id: cat.productCategory,
    label: cat.productCategory
  }));

  const customClassName = (value) => `bg-transparent border outline-none shadow-xs rounded-[7px] px-2 py-1.5 ${value ? "border-blue-600 text-blue-600" : "border-gray-300"}`;

  if (loadingVisit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Loading visit data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div>
        {submitSuccess && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            <p className="font-medium">{submitMessage}</p>
          </div>
        )}

        <div className="bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-600 px-6 py-2">
            <div className="flex justify-between items-center text-white">
              <div>
                <h1 className="text-2xl font-bold">
                  {isEditMode ? 'Edit Client Visit' : (isConvertMode ? 'Convert Expo Lead to Client Visit' : 'Client Visit Form')}
                </h1>
                <p className="text-red-100 text-sm mt-0.5">
                  {isEditMode ? `Editing visit for Client ID: ${clientData?.clientId}` : 'Record new client visit and lead details'}
                </p>
              </div>
              <button onClick={() => navigate(-1)} className="hover:text-red-100" title="Go back">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-2 space-y-2.5">
            
            {/* 1. Visit Information: HIDDEN IN EDIT MODE */}
            {!isEditMode && (
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">📅</span> Visit Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Division <span className="text-red-500">*</span></label>
                    <SelectDropdown options={divisions} value={formData.divisionId} onChange={handleDivisionChange} placeholder="Select Division" valueKey="id" labelKey="label" error={errors.divisionId} />
                    {errors.divisionId && <p className="text-red-500 text-xs mt-1">{errors.divisionId}</p>}
                  </div>

                  {/* Business Vertical (Filtered: isVertical === true) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Vertical <span className="text-red-500">*</span>
                    </label>
                    <SelectDropdown
                      options={verticalList}
                      value={formData.businessVerticalId}
                      onChange={handleBusinessVerticalChange}
                      placeholder={formData.divisionId ? "Select Business Vertical" : "Select Division First"}
                      searchable={true}
                      valueKey="id"
                      labelKey="label"
                      disabled={!formData.divisionId}
                      // error={errors.businessVerticalId}
                    />
                    {/* {errors.businessVerticalId && <p className="text-red-500 text-xs mt-1">{errors.businessVerticalId}</p>} */}
                  </div>
                  {branchList.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Branch <span className="text-red-500">*</span>
                      </label>
                      <SelectDropdown
                        options={branchList}
                        value={formData.branchId}
                        onChange={(branchId) => {
                          setFormData(prev => ({ ...prev, branchId }));
                          if (errors.branchId) setErrors(prev => ({ ...prev, branchId: '' }));
                        }}
                        placeholder="Select Branch"
                        searchable={true}
                        valueKey="id"
                        labelKey="label"
                      />
                      {errors.branchId && <p className="text-red-500 text-xs mt-1">{errors.branchId}</p>}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
                    <SelectDropdown
                      options={executiveList}
                      value={formData.ownerId}
                      onChange={handleOwnerChange}
                      placeholder="Select Owner"
                      searchable={true}
                      valueKey="id"
                      labelKey="label"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Call Type <span className="text-red-500">*</span></label>
                    <SelectDropdown options={visitTypeOptions} value={formData.visitType} onChange={(v) => setFormData(prev => ({ ...prev, visitType: v }))} placeholder="Select Visit Type" valueKey="id" labelKey="label" />
                    {errors.visitType && <p className="text-red-500 text-xs mt-1">{errors.visitType}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
                    <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" />
                    {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
                  </div>

                  {formData.sourceType === 'expo' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
                        <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" error={errors.expoYear} />
                        {errors.expoYear && <p className="text-red-500 text-xs mt-1">{errors.expoYear}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
                        <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
                        {errors.expoPlace && <p className="text-red-500 text-xs mt-1">{errors.expoPlace}</p>}
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Call Date <span className="text-red-500">*</span></label>
                    <CustomDatePicker value={formData.visitDate} onChange={(date) => setFormData(prev => ({ ...prev, visitDate: date }))} placeholder="Select date" error={errors.visitDate} />
                    {errors.visitDate && <p className="text-red-500 text-xs mt-1">{errors.visitDate}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Call Time</label>
                    <input type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} className={`w-full ${customClassName(formData.visitTime)}`} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
                    <CustomDatePicker value={formData.nextFollowUpDate} onChange={(date) => setFormData(prev => ({ ...prev, nextFollowUpDate: date }))} placeholder="Select follow-up date" minDate={new Date()} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Time</label>
                    <input type="time" name="nextFollowUpTime" value={formData.nextFollowUpTime} onChange={handleChange} className={`w-full ${customClassName(formData.nextFollowUpTime)}`} />
                  </div>
                </div>
              </div>
            )}

            {/* Organization Information: SHOWN IN EDIT MODE (Allows seeing & editing Vertical & Owner) */}
            {isEditMode && (
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">🏢</span> Organization Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                    <input type="text" value={formData.divisionName} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                    <input type="text" value={clientData?.clientId || ''} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono font-bold cursor-not-allowed" />
                  </div>

                  {/* Business Vertical visible and editable in Edit Mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Vertical <span className="text-red-500">*</span>
                    </label>
                    <SelectDropdown
                      options={verticalList}
                      value={formData.businessVerticalId}
                      onChange={handleBusinessVerticalChange}
                      placeholder="Select Business Vertical"
                      searchable={true}
                      valueKey="id"
                      labelKey="label"
                      // error={errors.businessVerticalId}
                    />
                    {/* {errors.businessVerticalId && <p className="text-red-500 text-xs mt-1">{errors.businessVerticalId}</p>} */}
                  </div>
                  {/* {branchList.length > 0 && ( */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Branch <span className="text-red-500">*</span>
                      </label>
                      <SelectDropdown
                        options={branchList}
                        value={formData.branchId}
                        onChange={(branchId) => {
                          setFormData(prev => ({ ...prev, branchId }));
                          if (errors.branchId) setErrors(prev => ({ ...prev, branchId: '' }));
                        }}
                        placeholder="Select Branch"
                        searchable={true}
                        valueKey="id"
                        labelKey="label"
                      />
                      {errors.branchId && <p className="text-red-500 text-xs mt-1">{errors.branchId}</p>}
                    </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
                    <SelectDropdown
                      options={executiveList}
                      value={formData.ownerId}
                      onChange={handleOwnerChange}
                      placeholder="Select Owner"
                      searchable={true}
                      valueKey="id"
                      labelKey="label"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 2. Client & Candidate Information */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🏢</span> Client & Candidate Information
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
                <div>
                  {(isDigidaDivision || isCusoDivision) && (<label className="block text-sm font-medium text-gray-700 mb-1">
                    Company / Client <span className="text-red-500">*</span>
                  </label>)}

                  {isEducationDivision && (<label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Name / School / College / Student <span className="text-red-500">*</span>
                  </label>)}

                  {isReldaDivision && (<label className="block text-sm font-medium text-gray-700 mb-1">
                    Client / Dealer / Distributor <span className="text-red-500">*</span>
                  </label>)}

                  <input 
                    type="text" 
                    name="clientName" 
                    value={formData.clientName} 
                    onChange={handleChange} 
                    className={`w-full ${customClassName(formData.clientName)} ${errors.clientName ? 'border-red-500' : ''}`} 
                    placeholder="Enter name" 
                  />
                  {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName}</p>}
                </div>
                {!isBrawnyDivision &&( 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry Type {!isDnbsDivision && <span className="text-red-500">*</span>}
                  </label>
                  <SelectDropdown 
                    options={industryOptions} 
                    value={formData.industryType} 
                    onChange={(v) => setFormData(prev => ({ ...prev, industryType: v }))} 
                    placeholder={isDnbsDivision ? "Select Industry (Optional)" : "Select Industry"} 
                    searchable 
                    valueKey="id" 
                    labelKey="label" 
                    // error={errors.industryType} 
                  />
                  {/* {errors.industryType && <p className="text-red-500 text-xs mt-1">{errors.industryType}</p>} */}
                </div>
                )}
                {!isBrawnyDivision &&( 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry Sub-Type</label>
                  <SelectDropdown options={filteredSubIndustries} value={formData.industrySubType} onChange={handleIndustrySubTypeChange} placeholder="Select Sub-Industry" valueKey="id" labelKey="label" disabled={!formData.industryType} />
                </div>
                )}
                {showOtherIndustry && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specify Industry <span className="text-red-500">*</span></label>
                    <input type="text" value={otherIndustry} onChange={(e) => setOtherIndustry(e.target.value)} className={`w-full ${customClassName(otherIndustry)} ${errors.otherIndustry ? 'border-red-500' : ''}`} placeholder="Please specify the industry" />
                    {errors.otherIndustry && <p className="text-red-500 text-xs mt-1">{errors.otherIndustry}</p>}
                  </div>
                )}
          
              </div>

              {/* SECTION A: BRAWNY & DNBS ONLY (Student, Age, Profession) */}
              {isEducationDivision && (
                <div className="mt-4 rounded-lg">
                  <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                    Student & Candidate Details (Brawny & DNBS)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Briefcase className="w-3.5 h-3.5 text-gray-500" /> Profession
                      </label>
                      <SelectDropdown
                        options={professionOptions}
                        value={formData.profession}
                        onChange={(val) => setFormData(prev => ({ ...prev, profession: val }))}
                        placeholder="Select Profession"
                        valueKey="id"
                        labelKey="label"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Student / Candidate Name {formData.age && Number(formData.age) < 18 && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        name="studentName"
                        value={formData.studentName}
                        onChange={handleChange}
                        className={`w-full ${customClassName(formData.studentName)}`}
                        placeholder="Student full name"
                      />
                      {errors.studentName && <p className="text-red-500 text-xs mt-1">{errors.studentName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                      <input
                        type="number"
                        name="age"
                        min="0"
                        max="100"
                        value={formData.age}
                        onChange={handleChange}
                        className={`w-full ${customClassName(formData.age)}`}
                        placeholder="e.g., 16"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION B: CUSO ONLY (Our Product with isVertical===false & B2B Details) */}
              {isCusoDivision && (
                <div className="mt-4">
                  <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    Product & Business Profile (CUSO Division)
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Our Product (Filtered: isVertical === false) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Our ERP Product <span className="text-red-500">*</span>
                      </label>
                      <SelectDropdown
                        options={productList}
                        value={formData.ourProductId}
                        onChange={handleProductChange}
                        placeholder={formData.divisionId ? "Select ERP Product" : "Select Division First"}
                        searchable={true}
                        valueKey="id"
                        labelKey="label"
                        disabled={!formData.divisionId}
                        error={errors.ourProductId}
                      />
                      {errors.ourProductId && <p className="text-red-500 text-xs mt-1">{errors.ourProductId}</p>}
                    </div>

                    {/* Product Business Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Business Category
                      </label>
                      <SelectDropdown
                        options={ourBusinessCategoryList}
                        value={formData.ourProductBusinessCategoryId}
                        onChange={(val) => setFormData(prev => ({ ...prev, ourProductBusinessCategoryId: val }))}
                        placeholder={formData.ourProductId ? "Select Category" : "Select Product First"}
                        searchable={true}
                        disabled={!formData.ourProductId}
                        valueKey="id"
                        labelKey="label"
                      />
                    </div>

                    {/* Branch Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Branch Count</label>
                      <input
                        type="number"
                        name="branchCount"
                        min="1"
                        value={formData.branchCount}
                        onChange={handleChange}
                        className={`w-full ${customClassName(formData.branchCount)}`}
                        placeholder="e.g., 3"
                      />
                    </div>

                    {/* Employee Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employees Count</label>
                      <input
                        type="number"
                        name="employeeCount"
                        min="0"
                        value={formData.employeeCount}
                        onChange={handleChange}
                        className={`w-full ${customClassName(formData.employeeCount)}`}
                        placeholder="e.g., 25"
                      />
                    </div>

                    {/* Annual Turnover */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Turnover</label>
                      <SelectDropdown
                        options={annualTurnoverOptions}
                        value={formData.annualTurnover}
                        onChange={(val) => setFormData(prev => ({ ...prev, annualTurnover: val }))}
                        placeholder="Select Turnover Range"
                        valueKey="id"
                        labelKey="label"
                      />
                    </div>

                    {/* Registration Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business Registration</label>
                      <SelectDropdown
                        options={registrationOptions}
                        value={formData.registrationType}
                        onChange={(val) => setFormData(prev => ({ 
                          ...prev, 
                          registrationType: val,
                          taxType: val === 'registered' ? 'gst' : 'na',
                          taxNumber: val === 'registered' ? prev.taxNumber : ''
                        }))}
                        valueKey="id"
                        labelKey="label"
                      />
                    </div>

                    {/* Tax Type & Tax Number (Only if Registered) */}
                    {formData.registrationType === 'registered' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tax Type</label>
                          <SelectDropdown
                            options={taxTypeOptions}
                            value={formData.taxType}
                            onChange={(val) => setFormData(prev => ({ ...prev, taxType: val }))}
                            valueKey="id"
                            labelKey="label"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tax Number / GSTIN</label>
                          <input
                            type="text"
                            name="taxNumber"
                            value={formData.taxNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, taxNumber: e.target.value.toUpperCase() }))}
                            className={`w-full ${customClassName(formData.taxNumber)} uppercase`}
                            placeholder="e.g., 33AAAAA0000A1Z5"
                          />
                        </div>
                      </>
                    )}

                    {/* Preferred Partner Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Partner Type</label>
                      <SelectDropdown
                        options={partnerTypeOptions}
                        value={formData.preferredPartnerType}
                        onChange={(val) => setFormData(prev => ({ ...prev, preferredPartnerType: val }))}
                        placeholder="Select Partner Type"
                        valueKey="id"
                        labelKey="label"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Division Program & Plan Offerings */}
            {!isDigidaDivision && (
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <SectionIcon size={22} className="text-primary" />
                  {config.title}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isCusoDivision ? "Select Subscription Plan" : isBrawnyDivision ? "Select Program" : isDnbsDivision ? "Select Course" : "Select Program / Course"}
                    </label>
                    <SelectDropdown
                      options={programList}
                      value={formData.programId}
                      onChange={handleProgramChange}
                      placeholder={
                        !formData.divisionId ? "Select Division First" :
                        programList.length === 0 ? "No Programs / Courses Available" : "Select Program / Course"
                      }
                      searchable={true}
                      valueKey="id"
                      labelKey="label"
                      disabled={!formData.divisionId || programList.length === 0}
                    />
                    {errors.programId && <p className="text-red-500 text-xs mt-1">{errors.programId}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Plan (Monthly / Annual / One-time)
                    </label>
                    <SelectDropdown
                      options={availablePlans}
                      value={formData.selectedPlanName}
                      onChange={handlePlanChange}
                      placeholder={formData.programId ? "Select Pricing Plan" : "Select Program First"}
                      searchable={false}
                      valueKey="id"
                      labelKey="label"
                      disabled={!formData.programId}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 4. Contact Details */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">👤</span> Contact Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name <span className="text-red-500">*</span></label>
                  <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className={`w-full ${customClassName(formData.contactName)} ${errors.contactName ? 'border-red-500' : ''}`} placeholder="Full name" />
                  {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input type="text" name="contactDesignation" value={formData.contactDesignation} onChange={handleChange} className={`w-full ${customClassName(formData.contactDesignation)}`} placeholder="e.g., CEO, Manager, Director" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className={`w-full ${customClassName(formData.contactEmail)} ${errors.contactEmail ? 'border-red-500' : ''}`} placeholder="email@example.com" />
                  {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                  <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className={`w-full ${customClassName(formData.contactPhone)} ${errors.contactPhone ? 'border-red-500' : ''}`} placeholder="10 digit mobile number" />
                  {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Landline {!formData.contactPhone && <span className="text-red-500">*</span>}</label>
                  <input type="text" name="contactLandline" value={formData.contactLandline} onChange={handleChange} className={`w-full ${customClassName(formData.contactLandline)} ${errors.contactLandline ? 'border-red-500' : ''}`} placeholder="Landline number with STD code" />
                  {errors.contactLandline && <p className="text-red-500 text-xs mt-1">{errors.contactLandline}</p>}
                </div>
              </div>
            </div>

            {/* 5. Location Information */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center"><span className="text-2xl mr-2">📍</span> Location Information</h2>
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={locationLoading}
                  className={`px-3 py-1.5 ${locationLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white rounded-md text-sm font-medium shadow-xs flex items-center gap-2`}
                >
                  <Camera className="w-4 h-4" />
                  <span>{locationLoading ? 'Capturing Location...' : 'Get Current Location'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea name="address" value={formData.address} onChange={handleChange} rows="2" className={`w-full ${customClassName(formData.address)}`} placeholder="Complete address" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
                  <input type="text" name="locality" value={formData.locality} onChange={handleChange} className={`w-full ${customClassName(formData.locality)}`} placeholder="Locality" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <SelectDropdown  options={countries}  value={formData.country}  onChange={handleCountryChange} placeholder="Select Country" valueKey="isoCode"  labelKey="name" searchable={true} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <SelectDropdown options={states} value={formData.state} onChange={handleStateChange}  placeholder={formData.country === 'IN' ? "Select State" : "Enter State"} valueKey="id"  labelKey="label"  disabled={!formData.country} searchable={true} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                  <SelectDropdown options={districts} value={formData.district} onChange={(v) => setFormData(prev => ({ ...prev, district: v }))} placeholder={formData.state ? "Select District" : "Select State First"} valueKey="id"  labelKey="label"  disabled={!formData.state || formData.country !== 'IN'} searchable={true} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength="6" className={`w-full ${customClassName(formData.pincode)}`} placeholder="6 digits" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input type="text" name="latitude" value={formData.latitude} onChange={handleChange} className={`w-full ${customClassName(formData.latitude)}`} placeholder="e.g., 28.6139" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input type="text" name="longitude" value={formData.longitude} onChange={handleChange} className={`w-full ${customClassName(formData.longitude)}`} placeholder="e.g., 77.2090" />
                </div>
              </div>

              {/* Location Images Upload */}
              <div className={`border-2 border-dotted ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} p-4 rounded mt-6 transition-colors`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <p className="text-center font-medium">Location Pictures (Optional)</p>
                <p className="text-center text-xs text-gray-500 mb-2">Upload or capture location images (max 2MB per image)</p>

                {locationImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {locationImages.map((file, index) => (
                      <div key={index} className="relative group">
                        <img src={URL.createObjectURL(file)} alt={`Location ${index + 1}`} className="w-20 h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setLocationImages(prev => prev.filter((_, i) => i !== index))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {existingImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {existingImages.map((imageUrl, index) => (
                      <div key={`existing-${index}`} className="relative group">
                        <img src={imageUrl} alt={`Existing Location ${index + 1}`} className="w-20 h-20 object-cover rounded border" />
                        <button type="button" onClick={() => setExistingImages(prev => prev.filter((_, i) => i !== index))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
                    <div className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-4 rounded-lg transition-colors">
                      <Plus className="w-5 h-5" /> <span>Upload Images</span>
                    </div>
                  </label>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
                    <div className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors">
                      <Camera className="w-5 h-5" /> <span>Use Camera</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* 6. Business Value & Requirements */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">💼</span> Business Details</h2>
              
              {!isEducationDivision && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {/* Product Categories */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Categories {!formData.programId && <span className="text-red-500">*</span>}
                    </label>

                    <SelectDropdown
                      options={productCategoryOptions}
                      value={formData.productCategory}
                      onChange={handleProductCategoryChange}
                      placeholder="Select Product Categories"
                      valueKey="id"
                      labelKey="label"
                      multiple={true}
                      searchable={true}
                      error={errors.productCategory}
                    />

                    {errors.productCategory && (
                      <p className="text-red-500 text-xs mt-1">{errors.productCategory}</p>
                    )}

                    {showOtherProductCategory && (
                      <div className="mt-3 w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Specify Product Category <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={otherProductCategory}
                          onChange={(e) => setOtherProductCategory(e.target.value)}
                          className={`w-full ${customClassName(otherProductCategory)} ${errors.otherProductCategory ? 'border-red-500' : ''}`}
                          placeholder="Please specify the product category"
                        />
                        {errors.otherProductCategory && (
                          <p className="text-red-500 text-xs mt-1">{errors.otherProductCategory}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Requirements */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Requirements {!formData.programId && <span className="text-red-500">*</span>}
                    </label>

                    <SelectDropdown
                      options={filteredRequirements}
                      value={formData.requirement}
                      onChange={handleRequirementChange}
                      placeholder="Select Requirements"
                      valueKey="id"
                      labelKey="label"
                      multiple={true}
                      searchable={true}
                      disabled={!formData.productCategory || formData.productCategory.length === 0}
                      error={errors.requirement}
                    />

                    {errors.requirement && (
                      <p className="text-red-500 text-xs mt-1">{errors.requirement}</p>
                    )}
                  </div>

                  {/* Other Requirements */}
                  {showOtherRequirement && (
                    <div className="sm:col-span-2 space-y-3">
                      {formData.requirement
                        .filter(req => req.includes('Others - Mention that product'))
                        .map((otherReq, index) => (
                          <div key={index}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Specify Other Requirement for {otherReq.split(' - ')[0]} <span className="text-red-500">*</span>
                            </label>

                            <input
                              type="text"
                              value={otherRequirements[otherReq] || ''}
                              onChange={(e) => handleOtherRequirementChange(otherReq, e.target.value)}
                              className={`w-full ${customClassName(otherRequirements[otherReq])} ${errors.otherRequirements ? 'border-red-500' : ''}`}
                              placeholder="Please specify the requirement"
                            />
                          </div>
                        ))}

                      {errors.otherRequirements && (
                        <p className="text-red-500 text-xs mt-1">{errors.otherRequirements}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 overflow-visible">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <SelectDropdown options={currencyOptions} value={formData.currency} onChange={(value) => setFormData(prev => ({ ...prev, currency: value }))} valueKey="id" labelKey="label" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Value <span className="text-red-500">*</span></label>
                  <input type="number" name="estimatedValue" value={formData.estimatedValue} onChange={handleChange} className={`w-full ${customClassName(formData.estimatedValue)} ${errors.estimatedValue ? 'border-red-500' : ''}`} placeholder="0" min="0" />
                  {errors.estimatedValue && <p className="text-red-500 text-xs mt-1">{errors.estimatedValue}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Value</label>
                  <input type="number" name="expectedValue" value={formData.expectedValue} onChange={handleChange} className={`w-full ${customClassName(formData.expectedValue)}`} placeholder="0" min="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recurring Value</label>
                  <input type="number" name="recurringValue" value={formData.recurringValue} onChange={handleChange} className={`w-full ${customClassName(formData.recurringValue)}`} placeholder="0" min="0" />
                </div>
              </div>
            </div>

            {/* 7. Sales Pipeline */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📊</span> Sales Pipeline</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage <span className="text-red-500">*</span></label>
                  <SelectDropdown options={salesStages} value={formData.stageId} onChange={(v) => setFormData(prev => ({ ...prev, stageId: v }))} placeholder="Select Stage" searchable valueKey="_id" labelKey="stageName" />
                  {errors.stageId && <p className="text-red-500 text-xs mt-1">{errors.stageId}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Closing Date</label>
                  <CustomDatePicker value={formData.expectedClosingDate} onChange={(date) => setFormData(prev => ({ ...prev, expectedClosingDate: date }))} placeholder="Select date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                  <SelectDropdown options={paymentStatusOptions} value={formData.paymentStatus} onChange={(v) => setFormData(prev => ({ ...prev, paymentStatus: v }))} valueKey="id" labelKey="label" />
                </div>
              </div>
            </div>

            {/* 8. Call Remarks: HIDDEN IN EDIT MODE */}
            {!isEditMode && (
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📝</span> Call Remarks</h2>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" className={`w-full ${customClassName(formData.remarks)}`} placeholder="Any important notes or action items..." />
              </div>
            )}

            {/* 9. Visit History Table: SHOWN IN EDIT MODE */}
            {isEditMode && clientData?.visitHistory && clientData.visitHistory.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">📜</span> Visit History
                </h2>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visit #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {clientData.visitHistory.map((visit, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(visit.visitDate).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitTime}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="capitalize block font-medium">{visit.visitType}</span>
                            {clientData?.sourceType === 'expo' && (
                              <div className="mt-1 text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 inline-block">
                                <span className="font-semibold text-[9px] uppercase">Source: Expo</span><br/>
                                {clientData.expoPlace} ({clientData.expoYear})
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{visit.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-800 mb-1">Please fix the following errors:</h3>
                <ul className="list-disc list-inside text-xs text-red-700 space-y-0.5">
                  {Object.entries(errors).map(([field, error]) => <li key={field}>{error}</li>)}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t">
              <button type="button" onClick={() => navigate(-1)} disabled={loading} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <div className="flex flex-col sm:flex-row gap-4">
                {!isEditMode && (
                  <button type="button" onClick={resetForm} disabled={loading} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium">Reset Form</button>
                )}
                <button type="submit" disabled={loading} className="w-full sm:w-auto px-6 py-2.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium shadow-xs disabled:opacity-50 flex items-center justify-center">
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isEditMode ? 'Updating...' : (isConvertMode ? 'Converting...' : 'Submitting...')}
                    </>
                  ) : (
                    isEditMode ? 'Update Visit' : (isConvertMode ? 'Convert & Submit' : 'Submit Visit')
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientVisitForm;


// // ─────────────────────────────────────────────────────────────────────────────
// // FILE: src/pages/sales/settings/ClientVisitForm.jsx
// // ─────────────────────────────────────────────────────────────────────────────
// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { industryData, productCategories, visitTypeOptions, sourceTypeOptions, currencyOptions, paymentStatusOptions, professionOptions, annualTurnoverOptions, registrationOptions, taxTypeOptions, partnerTypeOptions } from './IndustryData';
// import AllStatesAndDistricts from '../../helpers/AllStatesAndDistricts';
// import { Country } from 'country-state-city';
// import SelectDropdown from '../../customStyles/SelectDropdown';
// import CustomDatePicker from '../../customStyles/CustomDatePicker';
// import SummaryApi from '../../common/SummaryApi';
// import api from '../../common/apiClient';
// import { toast } from 'react-toastify';
// import imageCompression from 'browser-image-compression';
// import { Camera, Plus, X, Briefcase, GraduationCap, Package, CreditCard, Target, Layers  } from 'lucide-react';
// import { useSelector } from 'react-redux';

// const ClientVisitForm = () => {
//   const user = useSelector((state) => state?.user?.user);
//   const { id, leadId } = useParams();
//   const navigate = useNavigate();
//   const isEditMode = !!id;
//   const isConvertMode = !!leadId;
  
//   const [salesStages, setSalesStages] = useState([]);
//   const [loadingVisit, setLoadingVisit] = useState(false);

//   // Dropdown options states
//   const [ourProductList, setOurProductList] = useState([]);
//   const [ourBusinessCategoryList, setOurBusinessCategoryList] = useState([]);

//   //  Program & Plan States
//   const [programList, setProgramList] = useState([]);
//   const [availablePlans, setAvailablePlans] = useState([]);

//   const [showOtherProductCategory, setShowOtherProductCategory] = useState(false);
//   const [otherProductCategory, setOtherProductCategory] = useState('');
//   const [showOtherRequirement, setShowOtherRequirement] = useState(false);
//   const [otherRequirements, setOtherRequirements] = useState({});
//   const [locationImages, setLocationImages] = useState([]);
//   const [existingImages, setExistingImages] = useState([]);
//   const [isDragActive, setIsDragActive] = useState(false);
//   const [locationLoading, setLocationLoading] = useState(false);
//   const [executiveList, setExecutiveList] = useState([]);

//   const [formData, setFormData] = useState({
//     visitType: '',
//     sourceType: '',
//     expoYear: '',
//     expoPlace: '',
//     visitDate: new Date(),
//     visitTime: new Date().toTimeString().slice(0, 5),
//     nextFollowUpDate: null,
//     nextFollowUpTime: '',

//     divisionId: '',
//     divisionName: '',

//     // Client, Profession & Minor Student Details
//     clientName: '',
//     industryType: '',
//     industrySubType: '',

//     businessVerticalId: '',
//     ourProductId: '',
//     ourProductBusinessCategoryId: '',

//     branchCount: '1',
//     employeeCount: '',
//     annualTurnover: '',
//     registrationType: 'unregistered', // 'registered' or 'unregistered'
//     taxType: 'na',                   // 'gst', 'vat', 'salesTax', 'na'
//     taxNumber: '',
//     preferredPartnerType: '',

//     // BRAWNY & DNBS Only Fields
//     studentName: '',
//     age: '',
//     profession: '',

//     // Program & Plan Mapping
//     programId: '',
//     selectedPlanName: '',

//     contactName: '',
//     contactDesignation: '',
//     contactEmail: '',
//     contactPhone: '',
//     contactLandline: '',

//     address: '',
//     locality: '',
//     district: '',
//     state: '',
//     country: 'IN',
//     pincode: '',
//     latitude: '',
//     longitude: '',

//     productCategory: [],
//     requirement: [],
//     estimatedValue: 0,
//     expectedValue: 0,
//     recurringValue: 0,
//     currency: 'INR',

//     stageId: '',
//     expectedClosingDate: null,
//     paymentStatus: 'na',

//     remarks: '',
//     ownerId: user?._id || ''
//   });

//   const divisionConfig = {
//     RELDA: { icon: Package, title: "Products / Categories" },
//     CUSO: { icon: CreditCard, title: "Subscription Plans" },
//     DNBS: { icon: GraduationCap, title: "Courses / Programs" },
//     BRAWNY: { icon: Target, title: "Programs" },
//   };


//   // Checks if the selected division name is CUSO or DNBS
//   const isCusoDivision = Boolean(formData.divisionName?.toLowerCase().includes('cuso'));
//   const isDnbsDivision = Boolean(formData.divisionName?.toLowerCase().includes('dnbs'));
//   const isBrawnyDivision = Boolean(formData.divisionName?.toLowerCase().includes('brawny'));
//   const isDigidaDivision = Boolean(formData.divisionName?.toLowerCase().includes('digida'));
//   const isEducationDivision = isBrawnyDivision || isDnbsDivision;

//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [divisions, setDivisions] = useState([]);
//   const [countries, setCountries] = useState([]);
//   const [states, setStates] = useState([]);
//   const [districts, setDistricts] = useState([]);
//   const [filteredSubIndustries, setFilteredSubIndustries] = useState([]);
//   const [filteredRequirements, setFilteredRequirements] = useState([]);
//   const [showOtherIndustry, setShowOtherIndustry] = useState(false);
//   const [otherIndustry, setOtherIndustry] = useState('');
//   const [submitSuccess, setSubmitSuccess] = useState(false);
//   const [submitMessage, setSubmitMessage] = useState('');
//   const [clientData, setClientData] = useState(null);

//   const currentYear = new Date().getFullYear();
//   const startYear = 2025;
//   const expoYearOptions = [];
//   for (let y = currentYear; y >= startYear; y--) {
//     expoYearOptions.push({ id: y.toString(), label: y.toString() });
//   }  

//   const divisionName = formData.divisionName?.trim().toUpperCase() || "";
//   const config = divisionName.includes("RELDA")
//     ? divisionConfig.RELDA
//     : divisionName.includes("CUSO")
//     ? divisionConfig.CUSO
//     : divisionName.includes("DNBS")
//     ? divisionConfig.DNBS
//     : divisionName.includes("BRAWNY")
//     ? divisionConfig.BRAWNY
//     : { icon: Layers, title: "Program / Course / Plans" };

//   const SectionIcon = config.icon;

//   // Sync divisionName automatically if divisionId is available but divisionName is missing
//   useEffect(() => {
//     if (formData.divisionId && !formData.divisionName && divisions.length > 0) {
//       const matched = divisions.find(d => d.id === formData.divisionId);
//       if (matched) {
//         setFormData(prev => ({ ...prev, divisionName: matched.label }));
//       }
//     }
//   }, [formData.divisionId, formData.divisionName, divisions]);

//   // Fetch executives list for owner assignment
//   const fetchExecutives = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.executiveUsers.url);
//       const resData = response.data;
      
//       let execs = [];
//       if (Array.isArray(resData?.data)) {
//         execs = resData.data;
//       } else if (Array.isArray(resData?.users)) {
//         execs = resData.users;
//       } else if (Array.isArray(resData?.executives)) {
//         execs = resData.executives;
//       } else if (Array.isArray(resData)) {
//         execs = resData;
//       }

//       const formattedList = execs.map((exec) => {
//         const rawId = exec._id || exec.id || exec.userId || '';
//         const nameStr = (
//           exec.fullName
//             ? `${exec.fullName} ${exec.lastName || ''}`.trim()
//             : (exec.name || exec.clientName || exec.userName || exec.email || '')
//         ) || 'Executive';

//         return {
//           id: String(rawId),
//           _id: String(rawId),
//           value: String(rawId),
//           label: nameStr,
//           name: nameStr
//         };
//       });

//       setExecutiveList(formattedList);
//     } catch (error) {
//       console.error("Error fetching executives:", error);
//     }
//   }, []);

//   useEffect(() => {
//     fetchExecutives();
//   }, [fetchExecutives]);

//   //  Fetch Programs for Selected Division
//   const fetchProgramsForDivision = useCallback(async (divisionId) => {
//     if (!divisionId) {
//       setProgramList([]);
//       setAvailablePlans([]);
//       return;
//     }
//     try {
//       const response = await api.get(SummaryApi.getAllPrograms.url, { 
//         params: { divisionId, isActive: "true" } 
//       });

//       if (response.data?.success) {
//         const formatted = (response.data.data || []).map(p => ({
//           id: p._id,
//           label: p.name,
//           pricingPlans: p.pricingPlans || [],
//           offeringType: p.offeringType,
//         }));
//         setProgramList(formatted);
//       }
//     } catch (error) {
//       console.error('Error fetching programs:', error);
//     }
//   }, []);

//   // 1. Fetch Our ERP Products
//   const fetchOurProducts = useCallback(async (divisionId) => {
//     if (!divisionId) {
//       setOurProductList([]);
//       return;
//     }
//     try {
//       const response = await api.get(SummaryApi.getAllProducts.url, { 
//         params: { divisionId, isActive: "true" } 
//       });

//       if (response.data?.success) {
//         const formatted = (response.data.data || []).map(p => ({
//           id: p._id,
//           label: p.name,
//         }));
//         setOurProductList(formatted);
//       }
//     } catch (error) {
//       console.error('Error fetching products:', error);
//       setOurProductList([]);
//     }
//   }, []);

//   // 2. Fetch Business Categories based on Selected Product
//   const fetchBusinessCategories = useCallback(async (productId) => {
//     if (!productId) {
//       setOurBusinessCategoryList([]);
//       return;
//     }
//     try {
//       const response = await api.get(SummaryApi.getAllBusinessCategories.url, { params: { productId, isActive: "true" }});

//       if (response.data?.success) {
//         const formatted = (response.data.data || []).map(c => ({
//           id: c._id,
//           label: c.name,
//         }));
//         setOurBusinessCategoryList(formatted);
//       }
//     } catch (error) {
//       console.error('Error fetching business categories:', error);
//     }
//   }, []);

//   // Handle Our Product change & clear previous child category
//   const handleOurProductChange = (productId) => {
//     setFormData(prev => ({
//       ...prev,
//       ourProductId: productId,
//       ourProductBusinessCategoryId: '',
//     }));

//     if (errors.ourProductId) {
//       setErrors(prev => ({ ...prev, ourProductId: '' }));
//     }

//     fetchBusinessCategories(productId);
//   };

//   //  Handle Program Selection & Load Pricing Plans
//   const handleProgramChange = (selectedProgramId) => {
//     const selectedProg = programList.find(p => p.id === selectedProgramId);
//     const plans = selectedProg?.pricingPlans || [];

//     const formattedPlans = plans.map(plan => ({
//       id: plan.planName,
//       label: `${plan.planName} - ₹${plan.totalAmount?.toLocaleString('en-IN')} (${plan.billingCycle?.replace('_', ' ')})`,
//       amount: plan.totalAmount || 0,
//       billingCycle: plan.billingCycle,
//     }));

//     setAvailablePlans(formattedPlans);

//     let autoPlanName = '';
//     let autoExpectedVal = formData.expectedValue;
//     let autoRecurringVal = formData.recurringValue;

//     if (formattedPlans.length === 1) {
//       autoPlanName = formattedPlans[0].id;
//       autoExpectedVal = formattedPlans[0].amount; 
//       if (formattedPlans[0].billingCycle === 'monthly') {
//         autoRecurringVal = formattedPlans[0].amount;
//       }
//     }

//     setFormData(prev => ({
//       ...prev,
//       programId: selectedProgramId,
//       selectedPlanName: autoPlanName,
//       expectedValue: autoPlanName ? autoExpectedVal : prev.expectedValue, // ✅ Changed to expectedValue
//       recurringValue: autoPlanName ? autoRecurringVal : prev.recurringValue,
//     }));

//     if (errors.programId) {
//       setErrors(prev => ({ ...prev, programId: '' }));
//     }
//   };

//   //  Handle Pricing Plan Change & Auto-fill Deal Values
//   const handlePlanChange = (planName) => {
//     const chosenPlan = availablePlans.find(p => p.id === planName);
    
//     setFormData(prev => ({
//       ...prev,
//       selectedPlanName: planName,
//       expectedValue: chosenPlan ? chosenPlan.amount : prev.expectedValue, // ✅ Program விலை Expected-ல் உட்காரும்!
//       recurringValue: chosenPlan?.billingCycle === 'monthly' ? chosenPlan.amount : prev.recurringValue,
//     }));
//   };

//   // Handler for Assigned Executive / Owner dropdown change
//   const handleOwnerChange = (selectedVal) => {
//     let idValue = selectedVal;
//     if (selectedVal && typeof selectedVal === 'object') {
//       idValue = selectedVal.id || selectedVal._id || selectedVal.value;
//     }

//     setFormData(prev => ({
//       ...prev,
//       ownerId: String(idValue || '')
//     }));

//     if (errors.ownerId) {
//       setErrors(prev => ({ ...prev, ownerId: '' }));
//     }
//   };

//   const compressImage = async (file) => {
//     const options = {
//       maxSizeMB: 2,
//       maxWidthOrHeight: 1920,
//       useWebWorker: true,
//     };

//     try {
//       const compressedFile = await imageCompression(file, options);
//       return compressedFile;
//     } catch (error) {
//       console.error('Error compressing image:', error);
//       return file;
//     }
//   };

//   const handleFileChange = async (e) => {
//     const files = Array.from(e.target.files);

//     if (locationImages.length + files.length > 10) {
//       toast.error('Maximum 10 images allowed');
//       return;
//     }

//     const compressedFiles = await Promise.all(
//       files.map(async (file) => {
//         if (file.size > 2 * 1024 * 1024) {
//           const compressed = await compressImage(file);
//           return compressed;
//         }
//         return file;
//       })
//     );

//     setLocationImages(prev => [...prev, ...compressedFiles]);
//   };

//   const handleDragOver = (e) => {
//     e.preventDefault();
//     setIsDragActive(true);
//   };

//   const handleDragLeave = (e) => {
//     e.preventDefault();
//     setIsDragActive(false);
//   };

//   const handleDrop = async (e) => {
//     e.preventDefault();
//     setIsDragActive(false);

//     const files = Array.from(e.dataTransfer.files).filter(file =>
//       file.type.startsWith('image/')
//     );

//     if (locationImages.length + files.length > 10) {
//       toast.error('Maximum 10 images allowed');
//       return;
//     }

//     const compressedFiles = await Promise.all(
//       files.map(async (file) => {
//         if (file.size > 2 * 1024 * 1024) {
//           const compressed = await compressImage(file);
//           return compressed;
//         }
//         return file;
//       })
//     );

//     setLocationImages(prev => [...prev, ...compressedFiles]);
//   };

//   const reverseGeocode = async (latitude, longitude) => {
//     try {
//       const response = await fetch(
//         `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
//         {
//           headers: {
//             'Accept': 'application/json',
//             'User-Agent': 'ClientVisitForm/1.0'
//           }
//         }
//       );

//       if (!response.ok) {
//         throw new Error('Failed to fetch address');
//       }

//       const data = await response.json();
//       return data;
//     } catch (error) {
//       console.error('Reverse geocoding error:', error);

//       try {
//         const fallbackResponse = await fetch(
//           `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
//         );

//         if (fallbackResponse.ok) {
//           const fallbackData = await fallbackResponse.json();

//           let district = '';
//           if (fallbackData.localityInfo && fallbackData.localityInfo.administrative) {
//             const districtInfo = fallbackData.localityInfo.administrative.find(
//               admin => admin.description?.toLowerCase().includes('district') || admin.adminLevel === 5
//             );
//             district = districtInfo ? districtInfo.name.replace(' district', '') : '';
//           }

//           return {
//             display_name: fallbackData.locality || '',
//             address: {
//               road: fallbackData.street || '',
//               neighbourhood: fallbackData.locality || '',
//               suburb: fallbackData.suburb || '',
//               city: fallbackData.city || '',
//               state_district: district,
//               state: fallbackData.principalSubdivision || '',
//               country: fallbackData.countryName || '',
//               country_code: fallbackData.countryCode?.toLowerCase() || '',
//               postcode: fallbackData.postcode || ''
//             }
//           };
//         }
//       } catch (fallbackError) {
//         console.error('Fallback geocoding also failed:', fallbackError);
//       }

//       return null;
//     }
//   };

//   const setDistrictFromGeocode = (addressData, matchedState) => {
//     let apiDistrictName = addressData.state_district || addressData.county || addressData.city || '';

//     apiDistrictName = apiDistrictName
//       .replace(/ district$/i, '')
//       .replace(/ District$/i, '')
//       .replace(/ Dist\.?$/i, '')
//       .replace(/ Zilla$/i, '')
//       .replace(/ Jilla$/i, '')
//       .trim();

//     const stateDistricts = AllStatesAndDistricts[matchedState] || [];
//     let matchedDistrict = findClosestMatch(apiDistrictName, stateDistricts);

//     if (!matchedDistrict && apiDistrictName) {
//       if (addressData.city && addressData.city !== apiDistrictName) {
//         matchedDistrict = findClosestMatch(addressData.city, stateDistricts);
//       }
//       if (!matchedDistrict && addressData.locality) {
//         matchedDistrict = findClosestMatch(addressData.locality, stateDistricts);
//       }
//     }

//     if (matchedDistrict) {
//       setFormData(prev => ({ ...prev, district: matchedDistrict }));
//     } else {
//       setFormData(prev => ({ ...prev, district: '' }));
//     }
//   };

//   const findClosestMatch = (searchTerm, list) => {
//     if (!searchTerm || !list || list.length === 0) return null;

//     const normalizedSearch = searchTerm.toLowerCase().trim();
//     const exactMatch = list.find(item => item.toLowerCase().trim() === normalizedSearch);
//     if (exactMatch) return exactMatch;

//     const searchWithoutCommonWords = normalizedSearch
//       .replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '')
//       .trim();

//     const matchWithoutCommonWords = list.find(item =>
//       item.toLowerCase().replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '').trim() === searchWithoutCommonWords
//     );
//     if (matchWithoutCommonWords) return matchWithoutCommonWords;

//     const partialMatch = list.find(item => {
//       const normalizedItem = item.toLowerCase();
//       return normalizedItem.includes(normalizedSearch) ||
//         normalizedSearch.includes(normalizedItem) ||
//         normalizedItem.includes(searchWithoutCommonWords) ||
//         searchWithoutCommonWords.includes(normalizedItem);
//     });
//     if (partialMatch) return partialMatch;

//     const firstWord = normalizedSearch.split(' ')[0];
//     if (firstWord.length > 3) {
//       const firstWordMatch = list.find(item => item.toLowerCase().startsWith(firstWord));
//       if (firstWordMatch) return firstWordMatch;
//     }

//     return null;
//   };

//   const handleCountryChange = (countryCode) => {
//     setFormData(prev => ({
//       ...prev,
//       country: countryCode,
//       state: '',
//       district: ''
//     }));

//     if (countryCode === 'IN') {
//       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
//         id: state,
//         label: state
//       }));
//       setStates(indianStates);
//     } else {
//       setStates([]);
//     }
//   };

//   const handleStateChange = (stateName) => {
//     setFormData(prev => ({
//       ...prev,
//       state: stateName,
//       district: ''
//     }));
//   };

//   const getCurrentLocation = () => {
//     if (navigator.geolocation) {
//       setLocationLoading(true);
//       toast.info('Getting your location...');

//       navigator.geolocation.getCurrentPosition(
//         async (position) => {
//           const lat = position.coords.latitude;
//           const lon = position.coords.longitude;

//           setFormData(prev => ({
//             ...prev,
//             latitude: lat.toFixed(6),
//             longitude: lon.toFixed(6)
//           }));

//           const addressData = await reverseGeocode(lat, lon);

//           if (addressData) {
//             if (addressData.address) {
//               const addr = addressData.address;

//               const addressParts = [];
//               if (addr.house_number) addressParts.push(addr.house_number);
//               if (addr.road || addr.street) addressParts.push(addr.road || addr.street);
//               if (addr.neighbourhood) addressParts.push(addr.neighbourhood);
//               if (addr.suburb) addressParts.push(addr.suburb);
//               if (addr.city && addr.city !== addr.neighbourhood && addr.city !== addr.suburb) {
//                 addressParts.push(addr.city);
//               }

//               const fullAddress = addressParts.length > 0
//                 ? addressParts.filter(Boolean).join(', ')
//                 : addressData.display_name?.split(',').slice(0, 3).join(',') || '';

//               const apiStateName = addr.state || addr.principalSubdivision || '';
//               const matchedState = findClosestMatch(apiStateName, Object.keys(AllStatesAndDistricts));

//               if (matchedState) {
//                 setFormData(prev => ({
//                   ...prev,
//                   address: fullAddress,
//                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
//                   pincode: addr.postcode || prev.pincode
//                 }));

//                 const countryCode = addr.country_code ? addr.country_code.toUpperCase() : 'IN';

//                 if (countryCode !== formData.country) {
//                   handleCountryChange(countryCode);
//                   setTimeout(() => {
//                     handleStateChange(matchedState);
//                     setTimeout(() => {
//                       setDistrictFromGeocode(addr, matchedState);
//                       setLocationLoading(false);
//                     }, 300);
//                   }, 200);
//                 } else {
//                   handleStateChange(matchedState);
//                   setTimeout(() => {
//                     setDistrictFromGeocode(addr, matchedState);
//                     setLocationLoading(false);
//                   }, 300);
//                 }

//                 toast.success('Location and address captured successfully!');
//               } else {
//                 setFormData(prev => ({
//                   ...prev,
//                   address: fullAddress,
//                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
//                   state: apiStateName,
//                   pincode: addr.postcode || prev.pincode
//                 }));

//                 setLocationLoading(false);
//                 toast.warning('Location captured, but state/district may need manual adjustment');
//               }
//             }
//           } else {
//             setLocationLoading(false);
//             toast.warning('Location captured, but could not fetch address details. Please fill manually.');
//           }
//         },
//         (error) => {
//           console.error('Geolocation error:', error);
//           setLocationLoading(false);

//           let errorMessage = 'Unable to get location. ';
//           switch (error.code) {
//             case error.PERMISSION_DENIED:
//               errorMessage += 'Please allow location access in your browser settings.';
//               break;
//             case error.POSITION_UNAVAILABLE:
//               errorMessage += 'Location information is unavailable.';
//               break;
//             case error.TIMEOUT:
//               errorMessage += 'Request timed out. Please try again.';
//               break;
//             default:
//               errorMessage += 'An unknown error occurred.';
//           }
//           toast.error(errorMessage);
//         },
//         { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
//       );
//     } else {
//       toast.error('Geolocation is not supported by your browser');
//     }
//   };

//   const fetchOpenLeadDetails = useCallback(async () => {
//     setLoadingVisit(true);
//     try {
//       let lead = null;

//       if (SummaryApi.getOneExpoLead?.url) {
//         try {
//           const expoRes = await api.get(SummaryApi.getOneExpoLead.url(leadId));
//           if (expoRes.data?.success) {
//             lead = expoRes.data.data;
//           }
//         } catch {
//           console.log('Expo Lead fetch fallback...');
//         }
//       }

//       if (!lead) {
//         try {
//           const response = await api.get(SummaryApi.getOpenLeadById.url(leadId));
//           if (response.data?.success) {
//             lead = response.data.data;
//           }
//         } catch (err) {
//           console.error('Lead lookup error:', err);
//         }
//       }

//       if (lead) {
//         let matchedIndustryCategory = '';
//         let rawIndustryText = lead.industry || '';
//         if (!rawIndustryText && lead.division) {
//           rawIndustryText = Array.isArray(lead.division)
//             ? lead.division.join(' ')
//             : String(lead.division);
//         }

//         if (rawIndustryText) {
//           const targetText = String(rawIndustryText).toLowerCase().trim();
//           const foundIndustry = industryData.find(ind => {
//             const catLower = ind.industryCategory.toLowerCase().trim();
//             return catLower === targetText || catLower.includes(targetText) || targetText.includes(catLower);
//           });
//           if (foundIndustry) {
//             matchedIndustryCategory = foundIndustry.industryCategory;
//           }
//         }

//         let matchedProductCategories = [];
//         let matchedRequirements = [];
//         const rawRequirementText = lead.requirement || '';

//         if (rawRequirementText) {
//           productCategories.forEach(cat => {
//             if (rawRequirementText.toLowerCase().includes(cat.productCategory.toLowerCase())) {
//               matchedProductCategories.push(cat.productCategory);
//             }
//             cat.subCategories.forEach(sub => {
//               if (rawRequirementText.toLowerCase().includes(sub.toLowerCase())) {
//                 if (!matchedProductCategories.includes(cat.productCategory)) {
//                   matchedProductCategories.push(cat.productCategory);
//                 }
//                 matchedRequirements.push(sub);
//               }
//             });
//           });
//         }

//         const parsedDivisionId = Array.isArray(lead.division)
//           ? lead.division[0] || ''
//           : lead.division || '';

//         const parsedOwnerId = lead.ownerId
//           ? String(typeof lead.ownerId === 'object' ? (lead.ownerId._id || lead.ownerId.id) : lead.ownerId)
//           : '';

//         const leadProductId = lead.ourProductId?._id || lead.ourProductId || '';
//         const leadCategoryId = lead.ourProductBusinessCategoryId?._id || lead.ourProductBusinessCategoryId || '';

//         if (leadProductId) {
//           fetchBusinessCategories(leadProductId);
//         }

//         if (parsedDivisionId) {
//           fetchProgramsForDivision(parsedDivisionId);
//         }

//         setFormData(prev => ({
//           ...prev,
//           clientName: lead.companyOrSchool || lead.fullName || '',
//           industryType: matchedIndustryCategory || "Education & Training",
//           contactName: lead.fullName || '',
//           contactDesignation: lead.designation || '',
//           contactEmail: lead.email || '',
//           contactPhone: lead.mobile || '',
//           address: lead.address || lead.city || '',
//           locality: lead.locality || lead.city || '',
//           divisionId: parsedDivisionId || prev.divisionId,
//           sourceType: lead.source === 'expo' || lead.expoId ? 'expo' : (lead.source || 'expo'),
//           expoYear: lead.expoId?.expoYear ? String(lead.expoId.expoYear) : new Date().getFullYear().toString(),
//           expoPlace: lead.expoId?.expoName || lead.source || '',
//           productCategory: matchedProductCategories,
//           requirement: matchedRequirements,
//           ourProductId: leadProductId,
//           ourProductBusinessCategoryId: leadCategoryId,
//           remarks: lead.requirement || lead.remarks || '',
//           ownerId: parsedOwnerId || prev.ownerId
//         }));

//         toast.success(`Loaded Lead: ${lead.fullName}`);
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error('Failed to load lead information');
//     } finally {
//       setLoadingVisit(false);
//     }
//   }, [leadId, fetchBusinessCategories, fetchProgramsForDivision]);

//   const fetchVisitData = useCallback(async () => {
//     setLoadingVisit(true);
//     try {
//       const response = await api.get(SummaryApi.getOneClientVisit.url(id));

//       if (response.data.success) {
//         const visit = response.data.data;
//         setClientData(visit);

//         if (visit.locationImages && visit.locationImages.length > 0) {
//           setExistingImages(visit.locationImages);
//         }

//         let industrySubType = visit.industrySubType || '';
//         if (industrySubType.startsWith('Others - ')) {
//           setShowOtherIndustry(true);
//           setOtherIndustry(industrySubType.substring(9));
//           industrySubType = 'Others';
//         }
        
//         let productCats = [...(visit.productCategory || [])];
//         let otherCategoryText = "";
        
//         const otherCategoryIndex = productCats.findIndex(cat => cat.startsWith("Others - "));
//         if (otherCategoryIndex !== -1) {
//           otherCategoryText = productCats[otherCategoryIndex].substring(9);
//           productCats[otherCategoryIndex] = "Others - Mention that category";
//           setOtherProductCategory(otherCategoryText);
//           setShowOtherProductCategory(true);
//         }

//         const visitProductId = visit.ourProductId?._id || visit.ourProductId || '';
//         const visitCategoryId = visit.ourProductBusinessCategoryId?._id || visit.ourProductBusinessCategoryId || '';

//         if (visitProductId) {
//           fetchBusinessCategories(visitProductId);
//         }

//         const visitDivisionId = visit.divisionId?._id || visit.divisionId || '';
//         if (visitDivisionId) {
//           fetchProgramsForDivision(visitDivisionId);
//         }

//         let requirements = [...(visit.requirement || [])];
//         const otherReqs = {};
        
//         requirements = requirements.map(req => {
//           if (req.startsWith('Others - ')) {
//             const value = req.substring(9);
//             const newKey = "Others - Mention that product";
//             otherReqs[newKey] = value;
//             return newKey;
//           }
//           return req;
//         });
        
//         setOtherRequirements(otherReqs);
//         setShowOtherRequirement(Object.keys(otherReqs).length > 0);

//         const parsedOwnerId = visit.ownerId
//           ? String(typeof visit.ownerId === 'object' ? (visit.ownerId._id || visit.ownerId.id) : visit.ownerId)
//           : '';

//         setFormData({
//           visitType: visit.visitHistory?.[0]?.visitType || '',
//           sourceType: visit.sourceType || '',
//           expoYear: visit?.expoYear || '',
//           expoPlace: visit?.expoPlace || '',
//           visitDate: visit.visitHistory?.[0]?.visitDate ? new Date(visit.visitHistory[0].visitDate) : null,
//           visitTime: visit.visitHistory?.[0]?.visitTime || new Date().toTimeString().slice(0, 5),
//           nextFollowUpDate: visit.nextFollowUpDate ? new Date(visit.nextFollowUpDate) : null,
//           nextFollowUpTime: visit.nextFollowUpTime || '',
//           divisionId: visitDivisionId,
//           divisionName: visit.divisionId?.name || '',
//           clientName: visit.clientName || '',
//           studentName: visit.studentName || '',
//           age: visit.age || '',
//           profession: visit.profession || '',
//           industryType: visit.industryType || '',
//           industrySubType: industrySubType || '',
//           ourProductId: visitProductId,
//           ourProductBusinessCategoryId: visitCategoryId,
//           programId: visit.programId?._id || visit.programId || '',
//           selectedPlanName: visit.selectedPlanName || '',
//           contactName: visit.contactName || '',
//           contactDesignation: visit.contactDesignation || '',
//           contactEmail: visit.contactEmail || '',
//           contactPhone: visit.contactPhone || '',
//           contactLandline: visit.contactLandline || '',
//           address: visit.address || '',
//           locality: visit.locality || '',
//           district: visit.district || '',
//           state: visit.state || '',
//           country: visit.country || 'IN',
//           pincode: visit.pincode || '',
//           latitude: visit.latitude || '',
//           longitude: visit.longitude || '',
//           productCategory: productCats || [],
//           requirement: requirements || [],
//           estimatedValue: visit.estimatedValue || 0,
//           expectedValue: visit.expectedValue || 0,
//           recurringValue: visit.recurringValue || 0,
//           currency: visit.currency || 'INR',
//           stageId: visit.stageId?._id || '',
//           expectedClosingDate: visit.expectedClosingDate ? new Date(visit.expectedClosingDate) : null,
//           paymentStatus: visit.paymentStatus || 'na',
//           remarks: visit.visitHistory?.[0]?.remarks || '',
//           ownerId: parsedOwnerId,
//         });
//       }
//     } catch (error) {
//       console.error('Error fetching visit data:', error);
//       toast.error('Failed to load visit data. Please try again.');
//       navigate(-1);
//     } finally {
//       setLoadingVisit(false);
//     }
//   }, [id, navigate, fetchBusinessCategories, fetchProgramsForDivision]);

//   useEffect(() => {
//     if (isEditMode && id) {
//       fetchVisitData();
//     } else if (isConvertMode && leadId) {
//       fetchOpenLeadDetails();
//     }
//   }, [isEditMode, isConvertMode, id, leadId, fetchVisitData, fetchOpenLeadDetails]);

//   useEffect(() => {
//     const allCountries = Country.getAllCountries().map(country => ({
//       isoCode: country.isoCode,
//       name: country.name
//     }));
//     setCountries(allCountries);
//   }, []);

//   useEffect(() => {
//     if (formData.industryType) {
//       const industry = industryData.find(ind => ind.industryCategory === formData.industryType);
//       if (industry) {
//         let subCategories = industry.subCategories.map(sub => ({
//           id: sub,
//           label: sub
//         }));
        
//         if (!subCategories.some(sub => sub.id === 'Others')) {
//           subCategories.push({ id: 'Others', label: 'Others' });
//         }
        
//         setFilteredSubIndustries(subCategories);
//       }
//     } else {
//       setFilteredSubIndustries([]);
//       if (!isEditMode) {
//         setFormData(prev => ({ ...prev, industrySubType: '' }));
//       }
//     }
//   }, [formData.industryType, isEditMode]);

//   useEffect(() => {
//     setShowOtherIndustry(formData.industrySubType === 'Others');
//   }, [formData.industrySubType]);

//   useEffect(() => {
//     if (isEditMode && clientData) {
//       const otherCategoryMatch = clientData.productCategory?.find(cat => cat.includes('Others - '));
//       if (otherCategoryMatch) {
//         const categoryMatch = otherCategoryMatch.match(/Others - (.+)/);
//         if (categoryMatch) {
//           setOtherProductCategory(categoryMatch[1]);
//           setShowOtherProductCategory(true);
//         }
//       }
//     }
//   }, [isEditMode, clientData]);

//   useEffect(() => {
//     if (formData.productCategory && formData.productCategory.length > 0) {
//       const allRequirements = [];
//       formData.productCategory.forEach(categoryId => {
//         const category = productCategories.find(cat => cat.productCategory === categoryId);
//         if (category) {
//           category.subCategories.forEach(sub => {
//             if (!allRequirements.find(req => req.id === sub)) {
//               allRequirements.push({ id: sub, label: sub });
//             }
//           });
//         }
//       });
//       setFilteredRequirements(allRequirements);
//     } else {
//       setFilteredRequirements([]);
//       if (!isEditMode) {
//         setFormData(prev => ({ ...prev, requirement: [] }));
//       }
//     }
//   }, [formData.productCategory, isEditMode]);

//   useEffect(() => {
//     if (formData.state && AllStatesAndDistricts[formData.state]) {
//       const stateDistricts = AllStatesAndDistricts[formData.state].map(district => ({
//         id: district,
//         label: district
//       }));
//       setDistricts(stateDistricts);
//     } else {
//       setDistricts([]);
//       if (!isEditMode) {
//         setFormData(prev => ({ ...prev, district: '' }));
//       }
//     }
//   }, [formData.state, isEditMode]);

//   const fetchDivisions = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.getAllDivisions.url);
//       const divisionsData = response.data.data || [];
//       setDivisions(divisionsData.map(div => ({
//         id: div._id,
//         label: div.name
//       })));
//     } catch (error) {
//       console.error('Error fetching divisions:', error);
//       setDivisions([]);
//     }
//   }, []);

//   useEffect(() => {
//     fetchDivisions();
//   }, [fetchDivisions]);

//   const fetchSalesStages = useCallback(async (selectedDivisionId = null) => {
//     if (!selectedDivisionId) {
//       setSalesStages([]);
//       return;
//     }

//     setLoading(true);
//     try {
//       const params = {
//         includeInactive: false,
//         includeShared: true,
//         divisionId: selectedDivisionId,
//       };
//       const response = await api.get(SummaryApi.getAllStages.url, { params });
//       if (response.data.success) {
//         setSalesStages(response.data.data || []);
//       }
//     } catch (error) {
//       console.error('Failed to fetch stages:', error);
//       setSalesStages([]);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     if (formData.divisionId) {
//       fetchSalesStages(formData.divisionId);
//     }
//   }, [formData.divisionId, fetchSalesStages]);


//   const handleChange = (e) => {
//     const { name, value, type } = e.target;
//     let processedValue = value;

//     if (type === "number") {
//       if (value === "0") {
//         processedValue = "0";
//       } else {
//         processedValue = value.replace(/^0+/, '');
//       }
//     }

//     setFormData(prev => ({
//       ...prev,
//       [name]: processedValue
//     }));

//     if (errors[name]) {
//       setErrors(prev => ({ ...prev, [name]: '' }));
//     }
//   };

//   const handleProductCategoryChange = (selectedCategories) => {
//     setFormData(prev => ({ ...prev, productCategory: selectedCategories }));

//     const hasOthers = selectedCategories.includes("Others - Mention that category");
//     setShowOtherProductCategory(hasOthers);

//     if (!hasOthers) {
//       setOtherProductCategory('');
//     }

//     updateAvailableRequirements(selectedCategories);
//   };

//   const updateAvailableRequirements = (selectedCategories) => {
//     if (!selectedCategories || selectedCategories.length === 0) {
//       setFilteredRequirements([]);
//       if (!isEditMode) {
//         setFormData(prev => ({ ...prev, requirement: [] }));
//       }
//       return;
//     }

//     const allRequirements = [];
//     selectedCategories.forEach(categoryId => {
//       const category = productCategories.find(cat => cat.productCategory === categoryId);
//       if (category) {
//         category.subCategories.forEach(sub => {
//           if (!allRequirements.find(req => req.id === sub)) {
//             allRequirements.push({ id: sub, label: sub });
//           }
//         });
//       }
//     });
    
//     setFilteredRequirements(allRequirements);

//     const validRequirementIds = new Set(allRequirements.map(req => req.id));
//     const updatedRequirements = formData.requirement.filter(req => 
//       validRequirementIds.has(req) || req === "Others - Mention that product"
//     );
    
//     if (updatedRequirements.length !== formData.requirement.length) {
//       setFormData(prev => ({ ...prev, requirement: updatedRequirements }));
//     }
//   };

//   const handleIndustrySubTypeChange = (value) => {
//     setFormData(prev => ({ ...prev, industrySubType: value }));
//     setShowOtherIndustry(value === 'Others');
//     if (value !== 'Others') {
//       setOtherIndustry('');
//     }
//   };

//   const handleRequirementChange = (selectedRequirements) => {
//     setFormData(prev => ({ ...prev, requirement: selectedRequirements }));

//     const selectedOthers = selectedRequirements.filter(req => 
//       req.includes('Others - Mention that product')
//     );
//     setShowOtherRequirement(selectedOthers.length > 0);

//     const newOtherRequirements = {};
//     selectedOthers.forEach(req => {
//       if (otherRequirements[req]) {
//         newOtherRequirements[req] = otherRequirements[req];
//       } else {
//         newOtherRequirements[req] = '';
//       }
//     });
//     setOtherRequirements(newOtherRequirements);
//   };

//   const handleOtherRequirementChange = (category, value) => {
//     setOtherRequirements(prev => ({
//       ...prev,
//       [category]: value
//     }));
//   };

//   const handleDivisionChange = (divisionId) => {
//     const selectedDivision = divisions.find(div => div.id === divisionId);
//     const divName = selectedDivision?.label || '';

//     setFormData(prev => ({
//       ...prev,
//       divisionId: divisionId,
//       divisionName: divName,
//       stageId: '', 
//       ourProductId: '',
//       ourProductBusinessCategoryId: '',
//       programId: '',
//       selectedPlanName: '',
//     }));

//     setOurProductList([]);
//     setOurBusinessCategoryList([]);
//     if (errors.divisionId) setErrors(prev => ({ ...prev, divisionId: '' }));

//     fetchOurProducts(divisionId);
//     fetchProgramsForDivision(divisionId);
//     fetchSalesStages(divisionId);
//   };

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.divisionId) newErrors.divisionId = 'Division is required';
//     if (!formData.clientName) newErrors.clientName = 'Client / Parent name is required';

//     // If minor (<18), student name is required
//     if (formData.age && Number(formData.age) < 18 && !formData.studentName?.trim()) {
//       newErrors.studentName = 'Student name is required for minors (<18)';
//     }

//     // Industry type is required ONLY if NOT DNBS division
//     if (!isDnbsDivision && !formData.industryType) {
//       newErrors.industryType = 'Industry type is required';
//     }

//     if (!formData.contactName) newErrors.contactName = 'Contact name is required';

//     // Validate ERP Product if CUSO Division
//     // if (isCusoDivision && !formData.ourProductId) {
//     //   newErrors.ourProductId = 'Our ERP Product is required for CUSO division';
//     // }

//     if (isDigidaDivision) {
//       if (!formData.productCategory || formData.productCategory.length === 0) {
//         newErrors.productCategory = 'Product Category is required for Digida division';
//       }
//       if (!formData.requirement || formData.requirement.length === 0) {
//         newErrors.requirement = 'Requirement is required for Digida division';
//       }
//     }
//     // =========================================================================

//     if (!formData.contactPhone && !formData.contactLandline) {
//       newErrors.contactPhone = 'Either Contact Phone or Landline is required';
//       newErrors.contactLandline = 'Either Contact Phone or Landline is required';
//     }

//     const phoneRegex = /^[6-9]\d{9}$/;
//     if (formData.contactPhone && !phoneRegex.test(formData.contactPhone.replace(/\D/g, ''))) {
//       newErrors.contactPhone = 'Invalid phone number (10 digits required)';
//     }

//     if (!formData.stageId) newErrors.stageId = 'Stage is required';
    
//     if (formData.estimatedValue === '' || formData.estimatedValue === null || formData.estimatedValue === undefined) {
//       newErrors.estimatedValue = 'Invoice Value is required';
//     }

//     if (!formData.visitDate && !isEditMode) newErrors.visitDate = 'Visit date is required';
//     if (!formData.visitType && !isEditMode) newErrors.visitType = 'Visit type is required';
//     if (!formData.sourceType && !isEditMode) newErrors.sourceType = 'Source type is required';

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//       return;
//     }

//     setLoading(true);
//     setSubmitSuccess(false);
//     setSubmitMessage('');

//     try {
//       let processedIndustrySubType = formData.industrySubType;
//       if (formData.industrySubType === 'Others' && otherIndustry.trim()) {
//         processedIndustrySubType = `Others - ${otherIndustry}`;
//       }

//       const processedProductCategories = formData.productCategory.map(category => {
//         if (category === "Others - Mention that category" && otherProductCategory.trim()) {
//           return `Others - ${otherProductCategory}`;
//         }
//         return category;
//       });

//       const processedRequirements = formData.requirement.map(req => {
//         if (req === "Others - Mention that product" && otherRequirements[req]) {
//           return `Others - ${otherRequirements[req]}`;
//         }
//         return req;
//       });

//       const formDataToSend = new FormData();

//       const submitData = {
//         ...formData,

//         programId: formData.programId || null,
//         selectedPlanName: formData.selectedPlanName || '',
//         // Common Vertical IDs (Sent both for zero mismatch)
//         businessVerticalId: formData.ourProductId || null,
//         ourProductId: formData.ourProductId || null,

//         branchCount: isCusoDivision && formData.branchCount ? Number(formData.branchCount) : 1,
//         employeeCount: isCusoDivision && formData.employeeCount ? Number(formData.employeeCount) : null,
//         annualTurnover: isCusoDivision ? formData.annualTurnover : '',
//         registrationType: isCusoDivision ? formData.registrationType : 'unregistered',
//         taxType: isCusoDivision && formData.registrationType === 'registered' ? formData.taxType : 'na',
//         taxNumber: isCusoDivision && formData.registrationType === 'registered' ? formData.taxNumber.trim().toUpperCase() : '',
//         preferredPartnerType: isCusoDivision ? formData.preferredPartnerType : '',
//         ourProductBusinessCategoryId: isCusoDivision ? (formData.ourProductBusinessCategoryId || null) : null,

//         // BRAWNY & DNBS Specific Fields (Clear if CUSO)
//         studentName: isEducationDivision ? (formData.studentName?.trim() || '') : '',
//         age: isEducationDivision && formData.age ? Number(formData.age) : null,
//         profession: isEducationDivision ? (formData.profession?.trim() || '') : '',


//         expoYear: formData.sourceType === 'expo' ? formData.expoYear : '',
//         expoPlace: formData.sourceType === 'expo' ? formData.expoPlace : '',
//         industrySubType: processedIndustrySubType,
//         productCategory: processedProductCategories,
//         requirement: processedRequirements,
//         divisionName: formData.divisionName,
//         nextFollowUpDate: formData.nextFollowUpDate,
//         estimatedValue: parseFloat(formData.estimatedValue) || 0,
//         expectedValue: parseFloat(formData.expectedValue) || 0,
//         recurringValue: parseFloat(formData.recurringValue) || 0,
//       };

//       if (isEditMode) {
//         delete submitData.visitDate;
//         delete submitData.visitTime;
//         delete submitData.visitType;
//         delete submitData.remarks;

//         submitData.existingImages = existingImages;
//       } else {
//         submitData.initialVisitDate = formData.visitDate;
//         submitData.initialVisitTime = formData.visitTime;
//       }

//       if (isConvertMode) {
//         submitData.leadId = leadId;
//       }

//       Object.keys(submitData).forEach(key => {
//         if (submitData[key] !== null && submitData[key] !== undefined) {
//           if (Array.isArray(submitData[key])) {
//             formDataToSend.append(key, JSON.stringify(submitData[key]));
//           } else if (submitData[key] instanceof Date) {
//             formDataToSend.append(key, submitData[key].toISOString());
//           } else {
//             formDataToSend.append(key, submitData[key]);
//           }
//         }
//       });

//       locationImages.forEach((image) => {
//         formDataToSend.append('locationImages', image);
//       });

//       let apiConfig = SummaryApi.createClientVisit;
//       let targetUrl = '';

//       if (isEditMode) {
//         apiConfig = SummaryApi.updateClientVisit;
//         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(id) : apiConfig.url;
//       } else if (isConvertMode) {
//         apiConfig = SummaryApi.convertExpoLeadToVisit || SummaryApi.convertLeadToVisit;
//         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(leadId) : apiConfig.url;
//       } else {
//         apiConfig = SummaryApi.createClientVisit;
//         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url() : apiConfig.url;
//       }

//       const response = isEditMode
//         ? await api.put(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } })
//         : await api.post(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });

//       if (response.data.success) {
//         setSubmitSuccess(true);
//         const message = isEditMode
//           ? 'Client visit updated successfully!'
//           : `Client visit created successfully!\nClient ID: ${response.data.data.clientId}`;

//         setSubmitMessage(message);
//         toast.success(message);

//         setTimeout(() => {
//           resetForm();
//           setSubmitSuccess(false);
//           setSubmitMessage('');
//           navigate(-1);
//         }, 2000);
//       } else {
//         toast.error(response.data.message || 'Unexpected response from server.');
//       }
//     } catch (error) {
//       console.error('Error submitting form details:', error.response?.data || error);
//       const errorMessage =
//         error.response?.data?.message ||
//         error.response?.data?.error ||
//         (Array.isArray(error.response?.data?.errors) ? error.response.data.errors.join(', ') : null) ||
//         error.message ||
//         'Error submitting form. Please try again.';
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const resetForm = () => {
//     setFormData({
//       visitType: '',
//       sourceType: '',
//       expoYear: '',
//       expoPlace: '',
//       visitDate: new Date(),
//       visitTime: new Date().toTimeString().slice(0, 5),
//       nextFollowUpDate: null,
//       nextFollowUpTime: '',
//       divisionId: '',
//       divisionName: '',
//       clientName: '',
//       studentName: '',
//       age: '',
//       profession: '',
//       industryType: '',
//       industrySubType: '',
//       ourProductId: '',
//       ourProductBusinessCategoryId: '',
//       programId: '',
//       selectedPlanName: '',
//       contactName: '',
//       contactDesignation: '',
//       contactEmail: '',
//       contactPhone: '',
//       contactLandline: '',
//       address: '',
//       locality: '',
//       district: '',
//       state: '',
//       country: 'IN',
//       pincode: '',
//       latitude: '',
//       longitude: '',
//       productCategory: [],
//       requirement: [],
//       estimatedValue: 0,
//       expectedValue: 0,
//       recurringValue: 0,
//       currency: 'INR',
//       stageId: '',
//       expectedClosingDate: null,
//       paymentStatus: 'na',
//       remarks: '',
//       ownerId: user?._id || ''
//     });
//     setOurBusinessCategoryList([]);
//     setProgramList([]);
//     setAvailablePlans([]);
//     setErrors({});
//     setOtherIndustry('');
//     setOtherRequirements({});
//     setShowOtherRequirement(false);
//     setLocationImages([]);
//     setExistingImages([]);
//   };

//   const industryOptions = industryData.map(ind => ({
//     id: ind.industryCategory,
//     label: `${ind.icon} ${ind.industryCategory}`
//   }));

//   const productCategoryOptions = productCategories.map(cat => ({
//     id: cat.productCategory,
//     label: cat.productCategory
//   }));

//   useEffect(() => {
//     if (formData.country === 'IN') {
//       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
//         id: state,
//         label: state
//       }));
//       setStates(indianStates);
//     }
//   }, [formData.country]);

//   if (loadingVisit) {
//     return (
//       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//         <div className="text-center">
//           <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//           </svg>
//           <p className="text-gray-600">Loading visit data...</p>
//         </div>
//       </div>
//     );
//   }

//   const customClassName = (value) => {
//     return `bg-transparent border outline-none shadow-sm rounded-[7px] px-2 py-1.5 ${value ? "border-blue-600 text-blue-600" : "border-gray-300"}`;
//   };

//   return (
//     <div className="min-h-screen bg-gray-50">
//       <div>
//         {submitSuccess && (
//           <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
//             <p className="font-medium">{submitMessage}</p>
//           </div>
//         )}

//         <div className="bg-white overflow-hidden">
//           <div className="bg-gradient-to-r from-red-600 to-red-600 px-6 py-2">
//             <div className="flex justify-between items-center text-white">
//               <div>
//                 <h1 className="text-2xl font-bold">
//                   {isEditMode ? 'Edit Client Visit' : (isConvertMode ? 'Convert Expo Lead to Client Visit' : 'Client Visit Form')}
//                 </h1>
//                 <p className="text-blue-100 text-sm mt-1">
//                   {isEditMode ? `Editing visit for Client ID: ${clientData?.clientId}` : (isConvertMode ? 'Converting Open Lead Details' : 'Record new client visit details')}
//                 </p>
//               </div>
//               <button onClick={() => navigate(-1)} className="hover:text-blue-100" title="Go back">
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
//               </button>
//             </div>
//           </div>

//           <form onSubmit={handleSubmit} className="p-2 space-y-2.5">
//             {/* 1. Visit Information */}
//             {!isEditMode && (
//               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                   <span className="text-2xl mr-2">📅</span> Visit Information
//                 </h2>
//                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Division <span className="text-red-500">*</span></label>
//                     <SelectDropdown options={divisions} value={formData.divisionId} onChange={handleDivisionChange} placeholder="Select Division" valueKey="id" labelKey="label" error={errors.divisionId} />
//                     {errors.divisionId && <p className="text-red-500 text-xs mt-1">{errors.divisionId}</p>}
//                   </div>
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                        Business Vertical <span className="text-red-500">*</span>
//                       </label>
//                       <SelectDropdown
//                         options={ourProductList}
//                         value={formData.businessVerticalId}
//                         onChange={handleOurProductChange}
//                         placeholder={formData.divisionId ? "Select Business Vertical" : "Select Division First"}
//                         searchable={true}
//                         valueKey="id"
//                         labelKey="label"
//                         disabled={!formData.divisionId}
//                         error={errors.businessVerticalId}
//                       />
//                       {/* {errors.businessVerticalId && <p className="text-red-500 text-xs mt-1">{errors.businessVerticalId}</p>} */}
//                     </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
//                     <SelectDropdown
//                       options={executiveList}
//                       value={formData.ownerId}
//                       onChange={handleOwnerChange}
//                       placeholder="Select Owner"
//                       searchable={true}
//                       valueKey="id"
//                       labelKey="label"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Call Type <span className="text-red-500">*</span></label>
//                     <SelectDropdown options={visitTypeOptions} value={formData.visitType} onChange={(v) => setFormData(prev => ({ ...prev, visitType: v }))} placeholder="Select Visit Type" valueKey="id" labelKey="label" />
//                     {errors.visitType && <p className="text-red-500 text-xs mt-1">{errors.visitType}</p>}
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
//                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" />
//                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
//                   </div>

//                   {formData.sourceType === 'expo' && (
//                     <>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
//                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" error={errors.expoYear} />
//                         {errors.expoYear && <p className="text-red-500 text-xs mt-1">{errors.expoYear}</p>}
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
//                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
//                         {errors.expoPlace && <p className="text-red-500 text-xs mt-1">{errors.expoPlace}</p>}
//                       </div>
//                     </>
//                   )}

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Call Date <span className="text-red-500">*</span></label>
//                     <CustomDatePicker value={formData.visitDate} onChange={(date) => setFormData(prev => ({ ...prev, visitDate: date }))} placeholder="Select date" error={errors.visitDate} />
//                     {errors.visitDate && <p className="text-red-500 text-xs mt-1">{errors.visitDate}</p>}
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Call Time</label>
//                     <input type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} className={`w-full ${customClassName(formData.visitTime)}`} />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
//                     <CustomDatePicker value={formData.nextFollowUpDate} onChange={(date) => setFormData(prev => ({ ...prev, nextFollowUpDate: date }))} placeholder="Select follow-up date" minDate={new Date()} />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Time</label>
//                     <input type="time" name="nextFollowUpTime" value={formData.nextFollowUpTime} onChange={handleChange} className={`w-full ${customClassName(formData.nextFollowUpTime)}`} />
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Organization Information on Edit mode */}
//             {isEditMode && (
//               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">🏢</span> Organization Information</h2>
//                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
//                     <input type="text" value={formData.divisionName} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
//                     <input type="text" value={clientData?.clientId || ''} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
//                     <SelectDropdown
//                       options={executiveList}
//                       value={formData.ownerId}
//                       onChange={handleOwnerChange}
//                       placeholder="Select Executive Owner"
//                       searchable={true}
//                       valueKey="id"
//                       labelKey="label"
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
//                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" error={errors.sourceType} />
//                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
//                   </div>

//                   {formData.sourceType === 'expo' && (
//                     <>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
//                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" />
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
//                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
//                       </div>
//                     </>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* 2. Client & Minor Student Information */}
//             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                 <span className="text-2xl mr-2">🏢</span> Client & Candidate Information
//               </h2>
//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
//                 {/* Client / Parent Name */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Company / Parent Name / School / College / Student <span className="text-red-500">*</span>
//                   </label>
//                   <input 
//                     type="text" 
//                     name="clientName" 
//                     value={formData.clientName} 
//                     onChange={handleChange} 
//                     className={`w-full ${customClassName(formData.clientName)} ${errors.clientName ? 'border-red-500' : ''}`} 
//                     placeholder="Enter parent or company name" 
//                   />
//                   {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName}</p>}
//                 </div>

//                 {/* Industry Type: NOT required for DNBS */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Industry Type {!isDnbsDivision && <span className="text-red-500">*</span>}
//                   </label>
//                   <SelectDropdown 
//                     options={industryOptions} 
//                     value={formData.industryType} 
//                     onChange={(v) => setFormData(prev => ({ ...prev, industryType: v }))} 
//                     placeholder={isDnbsDivision ? "Select Industry (Optional)" : "Select Industry"} 
//                     searchable 
//                     valueKey="id" 
//                     labelKey="label" 
//                     error={errors.industryType} 
//                   />
//                   {errors.industryType && <p className="text-red-500 text-xs mt-1">{errors.industryType}</p>}
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Industry Sub-Type</label>
//                   <SelectDropdown options={filteredSubIndustries} value={formData.industrySubType} onChange={handleIndustrySubTypeChange} placeholder="Select Sub-Industry" valueKey="id" labelKey="label" disabled={!formData.industryType} />
//                 </div>

//                 {showOtherIndustry && (
//                   <div className="sm:col-span-2 lg:col-span-3">
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Specify Industry <span className="text-red-500">*</span></label>
//                     <input type="text" value={otherIndustry} onChange={(e) => setOtherIndustry(e.target.value)} className={`w-full ${customClassName(otherIndustry)} ${errors.otherIndustry ? 'border-red-500' : ''}`} placeholder="Please specify the industry" />
//                     {errors.otherIndustry && <p className="text-red-500 text-xs mt-1">{errors.otherIndustry}</p>}
//                   </div>
//                 )}
//               </div>

//                 {/*  Conditional Student Name (Shown only if age < 18) */}
//                 {isEducationDivision && (
//                  <div className="mt-4">
//                     <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-1.5">
//                       <GraduationCap className="w-4 h-4 text-blue-600" />
//                       Student & Candidate Details (Brawny & DNBS)
//                     </h3>
//                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                   
//                       <div>
//                         <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
//                           <Briefcase className="w-3.5 h-3.5 text-gray-500" /> Profession
//                         </label>
//                         <SelectDropdown
//                           options={professionOptions}
//                           value={formData.profession}
//                           onChange={(val) => setFormData(prev => ({ ...prev, profession: val }))}
//                           placeholder="Select Profession"
//                           valueKey="id"
//                           labelKey="label"
//                         />
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Student / Candidate Name {formData.age && Number(formData.age) < 18 && <span className="text-red-500">*</span>}
//                         </label>
//                         <input
//                           type="text"
//                           name="studentName"
//                           value={formData.studentName}
//                           onChange={handleChange}
//                           className={`w-full ${customClassName(formData.studentName)}`}
//                           placeholder="Student full name"
//                         />
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
//                         <input
//                           type="number"
//                           name="age"
//                           min="0"
//                           max="100"
//                           value={formData.age}
//                           onChange={handleChange}
//                           className={`w-full ${customClassName(formData.age)}`}
//                           placeholder="e.g., 16"
//                         />
//                       </div>

//                     </div>
//                   </div>
//                 )}

//                 {/* ========================================================= */}
//                 {/* SECTION B: CUSO ONLY (B2B Profile & Tax Compliance) */}
//                 {/* ========================================================= */}
//                 {isCusoDivision && (
//                   <div className="mt-4 rounded-lg">
//                     <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-1.5">
//                       <Briefcase className="w-4 h-4 text-blue-600" />
//                       Business Profile & Compliance Details (CUSO Division)
//                     </h3>
//                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Business Vertical / Product <span className="text-red-500">*</span>
//                         </label>
//                         <SelectDropdown
//                           options={ourProductList}
//                           value={formData.ourProductId}
//                           onChange={handleOurProductChange}
//                           placeholder={formData.divisionId ? "Select Vertical / Product" : "Select Division First"}
//                           searchable={true}
//                           valueKey="id"
//                           labelKey="label"
//                           disabled={!formData.divisionId}
//                           error={errors.ourProductId}
//                         />
//                         {/* {errors.ourProductId && <p className="text-red-500 text-xs mt-1">{errors.ourProductId}</p>} */}
//                       </div>
                    
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Product Business Category
//                         </label>
//                         <SelectDropdown
//                           options={ourBusinessCategoryList}
//                           value={formData.ourProductBusinessCategoryId}
//                           onChange={(val) => setFormData(prev => ({ ...prev, ourProductBusinessCategoryId: val }))}
//                           placeholder={formData.ourProductId ? "Select Business Category" : "Select ERP Product First"}
//                           searchable={true}
//                           disabled={!formData.ourProductId}
//                           valueKey="id"
//                           labelKey="label"
//                         />
//                       </div>
//                       {/* Branch Count */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Branch Count</label>
//                         <input
//                           type="number"
//                           name="branchCount"
//                           min="1"
//                           value={formData.branchCount}
//                           onChange={handleChange}
//                           className={`w-full ${customClassName(formData.branchCount)}`}
//                           placeholder="e.g., 3"
//                         />
//                       </div>

//                       {/* Employee Count */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Employees Count</label>
//                         <input
//                           type="number"
//                           name="employeeCount"
//                           min="0"
//                           value={formData.employeeCount}
//                           onChange={handleChange}
//                           className={`w-full ${customClassName(formData.employeeCount)}`}
//                           placeholder="e.g., 25"
//                         />
//                       </div>

//                       {/* Annual Turnover */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Annual Turnover</label>
//                         <SelectDropdown
//                           options={annualTurnoverOptions}
//                           value={formData.annualTurnover}
//                           onChange={(val) => setFormData(prev => ({ ...prev, annualTurnover: val }))}
//                           placeholder="Select Turnover Range"
//                           valueKey="id"
//                           labelKey="label"
//                         />
//                       </div>

//                       {/* Registration Type */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Business Registration</label>
//                         <SelectDropdown
//                           options={registrationOptions}
//                           value={formData.registrationType}
//                           onChange={(val) => setFormData(prev => ({ 
//                             ...prev, 
//                             registrationType: val,
//                             taxType: val === 'registered' ? 'gst' : 'na',
//                             taxNumber: val === 'registered' ? prev.taxNumber : ''
//                           }))}
//                           valueKey="id"
//                           labelKey="label"
//                         />
//                       </div>

//                       {/* Tax Type & Tax Number (Only if Registered) */}
//                       {formData.registrationType === 'registered' && (
//                         <>
//                           <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">Tax Type</label>
//                             <SelectDropdown
//                               options={taxTypeOptions}
//                               value={formData.taxType}
//                               onChange={(val) => setFormData(prev => ({ ...prev, taxType: val }))}
//                               valueKey="id"
//                               labelKey="label"
//                             />
//                           </div>

//                           <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">Tax Number / GSTIN</label>
//                             <input
//                               type="text"
//                               name="taxNumber"
//                               value={formData.taxNumber}
//                               onChange={(e) => setFormData(prev => ({ ...prev, taxNumber: e.target.value.toUpperCase() }))}
//                               className={`w-full ${customClassName(formData.taxNumber)} uppercase`}
//                               placeholder="e.g., 33AAAAA0000A1Z5"
//                             />
//                           </div>
//                         </>
//                       )}

//                       {/* Preferred Partner Type */}
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Partner Type</label>
//                         <SelectDropdown
//                           options={partnerTypeOptions}
//                           value={formData.preferredPartnerType}
//                           onChange={(val) => setFormData(prev => ({ ...prev, preferredPartnerType: val }))}
//                           placeholder="Select Partner Type"
//                           valueKey="id"
//                           labelKey="label"
//                         />
//                       </div>
//                     </div>
//                   </div>
//                 )}
//             </div>

//             {/*  3. Division Program & Plan Offerings */}
//             {!isDigidaDivision && (
//               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">

//                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
//                   <SectionIcon size={22} className="text-primary" />
//                   {config.title}
//                 </h2>
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 <div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   {isCusoDivision
//                     ? "Select Subscription Plan"
//                     : isBrawnyDivision
//                     ? "Select Program"
//                     : isDnbsDivision
//                     ? "Select Course"
//                     : "Select Program / Course"}
//                 </label>

//                 <SelectDropdown
//                   options={programList}
//                   value={formData.programId}
//                   onChange={handleProgramChange}
//                   placeholder={
//                     !formData.divisionId
//                       ? "Select Division First"
//                       : programList.length === 0
//                       ? isCusoDivision
//                         ? "No Subscription Plans Available"
//                         : "No Programs / Courses Available"
//                       : isCusoDivision
//                       ? "Select Subscription Plan"
//                       : isBrawnyDivision
//                       ? "Select Program (e.g. Archery)"
//                       : isDnbsDivision
//                       ? "Select Course (e.g. AI Bootcamp)"
//                       : "Select Program / Course"
//                   }
//                   searchable={true}
//                   valueKey="id"
//                   labelKey="label"
//                   disabled={!formData.divisionId || programList.length === 0}
//                 />

//                 {errors.programId && (
//                   <p className="text-red-500 text-xs mt-1">{errors.programId}</p>
//                 )}
//               </div>
//                   {errors.programId && <p className="text-red-500 text-xs mt-1">{errors.programId}</p>}
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Select Plan (Monthly / Annual / One-time)
//                   </label>
//                   <SelectDropdown
//                     options={availablePlans}
//                     value={formData.selectedPlanName}
//                     onChange={handlePlanChange}
//                     placeholder={formData.programId ? "Select Pricing Plan" : "Select Program First"}
//                     searchable={false}
//                     valueKey="id"
//                     labelKey="label"
//                     disabled={!formData.programId}
//                   />
//                 </div>
//               </div>
//             </div>)}

//             {/* 4. Contact Details */}
//             <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">👤</span> Contact Details</h2>
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name <span className="text-red-500">*</span></label>
//                   <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className={`w-full ${customClassName(formData.contactName)} ${errors.contactName ? 'border-red-500' : ''}`} placeholder="Full name" />
//                   {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
//                   <input type="text" name="contactDesignation" value={formData.contactDesignation} onChange={handleChange} className={`w-full ${customClassName(formData.contactDesignation)}`} placeholder="e.g., CEO, Manager, Director" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
//                   <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className={`w-full ${customClassName(formData.contactEmail)} ${errors.contactEmail ? 'border-red-500' : ''}`} placeholder="email@example.com" />
//                   {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
//                   <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className={`w-full ${customClassName(formData.contactPhone)} ${errors.contactPhone ? 'border-red-500' : ''}`} placeholder="10 digit mobile number" />
//                   {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Landline {!formData.contactPhone && <span className="text-red-500">*</span>}</label>
//                   <input type="text" name="contactLandline" value={formData.contactLandline} onChange={handleChange} className={`w-full ${customClassName(formData.contactLandline)} ${errors.contactLandline ? 'border-red-500' : ''}`} placeholder="Landline number with STD code" />
//                   {errors.contactLandline && <p className="text-red-500 text-xs mt-1">{errors.contactLandline}</p>}
//                 </div>
//               </div>
//             </div>

//             {/* 5. Location Information */}
//             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//               <div className="flex justify-between items-center mb-4">
//                 <h2 className="text-lg font-semibold text-gray-800 flex items-center"><span className="text-2xl mr-2">📍</span> Location Information</h2>
//                 <button
//                   type="button"
//                   onClick={getCurrentLocation}
//                   disabled={locationLoading}
//                   className={`px-2 py-1.5 ${locationLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-[7px] transition-colors text-sm font-medium shadow-sm flex items-center gap-2`}
//                 >
//                   {locationLoading ? (
//                     <>
//                       <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                       </svg>
//                       <span className="hidden sm:inline">Getting Location...</span>
//                       <span className="sm:hidden">Loading...</span>
//                     </>
//                   ) : (
//                     <>
//                       <span className="hidden sm:inline">Get Current Location</span>
//                       <span className="sm:hidden">Get Loca...</span>
//                     </>
//                   )}
//                 </button>
//               </div>

//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
//                 <div className="sm:col-span-2 lg:col-span-3">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
//                   <textarea name="address" value={formData.address} onChange={handleChange} rows="2" className={`w-full ${customClassName(formData.address)}`} placeholder="Complete address" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
//                   <input type="text" name="locality" value={formData.locality} onChange={handleChange} className={`w-full ${customClassName(formData.locality)}`} placeholder="Area/Locality" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
//                   <SelectDropdown options={countries} value={formData.country} onChange={handleCountryChange} placeholder="Select Country" valueKey="isoCode" labelKey="name" searchable={true} />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
//                   <SelectDropdown options={states} value={formData.state} onChange={handleStateChange} placeholder={formData.country === 'IN' ? "Select State" : "Enter State"} valueKey="id" labelKey="label" disabled={!formData.country} searchable={true} />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
//                   <SelectDropdown options={districts} value={formData.district} onChange={(v) => setFormData(prev => ({ ...prev, district: v }))} placeholder={formData.state ? "Select District" : "Select State First"} valueKey="id" labelKey="label" disabled={!formData.state || formData.country !== 'IN'} searchable={true} />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
//                   <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength="6" className={`w-full ${customClassName(formData.pincode)}`} placeholder="6 digits" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
//                   <input type="text" name="latitude" value={formData.latitude} onChange={handleChange} className={`w-full ${customClassName(formData.latitude)}`} placeholder="e.g., 28.6139" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
//                   <input type="text" name="longitude" value={formData.longitude} onChange={handleChange} className={`w-full ${customClassName(formData.longitude)}`} placeholder="e.g., 77.2090" />
//                 </div>
//               </div>

//               {/* Location Images Upload */}
//               <div className={`border-2 border-dotted ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} p-4 rounded mt-6 transition-colors`}
//                 onDragOver={handleDragOver}
//                 onDragLeave={handleDragLeave}
//                 onDrop={handleDrop}
//               >
//                 <p className="text-center font-medium">Location Pictures (Optional)</p>
//                 <p className="text-center text-xs text-gray-500 mb-2">Upload or capture location images (max 2MB per image)</p>

//                 {locationImages.length > 0 && (
//                   <div className="flex flex-wrap gap-2 mb-4">
//                     {locationImages.map((file, index) => (
//                       <div key={index} className="relative group">
//                         <img
//                           src={URL.createObjectURL(file)}
//                           alt={`Location ${index + 1}`}
//                           className="w-20 h-20 object-cover rounded border hover:border-blue-500 transition-colors"
//                         />
//                         <button
//                           type="button"
//                           onClick={() => setLocationImages((prev) => prev.filter((_, i) => i !== index))}
//                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
//                         >
//                           <X className="w-3 h-3" />
//                         </button>
//                         <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center rounded-b">
//                           {(file.size / 1024 / 1024).toFixed(1)}MB
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}

//                 {existingImages.length > 0 && (
//                   <div className="flex flex-wrap gap-2 mb-4">
//                     {existingImages.map((imageUrl, index) => (
//                       <div key={`existing-${index}`} className="relative group">
//                         <img
//                           src={imageUrl}
//                           alt={`Existing Location ${index + 1}`}
//                           className="w-20 h-20 object-cover rounded border border-gray-300 hover:border-blue-500 transition-colors"
//                         />
//                         <button
//                           type="button"
//                           onClick={() => setExistingImages((prev) => prev.filter((_, i) => i !== index))}
//                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
//                         >
//                           <X className="w-3 h-3" />
//                         </button>
//                       </div>
//                     ))}
//                   </div>
//                 )}

//                 <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
//                   <label className="cursor-pointer">
//                     <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
//                     <div className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-4 rounded-lg transition-colors">
//                       <Plus className="w-5 h-5" /> <span>Upload Images</span>
//                     </div>
//                   </label>
//                   <label className="cursor-pointer">
//                     <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
//                     <div className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors">
//                       <Camera className="w-5 h-5" /> <span>Use Camera</span>
//                     </div>
//                   </label>
//                 </div>

//                 <div className="text-center text-sm text-gray-600 mt-3 space-y-1">
//                   {(locationImages.length > 0 || existingImages.length > 0) && (
//                     <p>Total images: {locationImages.length + existingImages.length}</p>
//                   )}
//                   <p className="text-xs text-gray-500">Images larger than 2MB will be automatically compressed</p>
//                 </div>
//               </div>
//             </div>

//             {/* 6. Business Details */}
//             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">💼</span> Business Details</h2>
//               {!isEducationDivision && (<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-2">

//                 {/* Product Categories */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Product Categories {!formData.programId && <span className="text-red-500">*</span>}
//                   </label>

//                   <SelectDropdown
//                     options={productCategoryOptions}
//                     value={formData.productCategory}
//                     onChange={handleProductCategoryChange}
//                     placeholder="Select Product Categories"
//                     valueKey="id"
//                     labelKey="label"
//                     multiple={true}
//                     searchable={true}
//                     error={errors.productCategory}
//                   />

//                   {errors.productCategory && (
//                     <p className="text-red-500 text-xs mt-1">{errors.productCategory}</p>
//                   )}

//                   {showOtherProductCategory && (
//                     <div className="mt-3 w-full">
//                       <label className="block text-sm font-medium text-gray-700 mb-1">
//                         Specify Product Category <span className="text-red-500">*</span>
//                       </label>
//                       <input
//                         type="text"
//                         value={otherProductCategory}
//                         onChange={(e) => setOtherProductCategory(e.target.value)}
//                         className={`w-full ${customClassName(otherProductCategory)} ${errors.otherProductCategory ? 'border-red-500' : ''}`}
//                         placeholder="Please specify the product category"
//                       />
//                       {errors.otherProductCategory && (
//                         <p className="text-red-500 text-xs mt-1">{errors.otherProductCategory}</p>
//                       )}
//                     </div>
//                   )}
//                 </div>

//                 {/* Requirements */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Requirements {!formData.programId && <span className="text-red-500">*</span>}
//                   </label>

//                   <SelectDropdown
//                     options={filteredRequirements}
//                     value={formData.requirement}
//                     onChange={handleRequirementChange}
//                     placeholder="Select Requirements"
//                     valueKey="id"
//                     labelKey="label"
//                     multiple={true}
//                     searchable={true}
//                     disabled={!formData.productCategory || formData.productCategory.length === 0}
//                     error={errors.requirement}
//                   />

//                   {errors.requirement && (
//                     <p className="text-red-500 text-xs mt-1">{errors.requirement}</p>
//                   )}
//                 </div>

//                 {/* Other Requirements */}
//                 {showOtherRequirement && (
//                   <div className="space-y-3">
//                     {formData.requirement
//                       .filter(req => req.includes('Others - Mention that product'))
//                       .map((otherReq, index) => (
//                         <div key={index}>
//                           <label className="block text-sm font-medium text-gray-700 mb-1">
//                             Specify Other Requirement for {otherReq.split(' - ')[0]} <span className="text-red-500">*</span>
//                           </label>

//                           <input
//                             type="text"
//                             value={otherRequirements[otherReq] || ''}
//                             onChange={(e) => handleOtherRequirementChange(otherReq, e.target.value)}
//                             className={`w-full ${customClassName(otherRequirements[otherReq])} ${errors.otherRequirements ? 'border-red-500' : ''}`}
//                             placeholder="Please specify the requirement"
//                           />
//                         </div>
//                       ))}

//                     {errors.otherRequirements && (
//                       <p className="text-red-500 text-xs mt-1">{errors.otherRequirements}</p>
//                     )}
//                   </div>
//                 )}

//               </div>)}
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">

//                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:col-span-2 overflow-visible">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
//                     <SelectDropdown options={currencyOptions} value={formData.currency} onChange={(value) => setFormData(prev => ({ ...prev, currency: value }))} valueKey="id" labelKey="label" />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Value <span className="text-red-500">*</span></label>
//                     <input type="number" name="estimatedValue" value={formData.estimatedValue} onChange={handleChange} className={`w-full ${customClassName(formData.estimatedValue)} ${errors.estimatedValue ? 'border-red-500' : ''}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
//                     {errors.estimatedValue && <p className="text-red-500 text-xs mt-1">{errors.estimatedValue}</p>}
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Expected Value</label>
//                     <input type="number" name="expectedValue" value={formData.expectedValue} onChange={handleChange} className={`w-full ${customClassName(formData.expectedValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">Recurring Value</label>
//                     <input type="number" name="recurringValue" value={formData.recurringValue} onChange={handleChange} className={`w-full ${customClassName(formData.recurringValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* 7. Sales Pipeline */}
//             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📊</span> Sales Pipeline</h2>
//               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Stage <span className="text-red-500">*</span></label>
//                   <SelectDropdown options={salesStages} value={formData.stageId} onChange={(v) => setFormData(prev => ({ ...prev, stageId: v }))} placeholder="Select Stage" searchable valueKey="_id" labelKey="stageName" />
//                   {errors.stageId && <p className="text-red-500 text-xs mt-1">{errors.stageId}</p>}
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Expected Closing Date</label>
//                   <CustomDatePicker value={formData.expectedClosingDate} onChange={(date) => setFormData(prev => ({ ...prev, expectedClosingDate: date }))} placeholder="Select date" />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
//                   <SelectDropdown options={paymentStatusOptions} value={formData.paymentStatus} onChange={(v) => setFormData(prev => ({ ...prev, paymentStatus: v }))} valueKey="id" labelKey="label" />
//                 </div>
//               </div>
//             </div>

//             {/* 8. Visit Remarks */}
//             {!isEditMode && (
//               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
//                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📝</span> Visit Remarks</h2>
//                 <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="4" className={`w-full ${customClassName(formData.remarks)}`} placeholder="Any important notes, observations, or action items from this visit..." />
//               </div>
//             )}

//             {/* 9. Visit History Table in Edit mode */}
//             {isEditMode && clientData?.visitHistory && clientData.visitHistory.length > 0 && (
//               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
//                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                   <span className="text-2xl mr-2">📜</span> Visit History
//                 </h2>
//                 <div className="overflow-x-auto custom-scrollbar">
//                   <table className="min-w-full divide-y divide-gray-200">
//                     <thead className="bg-gray-100">
//                       <tr>
//                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visit #</th>
//                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
//                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
//                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
//                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
//                       </tr>
//                     </thead>
//                     <tbody className="bg-white divide-y divide-gray-200">
//                       {clientData.visitHistory.map((visit, index) => (
//                         <tr key={index}>
//                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitNumber}</td>
//                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(visit.visitDate).toLocaleDateString()}</td>
//                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitTime}</td>
//                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
//                             <span className="capitalize block font-medium">{visit.visitType}</span>
//                             {clientData?.sourceType === 'expo' && (
//                               <div className="mt-1 text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 inline-block">
//                                 <span className="font-semibold text-[9px] uppercase">Source: Expo</span><br/>
//                                 {clientData.expoPlace} ({clientData.expoYear})
//                               </div>
//                             )}
//                           </td>
//                           <td className="px-6 py-4 text-sm text-gray-900">{visit.remarks || '-'}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               </div>
//             )}

//             {Object.keys(errors).length > 0 && (
//               <div className="bg-red-50 border border-red-200 rounded-lg p-4">
//                 <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
//                 <ul className="list-disc list-inside text-sm text-red-700">
//                   {Object.entries(errors).map(([field, error]) => <li key={field}>{error}</li>)}
//                 </ul>
//               </div>
//             )}

//             {/* 10. Submit Buttons */}
//             <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t">
//               <button type="button" onClick={() => navigate(-1)} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Cancel</button>
//               <div className="flex flex-col sm:flex-row gap-4">
//                 {!isEditMode && (
//                   <button type="button" onClick={resetForm} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Reset Form</button>
//                 )}
//                 <button type="submit" disabled={loading} className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
//                   {loading ? (
//                     <>
//                       <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                       </svg>
//                       {isEditMode ? 'Updating...' : (isConvertMode ? 'Converting...' : 'Submitting...')}
//                     </>
//                   ) : (
//                     isEditMode ? 'Update Visit' : (isConvertMode ? 'Convert & Submit' : 'Submit Visit')
//                   )}
//                 </button>
//               </div>
//             </div>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ClientVisitForm;

// // import React, { useState, useEffect, useCallback } from 'react';
// // import { useParams, useNavigate } from 'react-router-dom';
// // import { industryData, productCategories, visitTypeOptions, sourceTypeOptions, currencyOptions, paymentStatusOptions } from './IndustryData';
// // import AllStatesAndDistricts from '../../helpers/AllStatesAndDistricts';
// // import { Country } from 'country-state-city';
// // import SelectDropdown from '../../customStyles/SelectDropdown';
// // import CustomDatePicker from '../../customStyles/CustomDatePicker';
// // import SummaryApi from '../../common/SummaryApi';
// // import api from '../../common/apiClient';
// // import { toast } from 'react-toastify';
// // import imageCompression from 'browser-image-compression';
// // import { Camera, Plus, X } from 'lucide-react';
// // import { useSelector } from 'react-redux';

// // const ClientVisitForm = () => {
// //   const user = useSelector((state) => state?.user?.user);
// //   const { id, leadId } = useParams();
// //   const navigate = useNavigate();
// //   const isEditMode = !!id;
// //   const isConvertMode = !!leadId;
  
// //   const [salesStages, setSalesStages] = useState([]);
// //   const [loadingVisit, setLoadingVisit] = useState(false);

// //   // Dropdown options states
// //   const [ourProductList, setOurProductList] = useState([]);
// //   const [ourBusinessCategoryList, setOurBusinessCategoryList] = useState([]);

// //   const [showOtherProductCategory, setShowOtherProductCategory] = useState(false);
// //   const [otherProductCategory, setOtherProductCategory] = useState('');
// //   const [showOtherRequirement, setShowOtherRequirement] = useState(false);
// //   const [otherRequirements, setOtherRequirements] = useState({});
// //   const [locationImages, setLocationImages] = useState([]);
// //   const [existingImages, setExistingImages] = useState([]);
// //   const [isDragActive, setIsDragActive] = useState(false);
// //   const [locationLoading, setLocationLoading] = useState(false);
// //   const [executiveList, setExecutiveList] = useState([]);

// //   const [formData, setFormData] = useState({
// //     visitType: '',
// //     sourceType: '',
// //     expoYear: '',
// //     expoPlace: '',
// //     visitDate: new Date(),
// //     visitTime: new Date().toTimeString().slice(0, 5),
// //     nextFollowUpDate: null,
// //     nextFollowUpTime: '',

// //     divisionId: '',
// //     divisionName: '',

// //     clientName: '',
// //     industryType: '',
// //     industrySubType: '',

// //     ourProductId: '',
// //     ourProductBusinessCategoryId: '',

// //     contactName: '',
// //     contactDesignation: '',
// //     contactEmail: '',
// //     contactPhone: '',
// //     contactLandline: '',

// //     address: '',
// //     locality: '',
// //     district: '',
// //     state: '',
// //     country: 'IN',
// //     pincode: '',
// //     latitude: '',
// //     longitude: '',

// //     productCategory: [],
// //     requirement: [],
// //     estimatedValue: 0,
// //     expectedValue: 0,
// //     recurringValue: 0,
// //     currency: 'INR',

// //     stageId: '',
// //     expectedClosingDate: null,
// //     paymentStatus: 'na',

// //     remarks: '',
// //     ownerId: user?._id || ''
// //   });

// //   // Checks if the selected division name is CUSO (case-insensitive)
// //   const isCusoDivision = Boolean(formData.divisionName?.toLowerCase().includes('cuso'));

// //   const [errors, setErrors] = useState({});
// //   const [loading, setLoading] = useState(false);
// //   const [divisions, setDivisions] = useState([]);
// //   const [countries, setCountries] = useState([]);
// //   const [states, setStates] = useState([]);
// //   const [districts, setDistricts] = useState([]);
// //   const [filteredSubIndustries, setFilteredSubIndustries] = useState([]);
// //   const [filteredRequirements, setFilteredRequirements] = useState([]);
// //   const [showOtherIndustry, setShowOtherIndustry] = useState(false);
// //   const [otherIndustry, setOtherIndustry] = useState('');
// //   const [submitSuccess, setSubmitSuccess] = useState(false);
// //   const [submitMessage, setSubmitMessage] = useState('');
// //   const [clientData, setClientData] = useState(null);

// //   const currentYear = new Date().getFullYear();
// //   const startYear = 2025;
// //   const expoYearOptions = [];

// //   for (let y = currentYear; y >= startYear; y--) {
// //     expoYearOptions.push({ id: y.toString(), label: y.toString() });
// //   }  

// //   // Sync divisionName automatically if divisionId is available but divisionName is missing
// //   useEffect(() => {
// //     if (formData.divisionId && !formData.divisionName && divisions.length > 0) {
// //       const matched = divisions.find(d => d.id === formData.divisionId);
// //       if (matched) {
// //         setFormData(prev => ({ ...prev, divisionName: matched.label }));
// //       }
// //     }
// //   }, [formData.divisionId, formData.divisionName, divisions]);

// //   // Fetch executives list for owner assignment
// //   const fetchExecutives = useCallback(async () => {
// //     try {
// //       const response = await api.get(SummaryApi.executiveUsers.url);
// //       const resData = response.data;
      
// //       let execs = [];
// //       if (Array.isArray(resData?.data)) {
// //         execs = resData.data;
// //       } else if (Array.isArray(resData?.users)) {
// //         execs = resData.users;
// //       } else if (Array.isArray(resData?.executives)) {
// //         execs = resData.executives;
// //       } else if (Array.isArray(resData)) {
// //         execs = resData;
// //       }

// //       const formattedList = execs.map((exec) => {
// //         const rawId = exec._id || exec.id || exec.userId || '';
// //         const nameStr = (
// //           exec.fullName
// //             ? `${exec.fullName} ${exec.lastName || ''}`.trim()
// //             : (exec.name || exec.clientName || exec.userName || exec.email || '')
// //         ) || 'Executive';

// //         return {
// //           id: String(rawId),
// //           _id: String(rawId),
// //           value: String(rawId),
// //           label: nameStr,
// //           name: nameStr
// //         };
// //       });

// //       setExecutiveList(formattedList);
// //     } catch (error) {
// //       console.error("Error fetching executives:", error);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchExecutives();
// //   }, [fetchExecutives]);

// //   // 1. Fetch Our ERP Products
// //   const fetchOurProducts = useCallback(async () => {
// //     try {
// //       const response = await api.get(SummaryApi.getAllProducts.url, { params: { isActive: "true" }});

// //       if (response.data?.success) {
// //         const formatted = (response.data.data || []).map(p => ({
// //           id: p._id,
// //           label: p.name,
// //         }));
// //         setOurProductList(formatted);
// //       }
// //     } catch (error) {
// //       console.error('Error fetching our products:', error);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchOurProducts();
// //   }, [fetchOurProducts]);

// //   // 2. Fetch Business Categories based on Selected Product
// //   const fetchBusinessCategories = useCallback(async (productId) => {
// //     if (!productId) {
// //       setOurBusinessCategoryList([]);
// //       return;
// //     }
// //     try {
// //       const response = await api.get(SummaryApi.getAllBusinessCategories.url, { params: { productId, isActive: "true" }});

// //       if (response.data?.success) {
// //         const formatted = (response.data.data || []).map(c => ({
// //           id: c._id,
// //           label: c.name,
// //         }));
// //         setOurBusinessCategoryList(formatted);
// //       }
// //     } catch (error) {
// //       console.error('Error fetching business categories:', error);
// //     }
// //   }, []);

// //   // Handle Our Product change & clear previous child category
// //   const handleOurProductChange = (productId) => {
// //     setFormData(prev => ({
// //       ...prev,
// //       ourProductId: productId,
// //       ourProductBusinessCategoryId: '',
// //     }));

// //     if (errors.ourProductId) {
// //       setErrors(prev => ({ ...prev, ourProductId: '' }));
// //     }

// //     fetchBusinessCategories(productId);
// //   };

// //   // Handler for Assigned Executive / Owner dropdown change
// //   const handleOwnerChange = (selectedVal) => {
// //     let idValue = selectedVal;
// //     if (selectedVal && typeof selectedVal === 'object') {
// //       idValue = selectedVal.id || selectedVal._id || selectedVal.value;
// //     }

// //     setFormData(prev => ({
// //       ...prev,
// //       ownerId: String(idValue || '')
// //     }));

// //     if (errors.ownerId) {
// //       setErrors(prev => ({ ...prev, ownerId: '' }));
// //     }
// //   };

// //   const compressImage = async (file) => {
// //     const options = {
// //       maxSizeMB: 2,
// //       maxWidthOrHeight: 1920,
// //       useWebWorker: true,
// //     };

// //     try {
// //       const compressedFile = await imageCompression(file, options);
// //       return compressedFile;
// //     } catch (error) {
// //       console.error('Error compressing image:', error);
// //       return file;
// //     }
// //   };

// //   const handleFileChange = async (e) => {
// //     const files = Array.from(e.target.files);

// //     if (locationImages.length + files.length > 10) {
// //       toast.error('Maximum 10 images allowed');
// //       return;
// //     }

// //     const compressedFiles = await Promise.all(
// //       files.map(async (file) => {
// //         if (file.size > 2 * 1024 * 1024) {
// //           const compressed = await compressImage(file);
// //           return compressed;
// //         }
// //         return file;
// //       })
// //     );

// //     setLocationImages(prev => [...prev, ...compressedFiles]);
// //   };

// //   const handleDragOver = (e) => {
// //     e.preventDefault();
// //     setIsDragActive(true);
// //   };

// //   const handleDragLeave = (e) => {
// //     e.preventDefault();
// //     setIsDragActive(false);
// //   };

// //   const handleDrop = async (e) => {
// //     e.preventDefault();
// //     setIsDragActive(false);

// //     const files = Array.from(e.dataTransfer.files).filter(file =>
// //       file.type.startsWith('image/')
// //     );

// //     if (locationImages.length + files.length > 10) {
// //       toast.error('Maximum 10 images allowed');
// //       return;
// //     }

// //     const compressedFiles = await Promise.all(
// //       files.map(async (file) => {
// //         if (file.size > 2 * 1024 * 1024) {
// //           const compressed = await compressImage(file);
// //           return compressed;
// //         }
// //         return file;
// //       })
// //     );

// //     setLocationImages(prev => [...prev, ...compressedFiles]);
// //   };

// //   const reverseGeocode = async (latitude, longitude) => {
// //     try {
// //       const response = await fetch(
// //         `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
// //         {
// //           headers: {
// //             'Accept': 'application/json',
// //             'User-Agent': 'ClientVisitForm/1.0'
// //           }
// //         }
// //       );

// //       if (!response.ok) {
// //         throw new Error('Failed to fetch address');
// //       }

// //       const data = await response.json();
// //       return data;
// //     } catch (error) {
// //       console.error('Reverse geocoding error:', error);

// //       try {
// //         const fallbackResponse = await fetch(
// //           `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
// //         );

// //         if (fallbackResponse.ok) {
// //           const fallbackData = await fallbackResponse.json();

// //           let district = '';
// //           if (fallbackData.localityInfo && fallbackData.localityInfo.administrative) {
// //             const districtInfo = fallbackData.localityInfo.administrative.find(
// //               admin => admin.description?.toLowerCase().includes('district') || admin.adminLevel === 5
// //             );
// //             district = districtInfo ? districtInfo.name.replace(' district', '') : '';
// //           }

// //           return {
// //             display_name: fallbackData.locality || '',
// //             address: {
// //               road: fallbackData.street || '',
// //               neighbourhood: fallbackData.locality || '',
// //               suburb: fallbackData.suburb || '',
// //               city: fallbackData.city || '',
// //               state_district: district,
// //               state: fallbackData.principalSubdivision || '',
// //               country: fallbackData.countryName || '',
// //               country_code: fallbackData.countryCode?.toLowerCase() || '',
// //               postcode: fallbackData.postcode || ''
// //             }
// //           };
// //         }
// //       } catch (fallbackError) {
// //         console.error('Fallback geocoding also failed:', fallbackError);
// //       }

// //       return null;
// //     }
// //   };

// //   const setDistrictFromGeocode = (addressData, matchedState) => {
// //     let apiDistrictName = addressData.state_district || addressData.county || addressData.city || '';

// //     apiDistrictName = apiDistrictName
// //       .replace(/ district$/i, '')
// //       .replace(/ District$/i, '')
// //       .replace(/ Dist\.?$/i, '')
// //       .replace(/ Zilla$/i, '')
// //       .replace(/ Jilla$/i, '')
// //       .trim();

// //     const stateDistricts = AllStatesAndDistricts[matchedState] || [];
// //     let matchedDistrict = findClosestMatch(apiDistrictName, stateDistricts);

// //     if (!matchedDistrict && apiDistrictName) {
// //       if (addressData.city && addressData.city !== apiDistrictName) {
// //         matchedDistrict = findClosestMatch(addressData.city, stateDistricts);
// //       }
// //       if (!matchedDistrict && addressData.locality) {
// //         matchedDistrict = findClosestMatch(addressData.locality, stateDistricts);
// //       }
// //     }

// //     if (matchedDistrict) {
// //       setFormData(prev => ({ ...prev, district: matchedDistrict }));
// //     } else {
// //       setFormData(prev => ({ ...prev, district: '' }));
// //     }
// //   };

// //   const findClosestMatch = (searchTerm, list) => {
// //     if (!searchTerm || !list || list.length === 0) return null;

// //     const normalizedSearch = searchTerm.toLowerCase().trim();
// //     const exactMatch = list.find(item => item.toLowerCase().trim() === normalizedSearch);
// //     if (exactMatch) return exactMatch;

// //     const searchWithoutCommonWords = normalizedSearch
// //       .replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '')
// //       .trim();

// //     const matchWithoutCommonWords = list.find(item =>
// //       item.toLowerCase().replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '').trim() === searchWithoutCommonWords
// //     );
// //     if (matchWithoutCommonWords) return matchWithoutCommonWords;

// //     const partialMatch = list.find(item => {
// //       const normalizedItem = item.toLowerCase();
// //       return normalizedItem.includes(normalizedSearch) ||
// //         normalizedSearch.includes(normalizedItem) ||
// //         normalizedItem.includes(searchWithoutCommonWords) ||
// //         searchWithoutCommonWords.includes(normalizedItem);
// //     });
// //     if (partialMatch) return partialMatch;

// //     const firstWord = normalizedSearch.split(' ')[0];
// //     if (firstWord.length > 3) {
// //       const firstWordMatch = list.find(item => item.toLowerCase().startsWith(firstWord));
// //       if (firstWordMatch) return firstWordMatch;
// //     }

// //     return null;
// //   };

// //   const handleCountryChange = (countryCode) => {
// //     setFormData(prev => ({
// //       ...prev,
// //       country: countryCode,
// //       state: '',
// //       district: ''
// //     }));

// //     if (countryCode === 'IN') {
// //       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
// //         id: state,
// //         label: state
// //       }));
// //       setStates(indianStates);
// //     } else {
// //       setStates([]);
// //     }
// //   };

// //   const handleStateChange = (stateName) => {
// //     setFormData(prev => ({
// //       ...prev,
// //       state: stateName,
// //       district: ''
// //     }));
// //   };

// //   const getCurrentLocation = () => {
// //     if (navigator.geolocation) {
// //       setLocationLoading(true);
// //       toast.info('Getting your location...');

// //       navigator.geolocation.getCurrentPosition(
// //         async (position) => {
// //           const lat = position.coords.latitude;
// //           const lon = position.coords.longitude;

// //           setFormData(prev => ({
// //             ...prev,
// //             latitude: lat.toFixed(6),
// //             longitude: lon.toFixed(6)
// //           }));

// //           const addressData = await reverseGeocode(lat, lon);

// //           if (addressData) {
// //             if (addressData.address) {
// //               const addr = addressData.address;

// //               const addressParts = [];
// //               if (addr.house_number) addressParts.push(addr.house_number);
// //               if (addr.road || addr.street) addressParts.push(addr.road || addr.street);
// //               if (addr.neighbourhood) addressParts.push(addr.neighbourhood);
// //               if (addr.suburb) addressParts.push(addr.suburb);
// //               if (addr.city && addr.city !== addr.neighbourhood && addr.city !== addr.suburb) {
// //                 addressParts.push(addr.city);
// //               }

// //               const fullAddress = addressParts.length > 0
// //                 ? addressParts.filter(Boolean).join(', ')
// //                 : addressData.display_name?.split(',').slice(0, 3).join(',') || '';

// //               const apiStateName = addr.state || addr.principalSubdivision || '';
// //               const matchedState = findClosestMatch(apiStateName, Object.keys(AllStatesAndDistricts));

// //               if (matchedState) {
// //                 setFormData(prev => ({
// //                   ...prev,
// //                   address: fullAddress,
// //                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
// //                   pincode: addr.postcode || prev.pincode
// //                 }));

// //                 const countryCode = addr.country_code ? addr.country_code.toUpperCase() : 'IN';

// //                 if (countryCode !== formData.country) {
// //                   handleCountryChange(countryCode);
// //                   setTimeout(() => {
// //                     handleStateChange(matchedState);
// //                     setTimeout(() => {
// //                       setDistrictFromGeocode(addr, matchedState);
// //                       setLocationLoading(false);
// //                     }, 300);
// //                   }, 200);
// //                 } else {
// //                   handleStateChange(matchedState);
// //                   setTimeout(() => {
// //                     setDistrictFromGeocode(addr, matchedState);
// //                     setLocationLoading(false);
// //                   }, 300);
// //                 }

// //                 toast.success('Location and address captured successfully!');
// //               } else {
// //                 setFormData(prev => ({
// //                   ...prev,
// //                   address: fullAddress,
// //                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
// //                   state: apiStateName,
// //                   pincode: addr.postcode || prev.pincode
// //                 }));

// //                 setLocationLoading(false);
// //                 toast.warning('Location captured, but state/district may need manual adjustment');
// //               }
// //             }
// //           } else {
// //             setLocationLoading(false);
// //             toast.warning('Location captured, but could not fetch address details. Please fill manually.');
// //           }
// //         },
// //         (error) => {
// //           console.error('Geolocation error:', error);
// //           setLocationLoading(false);

// //           let errorMessage = 'Unable to get location. ';
// //           switch (error.code) {
// //             case error.PERMISSION_DENIED:
// //               errorMessage += 'Please allow location access in your browser settings.';
// //               break;
// //             case error.POSITION_UNAVAILABLE:
// //               errorMessage += 'Location information is unavailable.';
// //               break;
// //             case error.TIMEOUT:
// //               errorMessage += 'Request timed out. Please try again.';
// //               break;
// //             default:
// //               errorMessage += 'An unknown error occurred.';
// //           }
// //           toast.error(errorMessage);
// //         },
// //         { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
// //       );
// //     } else {
// //       toast.error('Geolocation is not supported by your browser');
// //     }
// //   };

// //   const fetchOpenLeadDetails = useCallback(async () => {
// //     setLoadingVisit(true);
// //     try {
// //       let lead = null;

// //       if (SummaryApi.getOneExpoLead?.url) {
// //         try {
// //           const expoRes = await api.get(SummaryApi.getOneExpoLead.url(leadId));
// //           if (expoRes.data?.success) {
// //             lead = expoRes.data.data;
// //           }
// //         } catch {
// //           console.log('Expo Lead fetch fallback...');
// //         }
// //       }

// //       if (!lead) {
// //         try {
// //           const response = await api.get(SummaryApi.getOpenLeadById.url(leadId));
// //           if (response.data?.success) {
// //             lead = response.data.data;
// //           }
// //         } catch (err) {
// //           console.error('Lead lookup error:', err);
// //         }
// //       }

// //       if (lead) {
// //         let matchedIndustryCategory = '';
// //         let rawIndustryText = lead.industry || '';
// //         if (!rawIndustryText && lead.division) {
// //           rawIndustryText = Array.isArray(lead.division)
// //             ? lead.division.join(' ')
// //             : String(lead.division);
// //         }

// //         if (rawIndustryText) {
// //           const targetText = String(rawIndustryText).toLowerCase().trim();
// //           const foundIndustry = industryData.find(ind => {
// //             const catLower = ind.industryCategory.toLowerCase().trim();
// //             return catLower === targetText || catLower.includes(targetText) || targetText.includes(catLower);
// //           });
// //           if (foundIndustry) {
// //             matchedIndustryCategory = foundIndustry.industryCategory;
// //           }
// //         }

// //         let matchedProductCategories = [];
// //         let matchedRequirements = [];
// //         const rawRequirementText = lead.requirement || '';

// //         if (rawRequirementText) {
// //           productCategories.forEach(cat => {
// //             if (rawRequirementText.toLowerCase().includes(cat.productCategory.toLowerCase())) {
// //               matchedProductCategories.push(cat.productCategory);
// //             }
// //             cat.subCategories.forEach(sub => {
// //               if (rawRequirementText.toLowerCase().includes(sub.toLowerCase())) {
// //                 if (!matchedProductCategories.includes(cat.productCategory)) {
// //                   matchedProductCategories.push(cat.productCategory);
// //                 }
// //                 matchedRequirements.push(sub);
// //               }
// //             });
// //           });
// //         }

// //         const parsedDivisionId = Array.isArray(lead.division)
// //           ? lead.division[0] || ''
// //           : lead.division || '';

// //         const parsedOwnerId = lead.ownerId
// //           ? String(typeof lead.ownerId === 'object' ? (lead.ownerId._id || lead.ownerId.id) : lead.ownerId)
// //           : '';

// //         const leadProductId = lead.ourProductId?._id || lead.ourProductId || '';
// //         const leadCategoryId = lead.ourProductBusinessCategoryId?._id || lead.ourProductBusinessCategoryId || '';

// //         if (leadProductId) {
// //           fetchBusinessCategories(leadProductId);
// //         }

// //         setFormData(prev => ({
// //           ...prev,
// //           clientName: lead.companyOrSchool || lead.fullName || '',
// //           industryType: matchedIndustryCategory || "Education & Training",
// //           contactName: lead.fullName || '',
// //           contactDesignation: lead.designation || '',
// //           contactEmail: lead.email || '',
// //           contactPhone: lead.mobile || '',
// //           address: lead.address || lead.city || '',
// //           locality: lead.locality || lead.city || '',
// //           divisionId: parsedDivisionId || prev.divisionId,
// //           sourceType: lead.source === 'expo' || lead.expoId ? 'expo' : (lead.source || 'expo'),
// //           expoYear: lead.expoId?.expoYear ? String(lead.expoId.expoYear) : new Date().getFullYear().toString(),
// //           expoPlace: lead.expoId?.expoName || lead.source || '',
// //           productCategory: matchedProductCategories,
// //           requirement: matchedRequirements,
// //           ourProductId: leadProductId,
// //           ourProductBusinessCategoryId: leadCategoryId,
// //           remarks: lead.requirement || lead.remarks || '',
// //           ownerId: parsedOwnerId || prev.ownerId
// //         }));

// //         toast.success(`Loaded Lead: ${lead.fullName}`);
// //       }
// //     } catch (err) {
// //       console.error(err);
// //       toast.error('Failed to load lead information');
// //     } finally {
// //       setLoadingVisit(false);
// //     }
// //   }, [leadId, fetchBusinessCategories]);

// //   const fetchVisitData = useCallback(async () => {
// //     setLoadingVisit(true);
// //     try {
// //       const response = await api.get(SummaryApi.getOneClientVisit.url(id));

// //       if (response.data.success) {
// //         const visit = response.data.data;
// //         setClientData(visit);

// //         if (visit.locationImages && visit.locationImages.length > 0) {
// //           setExistingImages(visit.locationImages);
// //         }

// //         let industrySubType = visit.industrySubType || '';
// //         if (industrySubType.startsWith('Others - ')) {
// //           setShowOtherIndustry(true);
// //           setOtherIndustry(industrySubType.substring(9));
// //           industrySubType = 'Others';
// //         }
        
// //         let productCats = [...(visit.productCategory || [])];
// //         let otherCategoryText = "";
        
// //         const otherCategoryIndex = productCats.findIndex(cat => cat.startsWith("Others - "));
// //         if (otherCategoryIndex !== -1) {
// //           otherCategoryText = productCats[otherCategoryIndex].substring(9);
// //           productCats[otherCategoryIndex] = "Others - Mention that category";
// //           setOtherProductCategory(otherCategoryText);
// //           setShowOtherProductCategory(true);
// //         }

// //         const visitProductId = visit.ourProductId?._id || visit.ourProductId || '';
// //         const visitCategoryId = visit.ourProductBusinessCategoryId?._id || visit.ourProductBusinessCategoryId || '';

// //         if (visitProductId) {
// //           fetchBusinessCategories(visitProductId);
// //         }

// //         let requirements = [...(visit.requirement || [])];
// //         const otherReqs = {};
        
// //         requirements = requirements.map(req => {
// //           if (req.startsWith('Others - ')) {
// //             const value = req.substring(9);
// //             const newKey = "Others - Mention that product";
// //             otherReqs[newKey] = value;
// //             return newKey;
// //           }
// //           return req;
// //         });
        
// //         setOtherRequirements(otherReqs);
// //         setShowOtherRequirement(Object.keys(otherReqs).length > 0);

// //         const parsedOwnerId = visit.ownerId
// //           ? String(typeof visit.ownerId === 'object' ? (visit.ownerId._id || visit.ownerId.id) : visit.ownerId)
// //           : '';

// //         setFormData({
// //           visitType: visit.visitHistory?.[0]?.visitType || '',
// //           sourceType: visit.sourceType || '',
// //           expoYear: visit?.expoYear || '',
// //           expoPlace: visit?.expoPlace || '',
// //           visitDate: visit.visitHistory?.[0]?.visitDate ? new Date(visit.visitHistory[0].visitDate) : null,
// //           visitTime: visit.visitHistory?.[0]?.visitTime || new Date().toTimeString().slice(0, 5),
// //           nextFollowUpDate: visit.nextFollowUpDate ? new Date(visit.nextFollowUpDate) : null,
// //           nextFollowUpTime: visit.nextFollowUpTime || '',
// //           divisionId: visit.divisionId?._id || '',
// //           divisionName: visit.divisionId?.name || '',
// //           clientName: visit.clientName || '',
// //           industryType: visit.industryType || '',
// //           industrySubType: industrySubType || '',
// //           ourProductId: visitProductId,
// //           ourProductBusinessCategoryId: visitCategoryId,
// //           contactName: visit.contactName || '',
// //           contactDesignation: visit.contactDesignation || '',
// //           contactEmail: visit.contactEmail || '',
// //           contactPhone: visit.contactPhone || '',
// //           contactLandline: visit.contactLandline || '',
// //           address: visit.address || '',
// //           locality: visit.locality || '',
// //           district: visit.district || '',
// //           state: visit.state || '',
// //           country: visit.country || 'IN',
// //           pincode: visit.pincode || '',
// //           latitude: visit.latitude || '',
// //           longitude: visit.longitude || '',
// //           productCategory: productCats || [],
// //           requirement: requirements || [],
// //           estimatedValue: visit.estimatedValue || 0,
// //           expectedValue: visit.expectedValue || 0,
// //           recurringValue: visit.recurringValue || 0,
// //           currency: visit.currency || 'INR',
// //           stageId: visit.stageId?._id || '',
// //           expectedClosingDate: visit.expectedClosingDate ? new Date(visit.expectedClosingDate) : null,
// //           paymentStatus: visit.paymentStatus || 'na',
// //           remarks: visit.visitHistory?.[0]?.remarks || '',
// //           ownerId: parsedOwnerId,
// //         });
// //       }
// //     } catch (error) {
// //       console.error('Error fetching visit data:', error);
// //       toast.error('Failed to load visit data. Please try again.');
// //       navigate(-1);
// //     } finally {
// //       setLoadingVisit(false);
// //     }
// //   }, [id, navigate, fetchBusinessCategories]);

// //   useEffect(() => {
// //     if (isEditMode && id) {
// //       fetchVisitData();
// //     } else if (isConvertMode && leadId) {
// //       fetchOpenLeadDetails();
// //     }
// //   }, [isEditMode, isConvertMode, id, leadId, fetchVisitData, fetchOpenLeadDetails]);

// //   useEffect(() => {
// //     const allCountries = Country.getAllCountries().map(country => ({
// //       isoCode: country.isoCode,
// //       name: country.name
// //     }));
// //     setCountries(allCountries);
// //   }, []);

// //   useEffect(() => {
// //     if (formData.industryType) {
// //       const industry = industryData.find(ind => ind.industryCategory === formData.industryType);
// //       if (industry) {
// //         let subCategories = industry.subCategories.map(sub => ({
// //           id: sub,
// //           label: sub
// //         }));
        
// //         if (!subCategories.some(sub => sub.id === 'Others')) {
// //           subCategories.push({ id: 'Others', label: 'Others' });
// //         }
        
// //         setFilteredSubIndustries(subCategories);
// //       }
// //     } else {
// //       setFilteredSubIndustries([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, industrySubType: '' }));
// //       }
// //     }
// //   }, [formData.industryType, isEditMode]);

// //   useEffect(() => {
// //     setShowOtherIndustry(formData.industrySubType === 'Others');
// //   }, [formData.industrySubType]);

// //   useEffect(() => {
// //     if (isEditMode && clientData) {
// //       const otherCategoryMatch = clientData.productCategory?.find(cat => cat.includes('Others - '));
// //       if (otherCategoryMatch) {
// //         const categoryMatch = otherCategoryMatch.match(/Others - (.+)/);
// //         if (categoryMatch) {
// //           setOtherProductCategory(categoryMatch[1]);
// //           setShowOtherProductCategory(true);
// //         }
// //       }
// //     }
// //   }, [isEditMode, clientData]);

// //   useEffect(() => {
// //     if (formData.productCategory && formData.productCategory.length > 0) {
// //       const allRequirements = [];
// //       formData.productCategory.forEach(categoryId => {
// //         const category = productCategories.find(cat => cat.productCategory === categoryId);
// //         if (category) {
// //           category.subCategories.forEach(sub => {
// //             if (!allRequirements.find(req => req.id === sub)) {
// //               allRequirements.push({ id: sub, label: sub });
// //             }
// //           });
// //         }
// //       });
// //       setFilteredRequirements(allRequirements);
// //     } else {
// //       setFilteredRequirements([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, requirement: [] }));
// //       }
// //     }
// //   }, [formData.productCategory, isEditMode]);

// //   useEffect(() => {
// //     if (formData.state && AllStatesAndDistricts[formData.state]) {
// //       const stateDistricts = AllStatesAndDistricts[formData.state].map(district => ({
// //         id: district,
// //         label: district
// //       }));
// //       setDistricts(stateDistricts);
// //     } else {
// //       setDistricts([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, district: '' }));
// //       }
// //     }
// //   }, [formData.state, isEditMode]);

// //   const fetchDivisions = useCallback(async () => {
// //     try {
// //       const response = await api.get(SummaryApi.getAllDivisions.url);
// //       const divisionsData = response.data.data || [];
// //       setDivisions(divisionsData.map(div => ({
// //         id: div._id,
// //         label: div.name
// //       })));
// //     } catch (error) {
// //       console.error('Error fetching divisions:', error);
// //       setDivisions([]);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchDivisions();
// //   }, [fetchDivisions]);

// //   const fetchSalesStages = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       const params = {
// //         includeInactive: false,
// //         includeShared: true,
// //       };
// //       const response = await api.get(SummaryApi.getAllStages.url, { params });
// //       if (response.data.success) {
// //         setSalesStages(response.data.data || []);
// //       }
// //     } catch (error) {
// //       console.error('Failed to fetch stages:', error);
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchSalesStages();
// //   }, [fetchSalesStages]);

// //   const handleChange = (e) => {
// //     const { name, value, type } = e.target;
// //     let processedValue = value;

// //     if (type === "number") {
// //       if (value === "0") {
// //         processedValue = "0";
// //       } else {
// //         processedValue = value.replace(/^0+/, '');
// //       }
// //     }

// //     setFormData(prev => ({
// //       ...prev,
// //       [name]: processedValue
// //     }));

// //     if (errors[name]) {
// //       setErrors(prev => ({ ...prev, [name]: '' }));
// //     }
// //   };

// //   const handleProductCategoryChange = (selectedCategories) => {
// //     setFormData(prev => ({ ...prev, productCategory: selectedCategories }));

// //     const hasOthers = selectedCategories.includes("Others - Mention that category");
// //     setShowOtherProductCategory(hasOthers);

// //     if (!hasOthers) {
// //       setOtherProductCategory('');
// //     }

// //     updateAvailableRequirements(selectedCategories);
// //   };

// //   const updateAvailableRequirements = (selectedCategories) => {
// //     if (!selectedCategories || selectedCategories.length === 0) {
// //       setFilteredRequirements([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, requirement: [] }));
// //       }
// //       return;
// //     }

// //     const allRequirements = [];
// //     selectedCategories.forEach(categoryId => {
// //       const category = productCategories.find(cat => cat.productCategory === categoryId);
// //       if (category) {
// //         category.subCategories.forEach(sub => {
// //           if (!allRequirements.find(req => req.id === sub)) {
// //             allRequirements.push({ id: sub, label: sub });
// //           }
// //         });
// //       }
// //     });
    
// //     setFilteredRequirements(allRequirements);

// //     const validRequirementIds = new Set(allRequirements.map(req => req.id));
// //     const updatedRequirements = formData.requirement.filter(req => 
// //       validRequirementIds.has(req) || req === "Others - Mention that product"
// //     );
    
// //     if (updatedRequirements.length !== formData.requirement.length) {
// //       setFormData(prev => ({ ...prev, requirement: updatedRequirements }));
// //     }
// //   };

// //   const handleIndustrySubTypeChange = (value) => {
// //     setFormData(prev => ({ ...prev, industrySubType: value }));
// //     setShowOtherIndustry(value === 'Others');
// //     if (value !== 'Others') {
// //       setOtherIndustry('');
// //     }
// //   };

// //   const handleRequirementChange = (selectedRequirements) => {
// //     setFormData(prev => ({ ...prev, requirement: selectedRequirements }));

// //     const selectedOthers = selectedRequirements.filter(req => 
// //       req.includes('Others - Mention that product')
// //     );
// //     setShowOtherRequirement(selectedOthers.length > 0);

// //     const newOtherRequirements = {};
// //     selectedOthers.forEach(req => {
// //       if (otherRequirements[req]) {
// //         newOtherRequirements[req] = otherRequirements[req];
// //       } else {
// //         newOtherRequirements[req] = '';
// //       }
// //     });
// //     setOtherRequirements(newOtherRequirements);
// //   };

// //   const handleOtherRequirementChange = (category, value) => {
// //     setOtherRequirements(prev => ({
// //       ...prev,
// //       [category]: value
// //     }));
// //   };

// //   const handleDivisionChange = (divisionId) => {
// //     const selectedDivision = divisions.find(div => div.id === divisionId);
// //     const divName = selectedDivision?.label || '';
// //     const isCuso = divName.toLowerCase().includes('cuso');

// //     setFormData(prev => ({
// //       ...prev,
// //       divisionId: divisionId,
// //       divisionName: divName,
// //       ...(!isCuso && {
// //         ourProductId: '',
// //         ourProductBusinessCategoryId: ''
// //       })
// //     }));

// //     if (!isCuso) {
// //       setOurBusinessCategoryList([]);
// //     }

// //     if (errors.divisionId) {
// //       setErrors(prev => ({ ...prev, divisionId: '' }));
// //     }
// //   };

// //   const validateForm = () => {
// //     const newErrors = {};

// //     if (!formData.divisionId) newErrors.divisionId = 'Division is required';
// //     if (!formData.clientName) newErrors.clientName = 'Client name is required';
// //     if (!formData.industryType) newErrors.industryType = 'Industry type is required';
// //     if (!formData.contactName) newErrors.contactName = 'Contact name is required';
    
// //     // Validate ERP Product if CUSO Division is selected
// //     if (isCusoDivision && !formData.ourProductId) {
// //       newErrors.ourProductId = 'Our ERP Product is required for CUSO division';
// //     }

// //     if (!formData.contactPhone && !formData.contactLandline) {
// //       newErrors.contactPhone = 'Either Contact Phone or Landline is required';
// //       newErrors.contactLandline = 'Either Contact Phone or Landline is required';
// //     }

// //     const phoneRegex = /^[6-9]\d{9}$/;
// //     if (formData.contactPhone && !phoneRegex.test(formData.contactPhone.replace(/\D/g, ''))) {
// //       newErrors.contactPhone = 'Invalid phone number (10 digits required)';
// //     }

// //     if (!formData.stageId) newErrors.stageId = 'Stage is required';
    
// //     if (formData.estimatedValue === '' || formData.estimatedValue === null || formData.estimatedValue === undefined) {
// //       newErrors.estimatedValue = 'Invoice Value is required';
// //     }

// //     if (!formData.requirement || formData.requirement.length === 0) {
// //       newErrors.requirement = 'At least one requirement is needed';
// //     }

// //     if (!formData.visitDate && !isEditMode) newErrors.visitDate = 'Visit date is required';
// //     if (!formData.visitType && !isEditMode) newErrors.visitType = 'Visit type is required';
// //     if (!formData.sourceType && !isEditMode) newErrors.sourceType = 'Source type is required';
// //     if (formData.sourceType === 'expo') {
// //       if (!formData.expoYear) newErrors.expoYear = 'Expo year is required';
// //       if (!formData.expoPlace) newErrors.expoPlace = 'Expo place is required';
// //     }

// //     if (showOtherIndustry && !otherIndustry.trim()) {
// //       newErrors.otherIndustry = 'Please specify the industry';
// //     }

// //     if (showOtherProductCategory && !otherProductCategory.trim()) {
// //       newErrors.otherProductCategory = 'Please specify the product category';
// //     }

// //     if (showOtherRequirement) {
// //       let hasEmptyOtherRequirement = false;
// //       Object.keys(otherRequirements).forEach(key => {
// //         if (!otherRequirements[key] || !otherRequirements[key].trim()) {
// //           hasEmptyOtherRequirement = true;
// //         }
// //       });
// //       if (hasEmptyOtherRequirement) {
// //         newErrors.otherRequirements = 'Please specify all other requirements';
// //       }
// //     }

// //     setErrors(newErrors);
// //     return Object.keys(newErrors).length === 0;
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();

// //     if (!validateForm()) {
// //       window.scrollTo({ top: 0, behavior: 'smooth' });
// //       return;
// //     }

// //     setLoading(true);
// //     setSubmitSuccess(false);
// //     setSubmitMessage('');

// //     try {
// //       let processedIndustrySubType = formData.industrySubType;
// //       if (formData.industrySubType === 'Others' && otherIndustry.trim()) {
// //         processedIndustrySubType = `Others - ${otherIndustry}`;
// //       }

// //       const processedProductCategories = formData.productCategory.map(category => {
// //         if (category === "Others - Mention that category" && otherProductCategory.trim()) {
// //           return `Others - ${otherProductCategory}`;
// //         }
// //         return category;
// //       });

// //       const processedRequirements = formData.requirement.map(req => {
// //         if (req === "Others - Mention that product" && otherRequirements[req]) {
// //           return `Others - ${otherRequirements[req]}`;
// //         }
// //         return req;
// //       });

// //       const formDataToSend = new FormData();

// //       const submitData = {
// //         ...formData,
// //         ourProductId: formData.ourProductId || null,
// //         ourProductBusinessCategoryId: formData.ourProductBusinessCategoryId || null,
// //         expoYear: formData.sourceType === 'expo' ? formData.expoYear : '',
// //         expoPlace: formData.sourceType === 'expo' ? formData.expoPlace : '',
// //         industrySubType: processedIndustrySubType,
// //         productCategory: processedProductCategories,
// //         requirement: processedRequirements,
// //         divisionName: formData.divisionName,
// //         nextFollowUpDate: formData.nextFollowUpDate,
// //         estimatedValue: parseFloat(formData.estimatedValue) || 0,
// //         expectedValue: parseFloat(formData.expectedValue) || 0,
// //         recurringValue: parseFloat(formData.recurringValue) || 0,
// //       };

// //       if (isEditMode) {
// //         delete submitData.visitDate;
// //         delete submitData.visitTime;
// //         delete submitData.visitType;
// //         delete submitData.remarks;

// //         submitData.existingImages = existingImages;
// //       } else {
// //         submitData.initialVisitDate = formData.visitDate;
// //         submitData.initialVisitTime = formData.visitTime;
// //       }

// //       if (isConvertMode) {
// //         submitData.leadId = leadId;
// //       }

// //       Object.keys(submitData).forEach(key => {
// //         if (submitData[key] !== null && submitData[key] !== undefined) {
// //           if (Array.isArray(submitData[key])) {
// //             formDataToSend.append(key, JSON.stringify(submitData[key]));
// //           } else if (submitData[key] instanceof Date) {
// //             formDataToSend.append(key, submitData[key].toISOString());
// //           } else {
// //             formDataToSend.append(key, submitData[key]);
// //           }
// //         }
// //       });

// //       locationImages.forEach((image) => {
// //         formDataToSend.append('locationImages', image);
// //       });

// //       let apiConfig = SummaryApi.createClientVisit;
// //       let targetUrl = '';

// //       if (isEditMode) {
// //         apiConfig = SummaryApi.updateClientVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(id) : apiConfig.url;
// //       } else if (isConvertMode) {
// //         apiConfig = SummaryApi.convertExpoLeadToVisit || SummaryApi.convertLeadToVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(leadId) : apiConfig.url;
// //       } else {
// //         apiConfig = SummaryApi.createClientVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url() : apiConfig.url;
// //       }

// //       const response = isEditMode
// //         ? await api.put(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } })
// //         : await api.post(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });

// //       if (response.data.success) {
// //         setSubmitSuccess(true);
// //         const message = isEditMode
// //           ? 'Client visit updated successfully!'
// //           : `Client visit created successfully!\nClient ID: ${response.data.data.clientId}`;

// //         setSubmitMessage(message);
// //         toast.success(message);

// //         setTimeout(() => {
// //           resetForm();
// //           setSubmitSuccess(false);
// //           setSubmitMessage('');
// //           navigate(-1);
// //         }, 2000);
// //       } else {
// //         toast.error(response.data.message || 'Unexpected response from server.');
// //       }
// //     } catch (error) {
// //       console.error('Error submitting form details:', error.response?.data || error);
// //       const errorMessage =
// //         error.response?.data?.message ||
// //         error.response?.data?.error ||
// //         (Array.isArray(error.response?.data?.errors) ? error.response.data.errors.join(', ') : null) ||
// //         error.message ||
// //         'Error submitting form. Please try again.';
// //       toast.error(errorMessage);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const resetForm = () => {
// //     setFormData({
// //       visitType: '',
// //       sourceType: '',
// //       expoYear: '',
// //       expoPlace: '',
// //       visitDate: new Date(),
// //       visitTime: new Date().toTimeString().slice(0, 5),
// //       nextFollowUpDate: null,
// //       nextFollowUpTime: '',
// //       divisionId: '',
// //       divisionName: '',
// //       clientName: '',
// //       industryType: '',
// //       industrySubType: '',
// //       ourProductId: '',
// //       ourProductBusinessCategoryId: '',
// //       contactName: '',
// //       contactDesignation: '',
// //       contactEmail: '',
// //       contactPhone: '',
// //       contactLandline: '',
// //       address: '',
// //       locality: '',
// //       district: '',
// //       state: '',
// //       country: 'IN',
// //       pincode: '',
// //       latitude: '',
// //       longitude: '',
// //       productCategory: [],
// //       requirement: [],
// //       estimatedValue: 0,
// //       expectedValue: 0,
// //       recurringValue: 0,
// //       currency: 'INR',
// //       stageId: '',
// //       expectedClosingDate: null,
// //       paymentStatus: 'na',
// //       remarks: '',
// //       ownerId: user?._id || ''
// //     });
// //     setOurBusinessCategoryList([]);
// //     setErrors({});
// //     setOtherIndustry('');
// //     setOtherRequirements({});
// //     setShowOtherRequirement(false);
// //     setLocationImages([]);
// //     setExistingImages([]);
// //   };

// //   const industryOptions = industryData.map(ind => ({
// //     id: ind.industryCategory,
// //     label: `${ind.icon} ${ind.industryCategory}`
// //   }));

// //   const productCategoryOptions = productCategories.map(cat => ({
// //     id: cat.productCategory,
// //     label: cat.productCategory
// //   }));

// //   useEffect(() => {
// //     if (formData.country === 'IN') {
// //       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
// //         id: state,
// //         label: state
// //       }));
// //       setStates(indianStates);
// //     }
// //   }, [formData.country]);

// //   if (loadingVisit) {
// //     return (
// //       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
// //         <div className="text-center">
// //           <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //           </svg>
// //           <p className="text-gray-600">Loading visit data...</p>
// //         </div>
// //       </div>
// //     );
// //   }

// //   const customClassName = (value) => {
// //     return `bg-transparent border outline-none shadow-sm rounded-[7px] px-2 py-1.5 ${value ? "border-blue-600 text-blue-600" : "border-gray-300"}`;
// //   };

// //   return (
// //     <div className="min-h-screen bg-gray-50">
// //       <div>
// //         {submitSuccess && (
// //           <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
// //             <p className="font-medium">{submitMessage}</p>
// //           </div>
// //         )}

// //         <div className="bg-white overflow-hidden">
// //           <div className="bg-gradient-to-r from-red-600 to-red-600 px-6 py-2">
// //             <div className="flex justify-between items-center text-white">
// //               <div>
// //                 <h1 className="text-2xl font-bold">
// //                   {isEditMode ? 'Edit Client Visit' : (isConvertMode ? 'Convert Expo Lead to Client Visit' : 'Client Visit Form')}
// //                 </h1>
// //                 <p className="text-blue-100 text-sm mt-1">
// //                   {isEditMode ? `Editing visit for Client ID: ${clientData?.clientId}` : (isConvertMode ? 'Converting Open Lead Details' : 'Record new client visit details')}
// //                 </p>
// //               </div>
// //               <button onClick={() => navigate(-1)} className="hover:text-blue-100" title="Go back">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
// //               </button>
// //             </div>
// //           </div>

// //           <form onSubmit={handleSubmit} className="p-2 md:p-6 space-y-6">
// //             {/* Visit Information */}
// //             {!isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
// //                   <span className="text-2xl mr-2">📅</span> Visit Information
// //                 </h2>
// //                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Division <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={divisions} value={formData.divisionId} onChange={handleDivisionChange} placeholder="Select Division" valueKey="id" labelKey="label" error={errors.divisionId} />
// //                     {errors.divisionId && <p className="text-red-500 text-xs mt-1">{errors.divisionId}</p>}
// //                   </div>

// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
// //                     <SelectDropdown
// //                       options={executiveList}
// //                       value={formData.ownerId}
// //                       onChange={handleOwnerChange}
// //                       placeholder="Select Owner"
// //                       searchable={true}
// //                       valueKey="id"
// //                       labelKey="label"
// //                     />
// //                   </div>

// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={visitTypeOptions} value={formData.visitType} onChange={(v) => setFormData(prev => ({ ...prev, visitType: v }))} placeholder="Select Visit Type" valueKey="id" labelKey="label" />
// //                     {errors.visitType && <p className="text-red-500 text-xs mt-1">{errors.visitType}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" />
// //                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
// //                   </div>

// //                   {formData.sourceType === 'expo' && (
// //                     <>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
// //                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" error={errors.expoYear} />
// //                         {errors.expoYear && <p className="text-red-500 text-xs mt-1">{errors.expoYear}</p>}
// //                       </div>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
// //                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
// //                         {errors.expoPlace && <p className="text-red-500 text-xs mt-1">{errors.expoPlace}</p>}
// //                       </div>
// //                     </>
// //                   )}

// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Date <span className="text-red-500">*</span></label>
// //                     <CustomDatePicker value={formData.visitDate} onChange={(date) => setFormData(prev => ({ ...prev, visitDate: date }))} placeholder="Select date" error={errors.visitDate} />
// //                     {errors.visitDate && <p className="text-red-500 text-xs mt-1">{errors.visitDate}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Time</label>
// //                     <input type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} className={`w-full ${customClassName(formData.visitTime)}`} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
// //                     <CustomDatePicker value={formData.nextFollowUpDate} onChange={(date) => setFormData(prev => ({ ...prev, nextFollowUpDate: date }))} placeholder="Select follow-up date" minDate={new Date()} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Time</label>
// //                     <input type="time" name="nextFollowUpTime" value={formData.nextFollowUpTime} onChange={handleChange} className={`w-full ${customClassName(formData.nextFollowUpTime)}`} />
// //                   </div>
// //                 </div>
// //               </div>
// //             )}

// //             {/* Organization Information on Edit mode */}
// //             {isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">🏢</span> Organization Information</h2>
// //                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
// //                     <input type="text" value={formData.divisionName} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
// //                     <input type="text" value={clientData?.clientId || ''} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
// //                     <SelectDropdown
// //                       options={executiveList}
// //                       value={formData.ownerId}
// //                       onChange={handleOwnerChange}
// //                       placeholder="Select Executive Owner"
// //                       searchable={true}
// //                       valueKey="id"
// //                       labelKey="label"
// //                     />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" error={errors.sourceType} />
// //                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
// //                   </div>

// //                   {formData.sourceType === 'expo' && (
// //                     <>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
// //                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" />
// //                       </div>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
// //                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
// //                       </div>
// //                     </>
// //                   )}
// //                 </div>
// //               </div>
// //             )}

// //             {/* Client Information */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">🏢</span> Client Information</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 lg:col-span-1">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Client Name <span className="text-red-500">*</span></label>
// //                   <input type="text" name="clientName" value={formData.clientName} onChange={handleChange} className={`w-full ${customClassName(formData.clientName)} ${errors.clientName ? 'border-red-500' : ''}`} placeholder="Enter company name" />
// //                   {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={industryOptions} value={formData.industryType} onChange={(v) => setFormData(prev => ({ ...prev, industryType: v }))} placeholder="Select Industry" searchable valueKey="id" labelKey="label" error={errors.industryType} />
// //                   {errors.industryType && <p className="text-red-500 text-xs mt-1">{errors.industryType}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Industry Sub-Type</label>
// //                   <SelectDropdown options={filteredSubIndustries} value={formData.industrySubType} onChange={handleIndustrySubTypeChange} placeholder="Select Sub-Industry" valueKey="id" labelKey="label" disabled={!formData.industryType} />
// //                 </div>

// //                 {/* Render ERP Product & Category ONLY for CUSO Division (Spans across all columns cleanly) */}
// //                 {isCusoDivision && (
// //                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:col-span-2 lg:col-span-3 overflow-visible bg-blue-50/50 p-4 rounded-lg border border-blue-200">
// //                     <div>
// //                       <label className="block text-sm font-medium text-gray-700 mb-1">
// //                         Our ERP Product <span className="text-red-500">*</span>
// //                       </label>
// //                       <SelectDropdown
// //                         options={ourProductList}
// //                         value={formData.ourProductId}
// //                         onChange={handleOurProductChange}
// //                         placeholder="Select ERP Product"
// //                         searchable={true}
// //                         valueKey="id"
// //                         labelKey="label"
// //                         error={errors.ourProductId}
// //                       />
// //                       {errors.ourProductId && <p className="text-red-500 text-xs mt-1">{errors.ourProductId}</p>}
// //                     </div>

// //                     <div>
// //                       <label className="block text-sm font-medium text-gray-700 mb-1">
// //                         Product Business Category
// //                       </label>
// //                       <SelectDropdown
// //                         options={ourBusinessCategoryList}
// //                         value={formData.ourProductBusinessCategoryId}
// //                         onChange={(val) => setFormData(prev => ({ ...prev, ourProductBusinessCategoryId: val }))}
// //                         placeholder={formData.ourProductId ? "Select Business Category" : "Select ERP Product First"}
// //                         searchable={true}
// //                         disabled={!formData.ourProductId}
// //                         valueKey="id"
// //                         labelKey="label"
// //                       />
// //                     </div>
// //                   </div>
// //                 )}

// //                 {showOtherIndustry && (
// //                   <div className="sm:col-span-2 lg:col-span-3">
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Specify Industry <span className="text-red-500">*</span></label>
// //                     <input type="text" value={otherIndustry} onChange={(e) => setOtherIndustry(e.target.value)} className={`w-full ${customClassName(otherIndustry)} ${errors.otherIndustry ? 'border-red-500' : ''}`} placeholder="Please specify the industry" />
// //                     {errors.otherIndustry && <p className="text-red-500 text-xs mt-1">{errors.otherIndustry}</p>}
// //                   </div>
// //                 )}
// //               </div>
// //             </div>

// //             {/* Contact Details */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">👤</span> Contact Details</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name <span className="text-red-500">*</span></label>
// //                   <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className={`w-full ${customClassName(formData.contactName)} ${errors.contactName ? 'border-red-500' : ''}`} placeholder="Full name" />
// //                   {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
// //                   <input type="text" name="contactDesignation" value={formData.contactDesignation} onChange={handleChange} className={`w-full ${customClassName(formData.contactDesignation)}`} placeholder="e.g., CEO, Manager, Director" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
// //                   <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className={`w-full ${customClassName(formData.contactEmail)} ${errors.contactEmail ? 'border-red-500' : ''}`} placeholder="email@example.com" />
// //                   {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
// //                   <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className={`w-full ${customClassName(formData.contactPhone)} ${errors.contactPhone ? 'border-red-500' : ''}`} placeholder="10 digit mobile number" />
// //                   {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Landline {!formData.contactPhone && <span className="text-red-500">*</span>}</label>
// //                   <input type="text" name="contactLandline" value={formData.contactLandline} onChange={handleChange} className={`w-full ${customClassName(formData.contactLandline)} ${errors.contactLandline ? 'border-red-500' : ''}`} placeholder="Landline number with STD code" />
// //                   {errors.contactLandline && <p className="text-red-500 text-xs mt-1">{errors.contactLandline}</p>}
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Location Information */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <div className="flex justify-between items-center mb-4">
// //                 <h2 className="text-lg font-semibold text-gray-800 flex items-center"><span className="text-2xl mr-2">📍</span> Location Information</h2>
// //                 <button
// //                   type="button"
// //                   onClick={getCurrentLocation}
// //                   disabled={locationLoading}
// //                   className={`px-2 py-1.5 ${locationLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-[7px] transition-colors text-sm font-medium shadow-sm flex items-center gap-2`}
// //                 >
// //                   {locationLoading ? (
// //                     <>
// //                       <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //                       </svg>
// //                       <span className="hidden sm:inline">Getting Location...</span>
// //                       <span className="sm:hidden">Loading...</span>
// //                     </>
// //                   ) : (
// //                     <>
// //                       <span className="hidden sm:inline">Get Current Location</span>
// //                       <span className="sm:hidden">Get Loca...</span>
// //                     </>
// //                   )}
// //                 </button>
// //               </div>

// //               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 lg:col-span-3">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
// //                   <textarea name="address" value={formData.address} onChange={handleChange} rows="2" className={`w-full ${customClassName(formData.address)}`} placeholder="Complete address" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
// //                   <input type="text" name="locality" value={formData.locality} onChange={handleChange} className={`w-full ${customClassName(formData.locality)}`} placeholder="Area/Locality" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
// //                   <SelectDropdown options={countries} value={formData.country} onChange={handleCountryChange} placeholder="Select Country" valueKey="isoCode" labelKey="name" searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
// //                   <SelectDropdown options={states} value={formData.state} onChange={handleStateChange} placeholder={formData.country === 'IN' ? "Select State" : "Enter State"} valueKey="id" labelKey="label" disabled={!formData.country} searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
// //                   <SelectDropdown options={districts} value={formData.district} onChange={(v) => setFormData(prev => ({ ...prev, district: v }))} placeholder={formData.state ? "Select District" : "Select State First"} valueKey="id" labelKey="label" disabled={!formData.state || formData.country !== 'IN'} searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
// //                   <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength="6" className={`w-full ${customClassName(formData.pincode)}`} placeholder="6 digits" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
// //                   <input type="text" name="latitude" value={formData.latitude} onChange={handleChange} className={`w-full ${customClassName(formData.latitude)}`} placeholder="e.g., 28.6139" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
// //                   <input type="text" name="longitude" value={formData.longitude} onChange={handleChange} className={`w-full ${customClassName(formData.longitude)}`} placeholder="e.g., 77.2090" />
// //                 </div>
// //               </div>

// //               {/* Location Images Upload */}
// //               <div className={`border-2 border-dotted ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} p-4 rounded mt-6 transition-colors`}
// //                 onDragOver={handleDragOver}
// //                 onDragLeave={handleDragLeave}
// //                 onDrop={handleDrop}
// //               >
// //                 <p className="text-center font-medium">Location Pictures (Optional)</p>
// //                 <p className="text-center text-xs text-gray-500 mb-2">Upload or capture location images (max 2MB per image)</p>

// //                 {locationImages.length > 0 && (
// //                   <div className="flex flex-wrap gap-2 mb-4">
// //                     {locationImages.map((file, index) => (
// //                       <div key={index} className="relative group">
// //                         <img
// //                           src={URL.createObjectURL(file)}
// //                           alt={`Location ${index + 1}`}
// //                           className="w-20 h-20 object-cover rounded border hover:border-blue-500 transition-colors"
// //                         />
// //                         <button
// //                           type="button"
// //                           onClick={() => setLocationImages((prev) => prev.filter((_, i) => i !== index))}
// //                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
// //                         >
// //                           <X className="w-3 h-3" />
// //                         </button>
// //                         <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center rounded-b">
// //                           {(file.size / 1024 / 1024).toFixed(1)}MB
// //                         </div>
// //                       </div>
// //                     ))}
// //                   </div>
// //                 )}

// //                 {existingImages.length > 0 && (
// //                   <div className="flex flex-wrap gap-2 mb-4">
// //                     {existingImages.map((imageUrl, index) => (
// //                       <div key={`existing-${index}`} className="relative group">
// //                         <img
// //                           src={imageUrl}
// //                           alt={`Existing Location ${index + 1}`}
// //                           className="w-20 h-20 object-cover rounded border border-gray-300 hover:border-blue-500 transition-colors"
// //                         />
// //                         <button
// //                           type="button"
// //                           onClick={() => setExistingImages((prev) => prev.filter((_, i) => i !== index))}
// //                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
// //                         >
// //                           <X className="w-3 h-3" />
// //                         </button>
// //                       </div>
// //                     ))}
// //                   </div>
// //                 )}

// //                 <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
// //                   <label className="cursor-pointer">
// //                     <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
// //                     <div className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-4 rounded-lg transition-colors">
// //                       <Plus className="w-5 h-5" /> <span>Upload Images</span>
// //                     </div>
// //                   </label>
// //                   <label className="cursor-pointer">
// //                     <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
// //                     <div className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors">
// //                       <Camera className="w-5 h-5" /> <span>Use Camera</span>
// //                     </div>
// //                   </label>
// //                 </div>

// //                 <div className="text-center text-sm text-gray-600 mt-3 space-y-1">
// //                   {(locationImages.length > 0 || existingImages.length > 0) && (
// //                     <p>Total images: {locationImages.length + existingImages.length}</p>
// //                   )}
// //                   <p className="text-xs text-gray-500">Images larger than 2MB will be automatically compressed</p>
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Business Details */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">💼</span> Business Details</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 overflow-visible">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Product Categories <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={productCategoryOptions} value={formData.productCategory} onChange={handleProductCategoryChange} placeholder="Select Product Categories" valueKey="id" labelKey="label" multiple={true} searchable={true} error={errors.productCategory} />
// //                   {errors.productCategory && <p className="text-red-500 text-xs mt-1">{errors.productCategory}</p>}

// //                   {showOtherProductCategory && (
// //                     <div className="mt-3">
// //                       <label className="block text-sm font-medium text-gray-700 mb-1">Specify Product Category <span className="text-red-500">*</span></label>
// //                       <input type="text" value={otherProductCategory} onChange={(e) => setOtherProductCategory(e.target.value)} className={`w-full ${customClassName(otherProductCategory)} ${errors.otherProductCategory ? 'border-red-500' : ''}`} placeholder="Please specify the product category" />
// //                       {errors.otherProductCategory && <p className="text-red-500 text-xs mt-1">{errors.otherProductCategory}</p>}
// //                     </div>
// //                   )}
// //                 </div>

// //                 <div className="sm:col-span-2 overflow-visible">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Requirements <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={filteredRequirements} value={formData.requirement} onChange={handleRequirementChange} placeholder="Select Requirements" valueKey="id" labelKey="label" multiple={true} searchable={true} disabled={!formData.productCategory || formData.productCategory.length === 0} error={errors.requirement} />
// //                   {errors.requirement && <p className="text-red-500 text-xs mt-1">{errors.requirement}</p>}
// //                 </div>

// //                 {showOtherRequirement && (
// //                   <div className="sm:col-span-2 space-y-3">
// //                     {formData.requirement
// //                       .filter(req => req.includes('Others - Mention that product'))
// //                       .map((otherReq, index) => (
// //                         <div key={index}>
// //                           <label className="block text-sm font-medium text-gray-700 mb-1">
// //                             Specify Other Requirement for {otherReq.split(' - ')[0]} <span className="text-red-500">*</span>
// //                           </label>
// //                           <input
// //                             type="text"
// //                             value={otherRequirements[otherReq] || ''}
// //                             onChange={(e) => handleOtherRequirementChange(otherReq, e.target.value)}
// //                             className={`w-full ${customClassName(otherRequirements[otherReq])} ${errors.otherRequirements ? 'border-red-500' : ''}`}
// //                             placeholder="Please specify the requirement"
// //                           />
// //                         </div>
// //                       ))}
// //                     {errors.otherRequirements && <p className="text-red-500 text-xs mt-1">{errors.otherRequirements}</p>}
// //                   </div>
// //                 )}

// //                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:col-span-2 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
// //                     <SelectDropdown options={currencyOptions} value={formData.currency} onChange={(value) => setFormData(prev => ({ ...prev, currency: value }))} valueKey="id" labelKey="label" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Value <span className="text-red-500">*</span></label>
// //                     <input type="number" name="estimatedValue" value={formData.estimatedValue} onChange={handleChange} className={`w-full ${customClassName(formData.estimatedValue)} ${errors.estimatedValue ? 'border-red-500' : ''}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                     {errors.estimatedValue && <p className="text-red-500 text-xs mt-1">{errors.estimatedValue}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Expected Value</label>
// //                     <input type="number" name="expectedValue" value={formData.expectedValue} onChange={handleChange} className={`w-full ${customClassName(formData.expectedValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Recurring Value</label>
// //                     <input type="number" name="recurringValue" value={formData.recurringValue} onChange={handleChange} className={`w-full ${customClassName(formData.recurringValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                   </div>
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Sales Pipeline */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📊</span> Sales Pipeline</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Stage <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={salesStages} value={formData.stageId} onChange={(v) => setFormData(prev => ({ ...prev, stageId: v }))} placeholder="Select Stage" searchable valueKey="_id" labelKey="stageName" />
// //                   {errors.stageId && <p className="text-red-500 text-xs mt-1">{errors.stageId}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Expected Closing Date</label>
// //                   <CustomDatePicker value={formData.expectedClosingDate} onChange={(date) => setFormData(prev => ({ ...prev, expectedClosingDate: date }))} placeholder="Select date" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
// //                   <SelectDropdown options={paymentStatusOptions} value={formData.paymentStatus} onChange={(v) => setFormData(prev => ({ ...prev, paymentStatus: v }))} valueKey="id" labelKey="label" />
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Visit Remarks */}
// //             {!isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📝</span> Visit Remarks</h2>
// //                 <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="4" className={`w-full ${customClassName(formData.remarks)}`} placeholder="Any important notes, observations, or action items from this visit..." />
// //               </div>
// //             )}

// //             {/* Visit History Table in Edit mode */}
// //             {isEditMode && clientData?.visitHistory && clientData.visitHistory.length > 0 && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
// //                   <span className="text-2xl mr-2">📜</span> Visit History
// //                 </h2>
// //                 <div className="overflow-x-auto custom-scrollbar">
// //                   <table className="min-w-full divide-y divide-gray-200">
// //                     <thead className="bg-gray-100">
// //                       <tr>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visit #</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
// //                       </tr>
// //                     </thead>
// //                     <tbody className="bg-white divide-y divide-gray-200">
// //                       {clientData.visitHistory.map((visit, index) => (
// //                         <tr key={index}>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitNumber}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(visit.visitDate).toLocaleDateString()}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitTime}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
// //                             <span className="capitalize block font-medium">{visit.visitType}</span>
// //                             {clientData?.sourceType === 'expo' && (
// //                               <div className="mt-1 text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 inline-block">
// //                                 <span className="font-semibold text-[9px] uppercase">Source: Expo</span><br/>
// //                                 {clientData.expoPlace} ({clientData.expoYear})
// //                               </div>
// //                             )}
// //                           </td>
// //                           <td className="px-6 py-4 text-sm text-gray-900">{visit.remarks || '-'}</td>
// //                         </tr>
// //                       ))}
// //                     </tbody>
// //                   </table>
// //                 </div>
// //               </div>
// //             )}

// //             {Object.keys(errors).length > 0 && (
// //               <div className="bg-red-50 border border-red-200 rounded-lg p-4">
// //                 <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
// //                 <ul className="list-disc list-inside text-sm text-red-700">
// //                   {Object.entries(errors).map(([field, error]) => <li key={field}>{error}</li>)}
// //                 </ul>
// //               </div>
// //             )}

// //             {/* Submit Buttons */}
// //             <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t">
// //               <button type="button" onClick={() => navigate(-1)} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Cancel</button>
// //               <div className="flex flex-col sm:flex-row gap-4">
// //                 {!isEditMode && (
// //                   <button type="button" onClick={resetForm} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Reset Form</button>
// //                 )}
// //                 <button type="submit" disabled={loading} className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
// //                   {loading ? (
// //                     <>
// //                       <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //                       </svg>
// //                       {isEditMode ? 'Updating...' : (isConvertMode ? 'Converting...' : 'Submitting...')}
// //                     </>
// //                   ) : (
// //                     isEditMode ? 'Update Visit' : (isConvertMode ? 'Convert & Submit' : 'Submit Visit')
// //                   )}
// //                 </button>
// //               </div>
// //             </div>
// //           </form>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default ClientVisitForm;

// // import React, { useState, useEffect, useCallback } from 'react';
// // import { useParams, useNavigate } from 'react-router-dom';
// // import { industryData, productCategories, visitTypeOptions, sourceTypeOptions, currencyOptions, paymentStatusOptions } from './IndustryData';
// // import AllStatesAndDistricts from '../../helpers/AllStatesAndDistricts';
// // import { Country } from 'country-state-city';
// // import SelectDropdown from '../../customStyles/SelectDropdown';
// // import CustomDatePicker from '../../customStyles/CustomDatePicker';
// // import SummaryApi from '../../common/SummaryApi';
// // import api from '../../common/apiClient';
// // import { toast } from 'react-toastify';
// // import imageCompression from 'browser-image-compression';
// // import { Camera, Plus, X } from 'lucide-react';
// // import { useSelector } from 'react-redux';

// // const ClientVisitForm = () => {
// //   const user = useSelector((state) => state?.user?.user);
// //   const { id, leadId } = useParams();
// //   const navigate = useNavigate();
// //   const isEditMode = !!id;
// //   const isConvertMode = !!leadId;

// //   const [salesStages, setSalesStages] = useState([]);
// //   const [loadingVisit, setLoadingVisit] = useState(false);
  
// //   const [showOtherProductCategory, setShowOtherProductCategory] = useState(false);
// //   const [otherProductCategory, setOtherProductCategory] = useState('');
// //   const [showOtherRequirement, setShowOtherRequirement] = useState(false);
// //   const [otherRequirements, setOtherRequirements] = useState({});
// //   const [locationImages, setLocationImages] = useState([]);
// //   const [existingImages, setExistingImages] = useState([]);
// //   const [isDragActive, setIsDragActive] = useState(false);
// //   const [locationLoading, setLocationLoading] = useState(false);
// //   const [executiveList, setExecutiveList] = useState([]);

// //   const [formData, setFormData] = useState({
// //     visitType: '',
// //     sourceType: '',
// //     expoYear: '',
// //     expoPlace: '',
// //     visitDate: new Date(),
// //     visitTime: new Date().toTimeString().slice(0, 5),
// //     nextFollowUpDate: null,
// //     nextFollowUpTime: '',

// //     divisionId: '',
// //     divisionName: '',

// //     clientName: '',
// //     industryType: '',
// //     industrySubType: '',

// //     contactName: '',
// //     contactDesignation: '',
// //     contactEmail: '',
// //     contactPhone: '',
// //     contactLandline: '',

// //     address: '',
// //     locality: '',
// //     district: '',
// //     state: '',
// //     country: 'IN',
// //     pincode: '',
// //     latitude: '',
// //     longitude: '',

// //     productCategory: [],
// //     requirement: [],
// //     estimatedValue: 0,
// //     expectedValue: 0,
// //     recurringValue: 0,
// //     currency: 'INR',

// //     stageId: '',
// //     expectedClosingDate: null,
// //     paymentStatus: 'na',

// //     remarks: '',
// //     ownerId: user._id
// //   });

// //   const [errors, setErrors] = useState({});
// //   const [loading, setLoading] = useState(false);
// //   const [divisions, setDivisions] = useState([]);
// //   const [countries, setCountries] = useState([]);
// //   const [states, setStates] = useState([]);
// //   const [districts, setDistricts] = useState([]);
// //   const [filteredSubIndustries, setFilteredSubIndustries] = useState([]);
// //   const [filteredRequirements, setFilteredRequirements] = useState([]);
// //   const [showOtherIndustry, setShowOtherIndustry] = useState(false);
// //   const [otherIndustry, setOtherIndustry] = useState('');
// //   const [submitSuccess, setSubmitSuccess] = useState(false);
// //   const [submitMessage, setSubmitMessage] = useState('');
// //   const [clientData, setClientData] = useState(null);

// //   const currentYear = new Date().getFullYear();
// //   const startYear = 2025;
// //   const expoYearOptions = [];

// //   for (let y = currentYear; y >= startYear; y--) {
// //     expoYearOptions.push({ id: y.toString(), label: y.toString() });
// //   }  

// //   // Fetch executives list for owner assignment
// //   const fetchExecutives = useCallback(async () => {
// //     try {
// //       const response = await api.get(SummaryApi.executiveUsers.url);
// //       const resData = response.data;
      
// //       let execs = [];
// //       if (Array.isArray(resData?.data)) {
// //         execs = resData.data;
// //       } else if (Array.isArray(resData?.users)) {
// //         execs = resData.users;
// //       } else if (Array.isArray(resData?.executives)) {
// //         execs = resData.executives;
// //       } else if (Array.isArray(resData)) {
// //         execs = resData;
// //       }

// //       const formattedList = execs.map((exec) => {
// //         const rawId = exec._id || exec.id || exec.userId || '';
// //         const nameStr = (
// //           exec.fullName
// //             ? `${exec.fullName} ${exec.lastName || ''}`.trim()
// //             : (exec.name || exec.clientName || exec.userName || exec.email || '')
// //         ) || 'Executive';

// //         return {
// //           id: String(rawId),
// //           _id: String(rawId),
// //           value: String(rawId),
// //           label: nameStr,
// //           name: nameStr
// //         };
// //       });

// //       setExecutiveList(formattedList);
// //     } catch (error) {
// //       console.error("Error fetching executives:", error);
// //     }
// //   }, []);

// //   // Fetch executives on component mount
// //   useEffect(() => {
// //     fetchExecutives();
// //   }, [fetchExecutives]);

// //   // Handler for Assigned Executive / Owner dropdown change
// //   const handleOwnerChange = (selectedVal) => {
// //     let idValue = selectedVal;
// //     if (selectedVal && typeof selectedVal === 'object') {
// //       idValue = selectedVal.id || selectedVal._id || selectedVal.value;
// //     }

// //     setFormData(prev => ({
// //       ...prev,
// //       ownerId: String(idValue || '')
// //     }));

// //     if (errors.ownerId) {
// //       setErrors(prev => ({ ...prev, ownerId: '' }));
// //     }
// //   };

// //   const compressImage = async (file) => {
// //     const options = {
// //       maxSizeMB: 2,
// //       maxWidthOrHeight: 1920,
// //       useWebWorker: true,
// //     };

// //     try {
// //       const compressedFile = await imageCompression(file, options);
// //       return compressedFile;
// //     } catch (error) {
// //       console.error('Error compressing image:', error);
// //       return file;
// //     }
// //   };

// //   const handleFileChange = async (e) => {
// //     const files = Array.from(e.target.files);

// //     if (locationImages.length + files.length > 10) {
// //       toast.error('Maximum 10 images allowed');
// //       return;
// //     }

// //     const compressedFiles = await Promise.all(
// //       files.map(async (file) => {
// //         if (file.size > 2 * 1024 * 1024) {
// //           const compressed = await compressImage(file);
// //           return compressed;
// //         }
// //         return file;
// //       })
// //     );

// //     setLocationImages(prev => [...prev, ...compressedFiles]);
// //   };

// //   const handleDragOver = (e) => {
// //     e.preventDefault();
// //     setIsDragActive(true);
// //   };

// //   const handleDragLeave = (e) => {
// //     e.preventDefault();
// //     setIsDragActive(false);
// //   };

// //   const handleDrop = async (e) => {
// //     e.preventDefault();
// //     setIsDragActive(false);

// //     const files = Array.from(e.dataTransfer.files).filter(file =>
// //       file.type.startsWith('image/')
// //     );

// //     if (locationImages.length + files.length > 10) {
// //       toast.error('Maximum 10 images allowed');
// //       return;
// //     }

// //     const compressedFiles = await Promise.all(
// //       files.map(async (file) => {
// //         if (file.size > 2 * 1024 * 1024) {
// //           const compressed = await compressImage(file);
// //           return compressed;
// //         }
// //         return file;
// //       })
// //     );

// //     setLocationImages(prev => [...prev, ...compressedFiles]);
// //   };

// //   const reverseGeocode = async (latitude, longitude) => {
// //     try {
// //       const response = await fetch(
// //         `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
// //         {
// //           headers: {
// //             'Accept': 'application/json',
// //             'User-Agent': 'ClientVisitForm/1.0'
// //           }
// //         }
// //       );

// //       if (!response.ok) {
// //         throw new Error('Failed to fetch address');
// //       }

// //       const data = await response.json();
// //       return data;
// //     } catch (error) {
// //       console.error('Reverse geocoding error:', error);

// //       try {
// //         const fallbackResponse = await fetch(
// //           `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
// //         );

// //         if (fallbackResponse.ok) {
// //           const fallbackData = await fallbackResponse.json();

// //           let district = '';
// //           if (fallbackData.localityInfo && fallbackData.localityInfo.administrative) {
// //             const districtInfo = fallbackData.localityInfo.administrative.find(
// //               admin => admin.description?.toLowerCase().includes('district') || admin.adminLevel === 5
// //             );
// //             district = districtInfo ? districtInfo.name.replace(' district', '') : '';
// //           }

// //           return {
// //             display_name: fallbackData.locality || '',
// //             address: {
// //               road: fallbackData.street || '',
// //               neighbourhood: fallbackData.locality || '',
// //               suburb: fallbackData.suburb || '',
// //               city: fallbackData.city || '',
// //               state_district: district,
// //               state: fallbackData.principalSubdivision || '',
// //               country: fallbackData.countryName || '',
// //               country_code: fallbackData.countryCode?.toLowerCase() || '',
// //               postcode: fallbackData.postcode || ''
// //             }
// //           };
// //         }
// //       } catch (fallbackError) {
// //         console.error('Fallback geocoding also failed:', fallbackError);
// //       }

// //       return null;
// //     }
// //   };

// //   const getCurrentLocation = () => {
// //     if (navigator.geolocation) {
// //       setLocationLoading(true);
// //       toast.info('Getting your location...');

// //       navigator.geolocation.getCurrentPosition(
// //         async (position) => {
// //           const lat = position.coords.latitude;
// //           const lon = position.coords.longitude;

// //           setFormData(prev => ({
// //             ...prev,
// //             latitude: lat.toFixed(6),
// //             longitude: lon.toFixed(6)
// //           }));

// //           const addressData = await reverseGeocode(lat, lon);

// //           if (addressData) {
// //             if (addressData.address) {
// //               const addr = addressData.address;

// //               const addressParts = [];
// //               if (addr.house_number) addressParts.push(addr.house_number);
// //               if (addr.road || addr.street) addressParts.push(addr.road || addr.street);
// //               if (addr.neighbourhood) addressParts.push(addr.neighbourhood);
// //               if (addr.suburb) addressParts.push(addr.suburb);
// //               if (addr.city && addr.city !== addr.neighbourhood && addr.city !== addr.suburb) {
// //                 addressParts.push(addr.city);
// //               }

// //               const fullAddress = addressParts.length > 0
// //                 ? addressParts.filter(Boolean).join(', ')
// //                 : addressData.display_name?.split(',').slice(0, 3).join(',') || '';

// //               const apiStateName = addr.state || addr.principalSubdivision || '';
// //               const matchedState = findClosestMatch(apiStateName, Object.keys(AllStatesAndDistricts));

// //               if (matchedState) {
// //                 setFormData(prev => ({
// //                   ...prev,
// //                   address: fullAddress,
// //                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
// //                   pincode: addr.postcode || prev.pincode
// //                 }));

// //                 const countryCode = addr.country_code ? addr.country_code.toUpperCase() : 'IN';

// //                 if (countryCode !== formData.country) {
// //                   handleCountryChange(countryCode);
// //                   setTimeout(() => {
// //                     handleStateChange(matchedState);
// //                     setTimeout(() => {
// //                       setDistrictFromGeocode(addr, matchedState);
// //                       setLocationLoading(false);
// //                     }, 300);
// //                   }, 200);
// //                 } else {
// //                   handleStateChange(matchedState);
// //                   setTimeout(() => {
// //                     setDistrictFromGeocode(addr, matchedState);
// //                     setLocationLoading(false);
// //                   }, 300);
// //                 }

// //                 toast.success('Location and address captured successfully!');
// //               } else {
// //                 setFormData(prev => ({
// //                   ...prev,
// //                   address: fullAddress,
// //                   locality: addr.neighbourhood || addr.suburb || addr.locality || addr.city || '',
// //                   state: apiStateName,
// //                   pincode: addr.postcode || prev.pincode
// //                 }));

// //                 setLocationLoading(false);
// //                 toast.warning('Location captured, but state/district may need manual adjustment');
// //               }
// //             }
// //           } else {
// //             setLocationLoading(false);
// //             toast.warning('Location captured, but could not fetch address details. Please fill manually.');
// //           }
// //         },
// //         (error) => {
// //           console.error('Geolocation error:', error);
// //           setLocationLoading(false);

// //           let errorMessage = 'Unable to get location. ';
// //           switch (error.code) {
// //             case error.PERMISSION_DENIED:
// //               errorMessage += 'Please allow location access in your browser settings.';
// //               break;
// //             case error.POSITION_UNAVAILABLE:
// //               errorMessage += 'Location information is unavailable.';
// //               break;
// //             case error.TIMEOUT:
// //               errorMessage += 'Request timed out. Please try again.';
// //               break;
// //             default:
// //               errorMessage += 'An unknown error occurred.';
// //           }
// //           toast.error(errorMessage);
// //         },
// //         { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
// //       );
// //     } else {
// //       toast.error('Geolocation is not supported by your browser');
// //     }
// //   };

// //   const setDistrictFromGeocode = (addressData, matchedState) => {
// //     let apiDistrictName = addressData.state_district || addressData.county || addressData.city || '';

// //     apiDistrictName = apiDistrictName
// //       .replace(/ district$/i, '')
// //       .replace(/ District$/i, '')
// //       .replace(/ Dist\.?$/i, '')
// //       .replace(/ Zilla$/i, '')
// //       .replace(/ Jilla$/i, '')
// //       .trim();

// //     const stateDistricts = AllStatesAndDistricts[matchedState] || [];
// //     let matchedDistrict = findClosestMatch(apiDistrictName, stateDistricts);

// //     if (!matchedDistrict && apiDistrictName) {
// //       if (addressData.city && addressData.city !== apiDistrictName) {
// //         matchedDistrict = findClosestMatch(addressData.city, stateDistricts);
// //       }
// //       if (!matchedDistrict && addressData.locality) {
// //         matchedDistrict = findClosestMatch(addressData.locality, stateDistricts);
// //       }
// //     }

// //     if (matchedDistrict) {
// //       setFormData(prev => ({ ...prev, district: matchedDistrict }));
// //     } else {
// //       setFormData(prev => ({ ...prev, district: '' }));
// //     }
// //   };

// //   const findClosestMatch = (searchTerm, list) => {
// //     if (!searchTerm || !list || list.length === 0) return null;

// //     const normalizedSearch = searchTerm.toLowerCase().trim();
// //     const exactMatch = list.find(item => item.toLowerCase().trim() === normalizedSearch);
// //     if (exactMatch) return exactMatch;

// //     const searchWithoutCommonWords = normalizedSearch
// //       .replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '')
// //       .trim();

// //     const matchWithoutCommonWords = list.find(item =>
// //       item.toLowerCase().replace(/\b(district|city|town|taluk|tehsil|mandal|block)\b/gi, '').trim() === searchWithoutCommonWords
// //     );
// //     if (matchWithoutCommonWords) return matchWithoutCommonWords;

// //     const partialMatch = list.find(item => {
// //       const normalizedItem = item.toLowerCase();
// //       return normalizedItem.includes(normalizedSearch) ||
// //         normalizedSearch.includes(normalizedItem) ||
// //         normalizedItem.includes(searchWithoutCommonWords) ||
// //         searchWithoutCommonWords.includes(normalizedItem);
// //     });
// //     if (partialMatch) return partialMatch;

// //     const firstWord = normalizedSearch.split(' ')[0];
// //     if (firstWord.length > 3) {
// //       const firstWordMatch = list.find(item => item.toLowerCase().startsWith(firstWord));
// //       if (firstWordMatch) return firstWordMatch;
// //     }

// //     return null;
// //   };

// //   const fetchOpenLeadDetails = useCallback(async () => {
// //     setLoadingVisit(true);
// //     try {
// //       let lead = null;

// //       if (SummaryApi.getOneExpoLead?.url) {
// //         try {
// //           const expoRes = await api.get(SummaryApi.getOneExpoLead.url(leadId));
// //           if (expoRes.data?.success) {
// //             lead = expoRes.data.data;
// //           }
// //         } catch (expoErr) {
// //           console.log('Expo Lead fetch fallback...');
// //         }
// //       }

// //       if (!lead) {
// //         try {
// //           const response = await api.get(SummaryApi.getOpenLeadById.url(leadId));
// //           if (response.data?.success) {
// //             lead = response.data.data;
// //           }
// //         } catch (err) {
// //           console.error('Lead lookup error:', err);
// //         }
// //       }

// //       if (lead) {
// //         let matchedIndustryCategory = '';
// //         let rawIndustryText = lead.industry || '';
// //         if (!rawIndustryText && lead.division) {
// //           rawIndustryText = Array.isArray(lead.division)
// //             ? lead.division.join(' ')
// //             : String(lead.division);
// //         }

// //         if (rawIndustryText) {
// //           const targetText = String(rawIndustryText).toLowerCase().trim();
// //           const foundIndustry = industryData.find(ind => {
// //             const catLower = ind.industryCategory.toLowerCase().trim();
// //             return catLower === targetText || catLower.includes(targetText) || targetText.includes(catLower);
// //           });
// //           if (foundIndustry) {
// //             matchedIndustryCategory = foundIndustry.industryCategory;
// //           }
// //         }

// //         let matchedProductCategories = [];
// //         let matchedRequirements = [];
// //         const rawRequirementText = lead.requirement || '';

// //         if (rawRequirementText) {
// //           productCategories.forEach(cat => {
// //             if (rawRequirementText.toLowerCase().includes(cat.productCategory.toLowerCase())) {
// //               matchedProductCategories.push(cat.productCategory);
// //             }
// //             cat.subCategories.forEach(sub => {
// //               if (rawRequirementText.toLowerCase().includes(sub.toLowerCase())) {
// //                 if (!matchedProductCategories.includes(cat.productCategory)) {
// //                   matchedProductCategories.push(cat.productCategory);
// //                 }
// //                 matchedRequirements.push(sub);
// //               }
// //             });
// //           });
// //         }

// //         const parsedDivisionId = Array.isArray(lead.division)
// //           ? lead.division[0] || ''
// //           : lead.division || '';

// //         const parsedOwnerId = lead.ownerId
// //           ? String(typeof lead.ownerId === 'object' ? (lead.ownerId._id || lead.ownerId.id) : lead.ownerId)
// //           : '';

// //         setFormData(prev => ({
// //           ...prev,
// //           clientName: lead.companyOrSchool || lead.fullName || '',
// //           industryType: matchedIndustryCategory || "Education & Training",
// //           contactName: lead.fullName || '',
// //           contactDesignation: lead.designation || '',
// //           contactEmail: lead.email || '',
// //           contactPhone: lead.mobile || '',
// //           address: lead.address || lead.city || '',
// //           locality: lead.locality || lead.city || '',
// //           divisionId: parsedDivisionId || prev.divisionId,
// //           sourceType: lead.source === 'expo' || lead.expoId ? 'expo' : (lead.source || 'expo'),
// //           expoYear: lead.expoId?.expoYear ? String(lead.expoId.expoYear) : new Date().getFullYear().toString(),
// //           expoPlace: lead.expoId?.expoName || lead.source || '',
// //           productCategory: matchedProductCategories,
// //           requirement: matchedRequirements,
// //           remarks: lead.requirement || lead.remarks || '',
// //           ownerId: parsedOwnerId || prev.ownerId
// //         }));

// //         toast.success(`Loaded Lead: ${lead.fullName}`);
// //       }
// //     } catch (err) {
// //       console.error(err);
// //       toast.error('Failed to load lead information');
// //     } finally {
// //       setLoadingVisit(false);
// //     }
// //   }, [leadId]);

// //   const fetchVisitData = useCallback(async () => {
// //     setLoadingVisit(true);
// //     try {
// //       const response = await api.get(SummaryApi.getOneClientVisit.url(id));

// //       if (response.data.success) {
// //         const visit = response.data.data;
// //         setClientData(visit);

// //         if (visit.locationImages && visit.locationImages.length > 0) {
// //           setExistingImages(visit.locationImages);
// //         }

// //         let industrySubType = visit.industrySubType || '';
// //         if (industrySubType.startsWith('Others - ')) {
// //           setShowOtherIndustry(true);
// //           setOtherIndustry(industrySubType.substring(9));
// //           industrySubType = 'Others';
// //         }
        
// //         let productCategories = [...(visit.productCategory || [])];
// //         let otherCategoryText = "";
        
// //         const otherCategoryIndex = productCategories.findIndex(cat => cat.startsWith("Others - "));
// //         if (otherCategoryIndex !== -1) {
// //           otherCategoryText = productCategories[otherCategoryIndex].substring(9);
// //           productCategories[otherCategoryIndex] = "Others - Mention that category";
// //           setOtherProductCategory(otherCategoryText);
// //           setShowOtherProductCategory(true);
// //         }

// //         let requirements = [...(visit.requirement || [])];
// //         const otherReqs = {};
        
// //         requirements = requirements.map(req => {
// //           if (req.startsWith('Others - ')) {
// //             const value = req.substring(9);
// //             const newKey = "Others - Mention that product";
// //             otherReqs[newKey] = value;
// //             return newKey;
// //           }
// //           return req;
// //         });
        
// //         setOtherRequirements(otherReqs);
// //         setShowOtherRequirement(Object.keys(otherReqs).length > 0);

// //         const parsedOwnerId = visit.ownerId
// //           ? String(typeof visit.ownerId === 'object' ? (visit.ownerId._id || visit.ownerId.id) : visit.ownerId)
// //           : '';

// //         setFormData({
// //           visitType: visit.visitHistory?.[0]?.visitType || '',
// //           sourceType: visit.sourceType || '',
// //           expoYear: visit?.expoYear || '',
// //           expoPlace: visit?.expoPlace || '',
// //           visitDate: visit.visitHistory?.[0]?.visitDate ? new Date(visit.visitHistory[0].visitDate) : null,
// //           visitTime: visit.visitHistory?.[0]?.visitTime || new Date().toTimeString().slice(0, 5),
// //           nextFollowUpDate: visit.nextFollowUpDate ? new Date(visit.nextFollowUpDate) : null,
// //           nextFollowUpTime: visit.nextFollowUpTime || '',
// //           divisionId: visit.divisionId?._id || '',
// //           divisionName: visit.divisionId?.name || '',
// //           clientName: visit.clientName || '',
// //           industryType: visit.industryType || '',
// //           industrySubType: industrySubType || '',
// //           contactName: visit.contactName || '',
// //           contactDesignation: visit.contactDesignation || '',
// //           contactEmail: visit.contactEmail || '',
// //           contactPhone: visit.contactPhone || '',
// //           contactLandline: visit.contactLandline || '',
// //           address: visit.address || '',
// //           locality: visit.locality || '',
// //           district: visit.district || '',
// //           state: visit.state || '',
// //           country: visit.country || 'IN',
// //           pincode: visit.pincode || '',
// //           latitude: visit.latitude || '',
// //           longitude: visit.longitude || '',
// //           productCategory: productCategories || [],
// //           requirement: requirements || [],
// //           estimatedValue: visit.estimatedValue || 0,
// //           expectedValue: visit.expectedValue || 0,
// //           recurringValue: visit.recurringValue || 0,
// //           currency: visit.currency || 'INR',
// //           stageId: visit.stageId?._id || '',
// //           expectedClosingDate: visit.expectedClosingDate ? new Date(visit.expectedClosingDate) : null,
// //           paymentStatus: visit.paymentStatus || 'na',
// //           remarks: visit.visitHistory?.[0]?.remarks || '',
// //           ownerId: parsedOwnerId,
// //         });
// //       }
// //     } catch (error) {
// //       console.error('Error fetching visit data:', error);
// //       toast.error('Failed to load visit data. Please try again.');
// //       navigate(-1);
// //     } finally {
// //       setLoadingVisit(false);
// //     }
// //   }, [id, navigate]);

// //   useEffect(() => {
// //     if (isEditMode && id) {
// //       fetchVisitData();
// //     } else if (isConvertMode && leadId) {
// //       fetchOpenLeadDetails();
// //     }
// //   }, [isEditMode, isConvertMode, id, leadId, fetchVisitData, fetchOpenLeadDetails]);

// //   useEffect(() => {
// //     const allCountries = Country.getAllCountries().map(country => ({
// //       isoCode: country.isoCode,
// //       name: country.name
// //     }));
// //     setCountries(allCountries);
// //   }, []);

// //   useEffect(() => {
// //     if (formData.industryType) {
// //       const industry = industryData.find(ind => ind.industryCategory === formData.industryType);
// //       if (industry) {
// //         let subCategories = industry.subCategories.map(sub => ({
// //           id: sub,
// //           label: sub
// //         }));
        
// //         if (!subCategories.some(sub => sub.id === 'Others')) {
// //           subCategories.push({ id: 'Others', label: 'Others' });
// //         }
        
// //         setFilteredSubIndustries(subCategories);
// //       }
// //     } else {
// //       setFilteredSubIndustries([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, industrySubType: '' }));
// //       }
// //     }
// //   }, [formData.industryType, isEditMode]);

// //   useEffect(() => {
// //     setShowOtherIndustry(formData.industrySubType === 'Others');
// //   }, [formData.industrySubType]);

// //   useEffect(() => {
// //     if (isEditMode && clientData) {
// //       const otherCategoryMatch = clientData.productCategory?.find(cat => cat.includes('Others - '));
// //       if (otherCategoryMatch) {
// //         const categoryMatch = otherCategoryMatch.match(/Others - (.+)/);
// //         if (categoryMatch) {
// //           setOtherProductCategory(categoryMatch[1]);
// //           setShowOtherProductCategory(true);
// //         }
// //       }
// //     }
// //   }, [isEditMode, clientData]);

// //   useEffect(() => {
// //     if (formData.productCategory && formData.productCategory.length > 0) {
// //       const allRequirements = [];
// //       formData.productCategory.forEach(categoryId => {
// //         const category = productCategories.find(cat => cat.productCategory === categoryId);
// //         if (category) {
// //           category.subCategories.forEach(sub => {
// //             if (!allRequirements.find(req => req.id === sub)) {
// //               allRequirements.push({ id: sub, label: sub });
// //             }
// //           });
// //         }
// //       });
// //       setFilteredRequirements(allRequirements);
// //     } else {
// //       setFilteredRequirements([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, requirement: [] }));
// //       }
// //     }
// //   }, [formData.productCategory, isEditMode]);

// //   useEffect(() => {
// //     if (formData.state && AllStatesAndDistricts[formData.state]) {
// //       const stateDistricts = AllStatesAndDistricts[formData.state].map(district => ({
// //         id: district,
// //         label: district
// //       }));
// //       setDistricts(stateDistricts);
// //     } else {
// //       setDistricts([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, district: '' }));
// //       }
// //     }
// //   }, [formData.state, isEditMode]);

// //   const fetchDivisions = useCallback(async () => {
// //     try {
// //       const response = await api.get(SummaryApi.getAllDivisions.url);
// //       const divisionsData = response.data.data || [];
// //       setDivisions(divisionsData.map(div => ({
// //         id: div._id,
// //         label: div.name
// //       })));
// //     } catch (error) {
// //       console.error('Error fetching divisions:', error);
// //       setDivisions([]);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchDivisions();
// //   }, [fetchDivisions]);

// //   const fetchSalesStages = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       const params = {
// //         includeInactive: false,
// //         includeShared: true,
// //       };
// //       const response = await api.get(SummaryApi.getAllStages.url, { params });
// //       if (response.data.success) {
// //         setSalesStages(response.data.data || []);
// //       }
// //     } catch (error) {
// //       console.error('Failed to fetch stages:', error);
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     fetchSalesStages();
// //   }, [fetchSalesStages]);

// //   const handleChange = (e) => {
// //     const { name, value, type } = e.target;
// //     let processedValue = value;

// //     if (type === "number") {
// //       if (value === "0") {
// //         processedValue = "0";
// //       } else {
// //         processedValue = value.replace(/^0+/, '');
// //       }
// //     }

// //     setFormData(prev => ({
// //       ...prev,
// //       [name]: processedValue
// //     }));

// //     if (errors[name]) {
// //       setErrors(prev => ({ ...prev, [name]: '' }));
// //     }
// //   };

// //   const handleProductCategoryChange = (selectedCategories) => {
// //     setFormData(prev => ({ ...prev, productCategory: selectedCategories }));

// //     const hasOthers = selectedCategories.includes("Others - Mention that category");
// //     setShowOtherProductCategory(hasOthers);

// //     if (!hasOthers) {
// //       setOtherProductCategory('');
// //     }

// //     updateAvailableRequirements(selectedCategories);
// //   };

// //   const updateAvailableRequirements = (selectedCategories) => {
// //     if (!selectedCategories || selectedCategories.length === 0) {
// //       setFilteredRequirements([]);
// //       if (!isEditMode) {
// //         setFormData(prev => ({ ...prev, requirement: [] }));
// //       }
// //       return;
// //     }

// //     const allRequirements = [];
// //     selectedCategories.forEach(categoryId => {
// //       const category = productCategories.find(cat => cat.productCategory === categoryId);
// //       if (category) {
// //         category.subCategories.forEach(sub => {
// //           if (!allRequirements.find(req => req.id === sub)) {
// //             allRequirements.push({ id: sub, label: sub });
// //           }
// //         });
// //       }
// //     });
    
// //     setFilteredRequirements(allRequirements);

// //     const validRequirementIds = new Set(allRequirements.map(req => req.id));
// //     const updatedRequirements = formData.requirement.filter(req => 
// //       validRequirementIds.has(req) || req === "Others - Mention that product"
// //     );
    
// //     if (updatedRequirements.length !== formData.requirement.length) {
// //       setFormData(prev => ({ ...prev, requirement: updatedRequirements }));
// //     }
// //   };

// //   const handleIndustrySubTypeChange = (value) => {
// //     setFormData(prev => ({ ...prev, industrySubType: value }));
// //     setShowOtherIndustry(value === 'Others');
// //     if (value !== 'Others') {
// //       setOtherIndustry('');
// //     }
// //   };

// //   const handleRequirementChange = (selectedRequirements) => {
// //     setFormData(prev => ({ ...prev, requirement: selectedRequirements }));

// //     const selectedOthers = selectedRequirements.filter(req => 
// //       req.includes('Others - Mention that product')
// //     );
// //     setShowOtherRequirement(selectedOthers.length > 0);

// //     const newOtherRequirements = {};
// //     selectedOthers.forEach(req => {
// //       if (otherRequirements[req]) {
// //         newOtherRequirements[req] = otherRequirements[req];
// //       } else {
// //         newOtherRequirements[req] = '';
// //       }
// //     });
// //     setOtherRequirements(newOtherRequirements);
// //   };

// //   const handleOtherRequirementChange = (category, value) => {
// //     setOtherRequirements(prev => ({
// //       ...prev,
// //       [category]: value
// //     }));
// //   };

// //   const handleDivisionChange = (divisionId) => {
// //     const selectedDivision = divisions.find(div => div.id === divisionId);
// //     setFormData(prev => ({ ...prev, divisionId: divisionId, divisionName: selectedDivision?.label || '' }));
// //   };

// //   const handleCountryChange = (countryCode) => {
// //     setFormData(prev => ({
// //       ...prev,
// //       country: countryCode,
// //       state: '',
// //       district: ''
// //     }));

// //     if (countryCode === 'IN') {
// //       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
// //         id: state,
// //         label: state
// //       }));
// //       setStates(indianStates);
// //     } else {
// //       setStates([]);
// //     }
// //   };

// //   const handleStateChange = (stateName) => {
// //     setFormData(prev => ({
// //       ...prev,
// //       state: stateName,
// //       district: ''
// //     }));
// //   };

// //   const validateForm = () => {
// //     const newErrors = {};

// //     if (!formData.divisionId) newErrors.divisionId = 'Division is required';
// //     if (!formData.clientName) newErrors.clientName = 'Client name is required';
// //     if (!formData.industryType) newErrors.industryType = 'Industry type is required';
// //     if (!formData.contactName) newErrors.contactName = 'Contact name is required';
    
// //     if (!formData.contactPhone && !formData.contactLandline) {
// //       newErrors.contactPhone = 'Either Contact Phone or Landline is required';
// //       newErrors.contactLandline = 'Either Contact Phone or Landline is required';
// //     }

// //     const phoneRegex = /^[6-9]\d{9}$/;
// //     if (formData.contactPhone && !phoneRegex.test(formData.contactPhone.replace(/\D/g, ''))) {
// //       newErrors.contactPhone = 'Invalid phone number (10 digits required)';
// //     }

// //     if (!formData.stageId) newErrors.stageId = 'Stage is required';
    
// //     if (formData.estimatedValue === '' || formData.estimatedValue === null || formData.estimatedValue === undefined) {
// //       newErrors.estimatedValue = 'Invoice Value is required';
// //     }

// //     if (!formData.requirement || formData.requirement.length === 0) {
// //       newErrors.requirement = 'At least one requirement is needed';
// //     }

// //     if (!formData.visitDate && !isEditMode) newErrors.visitDate = 'Visit date is required';
// //     if (!formData.visitType && !isEditMode) newErrors.visitType = 'Visit type is required';
// //     if (!formData.sourceType && !isEditMode) newErrors.sourceType = 'Source type is required';
// //     if (formData.sourceType === 'expo') {
// //       if (!formData.expoYear) newErrors.expoYear = 'Expo year is required';
// //       if (!formData.expoPlace) newErrors.expoPlace = 'Expo place is required';
// //     }

// //     if (showOtherIndustry && !otherIndustry.trim()) {
// //       newErrors.otherIndustry = 'Please specify the industry';
// //     }

// //     if (showOtherProductCategory && !otherProductCategory.trim()) {
// //       newErrors.otherProductCategory = 'Please specify the product category';
// //     }

// //     if (showOtherRequirement) {
// //       let hasEmptyOtherRequirement = false;
// //       Object.keys(otherRequirements).forEach(key => {
// //         if (!otherRequirements[key] || !otherRequirements[key].trim()) {
// //           hasEmptyOtherRequirement = true;
// //         }
// //       });
// //       if (hasEmptyOtherRequirement) {
// //         newErrors.otherRequirements = 'Please specify all other requirements';
// //       }
// //     }

// //     setErrors(newErrors);
// //     return Object.keys(newErrors).length === 0;
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();

// //     if (!validateForm()) {
// //       window.scrollTo({ top: 0, behavior: 'smooth' });
// //       return;
// //     }

// //     setLoading(true);
// //     setSubmitSuccess(false);
// //     setSubmitMessage('');

// //     try {
// //       let processedIndustrySubType = formData.industrySubType;
// //       if (formData.industrySubType === 'Others' && otherIndustry.trim()) {
// //         processedIndustrySubType = `Others - ${otherIndustry}`;
// //       }

// //       const processedProductCategories = formData.productCategory.map(category => {
// //         if (category === "Others - Mention that category" && otherProductCategory.trim()) {
// //           return `Others - ${otherProductCategory}`;
// //         }
// //         return category;
// //       });

// //       const processedRequirements = formData.requirement.map(req => {
// //         if (req === "Others - Mention that product" && otherRequirements[req]) {
// //           return `Others - ${otherRequirements[req]}`;
// //         }
// //         return req;
// //       });

// //       const formDataToSend = new FormData();

// //       const submitData = {
// //         ...formData,
// //         expoYear: formData.sourceType === 'expo' ? formData.expoYear : '',
// //         expoPlace: formData.sourceType === 'expo' ? formData.expoPlace : '',
// //         industrySubType: processedIndustrySubType,
// //         productCategory: processedProductCategories,
// //         requirement: processedRequirements,
// //         divisionName: formData.divisionName,
// //         nextFollowUpDate: formData.nextFollowUpDate,
// //         estimatedValue: parseFloat(formData.estimatedValue) || 0,
// //         expectedValue: parseFloat(formData.expectedValue) || 0,
// //         recurringValue: parseFloat(formData.recurringValue) || 0,
// //       };

// //       if (isEditMode) {
// //         delete submitData.visitDate;
// //         delete submitData.visitTime;
// //         delete submitData.visitType;
// //         delete submitData.remarks;

// //         submitData.existingImages = existingImages;
// //       } else {
// //         submitData.initialVisitDate = formData.visitDate;
// //         submitData.initialVisitTime = formData.visitTime;
// //       }

// //       if (isConvertMode) {
// //         submitData.leadId = leadId;
// //       }

// //       Object.keys(submitData).forEach(key => {
// //         if (submitData[key] !== null && submitData[key] !== undefined) {
// //           if (Array.isArray(submitData[key])) {
// //             formDataToSend.append(key, JSON.stringify(submitData[key]));
// //           } else if (submitData[key] instanceof Date) {
// //             formDataToSend.append(key, submitData[key].toISOString());
// //           } else {
// //             formDataToSend.append(key, submitData[key]);
// //           }
// //         }
// //       });

// //       locationImages.forEach((image) => {
// //         formDataToSend.append('locationImages', image);
// //       });

// //       let apiConfig = SummaryApi.createClientVisit;
// //       let targetUrl = '';

// //       if (isEditMode) {
// //         apiConfig = SummaryApi.updateClientVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(id) : apiConfig.url;
// //       } else if (isConvertMode) {
// //         apiConfig = SummaryApi.convertExpoLeadToVisit || SummaryApi.convertLeadToVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url(leadId) : apiConfig.url;
// //       } else {
// //         apiConfig = SummaryApi.createClientVisit;
// //         targetUrl = typeof apiConfig.url === 'function' ? apiConfig.url() : apiConfig.url;
// //       }

// //       const response = isEditMode
// //         ? await api.put(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } })
// //         : await api.post(targetUrl, formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });

// //       if (response.data.success) {
// //         setSubmitSuccess(true);
// //         const message = isEditMode
// //           ? 'Client visit updated successfully!'
// //           : `Client visit created successfully!\nClient ID: ${response.data.data.clientId}`;

// //         setSubmitMessage(message);
// //         toast.success(message);

// //         setTimeout(() => {
// //           resetForm();
// //           setSubmitSuccess(false);
// //           setSubmitMessage('');
// //           navigate(-1);
// //         }, 2000);
// //       } else {
// //         toast.error(response.data.message || 'Unexpected response from server.');
// //       }
// //     } catch (error) {
// //       console.error('Error submitting form details:', error.response?.data || error);
// //       const errorMessage =
// //         error.response?.data?.message ||
// //         error.response?.data?.error ||
// //         (Array.isArray(error.response?.data?.errors) ? error.response.data.errors.join(', ') : null) ||
// //         error.message ||
// //         'Error submitting form. Please try again.';
// //       toast.error(errorMessage);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const resetForm = () => {
// //     setFormData({
// //       visitType: '',
// //       sourceType: '',
// //       expoYear: '',
// //       expoPlace: '',
// //       visitDate: new Date(),
// //       visitTime: new Date().toTimeString().slice(0, 5),
// //       nextFollowUpDate: null,
// //       nextFollowUpTime: '',
// //       divisionId: '',
// //       divisionName: '',
// //       clientName: '',
// //       industryType: '',
// //       industrySubType: '',
// //       contactName: '',
// //       contactDesignation: '',
// //       contactEmail: '',
// //       contactPhone: '',
// //       contactLandline: '',
// //       address: '',
// //       locality: '',
// //       district: '',
// //       state: '',
// //       country: 'IN',
// //       pincode: '',
// //       latitude: '',
// //       longitude: '',
// //       productCategory: [],
// //       requirement: [],
// //       estimatedValue: 0,
// //       expectedValue: 0,
// //       recurringValue: 0,
// //       currency: 'INR',
// //       stageId: '',
// //       expectedClosingDate: null,
// //       paymentStatus: 'na',
// //       remarks: '',
// //       ownerId: ''
// //     });
// //     setErrors({});
// //     setOtherIndustry('');
// //     setOtherRequirements({});
// //     setShowOtherRequirement(false);
// //     setLocationImages([]);
// //     setExistingImages([]);
// //   };

// //   const industryOptions = industryData.map(ind => ({
// //     id: ind.industryCategory,
// //     label: `${ind.icon} ${ind.industryCategory}`
// //   }));

// //   const productCategoryOptions = productCategories.map(cat => ({
// //     id: cat.productCategory,
// //     label: cat.productCategory
// //   }));

// //   useEffect(() => {
// //     if (formData.country === 'IN') {
// //       const indianStates = Object.keys(AllStatesAndDistricts).map(state => ({
// //         id: state,
// //         label: state
// //       }));
// //       setStates(indianStates);
// //     }
// //   }, [formData.country]);

// //   if (loadingVisit) {
// //     return (
// //       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
// //         <div className="text-center">
// //           <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //           </svg>
// //           <p className="text-gray-600">Loading visit data...</p>
// //         </div>
// //       </div>
// //     );
// //   }

// //   const customClassName = (value) => {
// //     return `bg-transparent border outline-none shadow-sm rounded-[7px] px-2 py-1.5 ${value ? "border-blue-600 text-blue-600" : "border-gray-300"}`;
// //   };

// //   return (
// //     <div className="min-h-screen bg-gray-50">
// //       <div>
// //         {submitSuccess && (
// //           <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
// //             <p className="font-medium">{submitMessage}</p>
// //           </div>
// //         )}

// //         <div className="bg-white overflow-hidden">
// //           <div className="bg-gradient-to-r from-red-600 to-red-600 px-6 py-2">
// //             <div className="flex justify-between items-center text-white">
// //               <div>
// //                 <h1 className="text-2xl font-bold">
// //                   {isEditMode ? 'Edit Client Visit' : (isConvertMode ? 'Convert Expo Lead to Client Visit' : 'Client Visit Form')}
// //                 </h1>
// //                 <p className="text-blue-100 text-sm mt-1">
// //                   {isEditMode ? `Editing visit for Client ID: ${clientData?.clientId}` : (isConvertMode ? 'Converting Open Lead Details' : 'Record new client visit details')}
// //                 </p>
// //               </div>
// //               <button onClick={() => navigate(-1)} className="hover:text-blue-100" title="Go back">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
// //               </button>
// //             </div>
// //           </div>

// //           <form onSubmit={handleSubmit} className="p-2 md:p-6 space-y-6">
// //             {/* Visit Information */}
// //             {!isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
// //                   <span className="text-2xl mr-2">📅</span> Visit Information
// //                 </h2>
// //                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Division <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={divisions} value={formData.divisionId} onChange={handleDivisionChange} placeholder="Select Division" valueKey="id" labelKey="label" error={errors.divisionId} />
// //                     {errors.divisionId && <p className="text-red-500 text-xs mt-1">{errors.divisionId}</p>}
// //                   </div>

// //                   {/* Assigned Executive Owner Field */}
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
// //                     <SelectDropdown
// //                       options={executiveList}
// //                       value={formData.ownerId}
// //                       onChange={handleOwnerChange}
// //                       placeholder="Select Owner"
// //                       searchable={true}
// //                       valueKey="id"
// //                       labelKey="label"
// //                     />
// //                   </div>

// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={visitTypeOptions} value={formData.visitType} onChange={(v) => setFormData(prev => ({ ...prev, visitType: v }))} placeholder="Select Visit Type" valueKey="id" labelKey="label" />
// //                     {errors.visitType && <p className="text-red-500 text-xs mt-1">{errors.visitType}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" />
// //                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
// //                   </div>

// //                   {formData.sourceType === 'expo' && (
// //                     <>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
// //                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" error={errors.expoYear} />
// //                         {errors.expoYear && <p className="text-red-500 text-xs mt-1">{errors.expoYear}</p>}
// //                       </div>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
// //                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
// //                         {errors.expoPlace && <p className="text-red-500 text-xs mt-1">{errors.expoPlace}</p>}
// //                       </div>
// //                     </>
// //                   )}

// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Date <span className="text-red-500">*</span></label>
// //                     <CustomDatePicker value={formData.visitDate} onChange={(date) => setFormData(prev => ({ ...prev, visitDate: date }))} placeholder="Select date" error={errors.visitDate} />
// //                     {errors.visitDate && <p className="text-red-500 text-xs mt-1">{errors.visitDate}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Visit Time</label>
// //                     <input type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} className={`w-full ${customClassName(formData.visitTime)}`} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
// //                     <CustomDatePicker value={formData.nextFollowUpDate} onChange={(date) => setFormData(prev => ({ ...prev, nextFollowUpDate: date }))} placeholder="Select follow-up date" minDate={new Date()} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Time</label>
// //                     <input type="time" name="nextFollowUpTime" value={formData.nextFollowUpTime} onChange={handleChange} className={`w-full ${customClassName(formData.nextFollowUpTime)}`} />
// //                   </div>
// //                 </div>
// //               </div>
// //             )}

// //             {/* Organization Information on Edit mode */}
// //             {isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">🏢</span> Organization Information</h2>
// //                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
// //                     <input type="text" value={formData.divisionName} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
// //                     <input type="text" value={clientData?.clientId || ''} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Owner 👤</label>
// //                     <SelectDropdown
// //                       options={executiveList}
// //                       value={formData.ownerId}
// //                       onChange={handleOwnerChange}
// //                       placeholder="Select Executive Owner"
// //                       searchable={true}
// //                       valueKey="id"
// //                       labelKey="label"
// //                     />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Source Type <span className="text-red-500">*</span></label>
// //                     <SelectDropdown options={sourceTypeOptions} value={formData.sourceType} onChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))} placeholder="Select Source Type" searchable={true} valueKey="id" labelKey="label" error={errors.sourceType} />
// //                     {errors.sourceType && <p className="text-red-500 text-xs mt-1">{errors.sourceType}</p>}
// //                   </div>

// //                   {formData.sourceType === 'expo' && (
// //                     <>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Year <span className="text-red-500">*</span></label>
// //                         <SelectDropdown options={expoYearOptions} value={formData.expoYear} onChange={(v) => setFormData(prev => ({ ...prev, expoYear: v }))} placeholder="Select Year" valueKey="id" labelKey="label" />
// //                       </div>
// //                       <div>
// //                         <label className="block text-sm font-medium text-gray-700 mb-1">Expo Place <span className="text-red-500">*</span></label>
// //                         <input type="text" name="expoPlace" value={formData.expoPlace} onChange={handleChange} className={`w-full ${customClassName(formData.expoPlace)} ${errors.expoPlace ? 'border-red-500' : ''}`} placeholder="Enter location" />
// //                       </div>
// //                     </>
// //                   )}
// //                 </div>
// //               </div>
// //             )}

// //             {/* Client Information */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">🏢</span> Client Information</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 lg:col-span-1">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Client Name <span className="text-red-500">*</span></label>
// //                   <input type="text" name="clientName" value={formData.clientName} onChange={handleChange} className={`w-full ${customClassName(formData.clientName)} ${errors.clientName ? 'border-red-500' : ''}`} placeholder="Enter company name" />
// //                   {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={industryOptions} value={formData.industryType} onChange={(v) => setFormData(prev => ({ ...prev, industryType: v }))} placeholder="Select Industry" searchable valueKey="id" labelKey="label" error={errors.industryType} />
// //                   {errors.industryType && <p className="text-red-500 text-xs mt-1">{errors.industryType}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Industry Sub-Type</label>
// //                   <SelectDropdown options={filteredSubIndustries} value={formData.industrySubType} onChange={handleIndustrySubTypeChange} placeholder="Select Sub-Industry" valueKey="id" labelKey="label" disabled={!formData.industryType} />
// //                 </div>

// //                 {showOtherIndustry && (
// //                   <div className="sm:col-span-2 lg:col-span-3">
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Specify Industry <span className="text-red-500">*</span></label>
// //                     <input type="text" value={otherIndustry} onChange={(e) => setOtherIndustry(e.target.value)} className={`w-full ${customClassName(otherIndustry)} ${errors.otherIndustry ? 'border-red-500' : ''}`} placeholder="Please specify the industry" />
// //                     {errors.otherIndustry && <p className="text-red-500 text-xs mt-1">{errors.otherIndustry}</p>}
// //                   </div>
// //                 )}
// //               </div>
// //             </div>

// //             {/* Contact Details */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">👤</span> Contact Details</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name <span className="text-red-500">*</span></label>
// //                   <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className={`w-full ${customClassName(formData.contactName)} ${errors.contactName ? 'border-red-500' : ''}`} placeholder="Full name" />
// //                   {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
// //                   <input type="text" name="contactDesignation" value={formData.contactDesignation} onChange={handleChange} className={`w-full ${customClassName(formData.contactDesignation)}`} placeholder="e.g., CEO, Manager, Director" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
// //                   <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className={`w-full ${customClassName(formData.contactEmail)} ${errors.contactEmail ? 'border-red-500' : ''}`} placeholder="email@example.com" />
// //                   {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
// //                   <input type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className={`w-full ${customClassName(formData.contactPhone)} ${errors.contactPhone ? 'border-red-500' : ''}`} placeholder="10 digit mobile number" />
// //                   {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Landline {!formData.contactPhone && <span className="text-red-500">*</span>}</label>
// //                   <input type="text" name="contactLandline" value={formData.contactLandline} onChange={handleChange} className={`w-full ${customClassName(formData.contactLandline)} ${errors.contactLandline ? 'border-red-500' : ''}`} placeholder="Landline number with STD code" />
// //                   {errors.contactLandline && <p className="text-red-500 text-xs mt-1">{errors.contactLandline}</p>}
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Location Information */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <div className="flex justify-between items-center mb-4">
// //                 <h2 className="text-lg font-semibold text-gray-800 flex items-center"><span className="text-2xl mr-2">📍</span> Location Information</h2>
// //                 <button
// //                   type="button"
// //                   onClick={getCurrentLocation}
// //                   disabled={locationLoading}
// //                   className={`px-2 py-1.5 ${locationLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-[7px] transition-colors text-sm font-medium shadow-sm flex items-center gap-2`}
// //                 >
// //                   {locationLoading ? (
// //                     <>
// //                       <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //                       </svg>
// //                       <span className="hidden sm:inline">Getting Location...</span>
// //                       <span className="sm:hidden">Loading...</span>
// //                     </>
// //                   ) : (
// //                     <>
// //                       <span className="hidden sm:inline">Get Current Location</span>
// //                       <span className="sm:hidden">Get Loca...</span>
// //                     </>
// //                   )}
// //                 </button>
// //               </div>

// //               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 lg:col-span-3">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
// //                   <textarea name="address" value={formData.address} onChange={handleChange} rows="2" className={`w-full ${customClassName(formData.address)}`} placeholder="Complete address" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
// //                   <input type="text" name="locality" value={formData.locality} onChange={handleChange} className={`w-full ${customClassName(formData.locality)}`} placeholder="Area/Locality" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
// //                   <SelectDropdown options={countries} value={formData.country} onChange={handleCountryChange} placeholder="Select Country" valueKey="isoCode" labelKey="name" searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
// //                   <SelectDropdown options={states} value={formData.state} onChange={handleStateChange} placeholder={formData.country === 'IN' ? "Select State" : "Enter State"} valueKey="id" labelKey="label" disabled={!formData.country} searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
// //                   <SelectDropdown options={districts} value={formData.district} onChange={(v) => setFormData(prev => ({ ...prev, district: v }))} placeholder={formData.state ? "Select District" : "Select State First"} valueKey="id" labelKey="label" disabled={!formData.state || formData.country !== 'IN'} searchable={true} />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
// //                   <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength="6" className={`w-full ${customClassName(formData.pincode)}`} placeholder="6 digits" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
// //                   <input type="text" name="latitude" value={formData.latitude} onChange={handleChange} className={`w-full ${customClassName(formData.latitude)}`} placeholder="e.g., 28.6139" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
// //                   <input type="text" name="longitude" value={formData.longitude} onChange={handleChange} className={`w-full ${customClassName(formData.longitude)}`} placeholder="e.g., 77.2090" />
// //                 </div>
// //               </div>

// //               {/* Location Images Upload */}
// //               <div className={`border-2 border-dotted ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} p-4 rounded mt-6 transition-colors`}
// //                 onDragOver={handleDragOver}
// //                 onDragLeave={handleDragLeave}
// //                 onDrop={handleDrop}
// //               >
// //                 <p className="text-center font-medium">Location Pictures (Optional)</p>
// //                 <p className="text-center text-xs text-gray-500 mb-2">Upload or capture location images (max 2MB per image)</p>

// //                 {locationImages.length > 0 && (
// //                   <div className="flex flex-wrap gap-2 mb-4">
// //                     {locationImages.map((file, index) => (
// //                       <div key={index} className="relative group">
// //                         <img
// //                           src={URL.createObjectURL(file)}
// //                           alt={`Location ${index + 1}`}
// //                           className="w-20 h-20 object-cover rounded border hover:border-blue-500 transition-colors"
// //                         />
// //                         <button
// //                           type="button"
// //                           onClick={() => setLocationImages((prev) => prev.filter((_, i) => i !== index))}
// //                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
// //                         >
// //                           <X className="w-3 h-3" />
// //                         </button>
// //                         <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center rounded-b">
// //                           {(file.size / 1024 / 1024).toFixed(1)}MB
// //                         </div>
// //                       </div>
// //                     ))}
// //                   </div>
// //                 )}

// //                 {existingImages.length > 0 && (
// //                   <div className="flex flex-wrap gap-2 mb-4">
// //                     {existingImages.map((imageUrl, index) => (
// //                       <div key={`existing-${index}`} className="relative group">
// //                         <img
// //                           src={imageUrl}
// //                           alt={`Existing Location ${index + 1}`}
// //                           className="w-20 h-20 object-cover rounded border border-gray-300 hover:border-blue-500 transition-colors"
// //                         />
// //                         <button
// //                           type="button"
// //                           onClick={() => setExistingImages((prev) => prev.filter((_, i) => i !== index))}
// //                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
// //                         >
// //                           <X className="w-3 h-3" />
// //                         </button>
// //                       </div>
// //                     ))}
// //                   </div>
// //                 )}

// //                 <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
// //                   <label className="cursor-pointer">
// //                     <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
// //                     <div className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-4 rounded-lg transition-colors">
// //                       <Plus className="w-5 h-5" /> <span>Upload Images</span>
// //                     </div>
// //                   </label>
// //                   <label className="cursor-pointer">
// //                     <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
// //                     <div className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors">
// //                       <Camera className="w-5 h-5" /> <span>Use Camera</span>
// //                     </div>
// //                   </label>
// //                 </div>

// //                 <div className="text-center text-sm text-gray-600 mt-3 space-y-1">
// //                   {(locationImages.length > 0 || existingImages.length > 0) && (
// //                     <p>Total images: {locationImages.length + existingImages.length}</p>
// //                   )}
// //                   <p className="text-xs text-gray-500">Images larger than 2MB will be automatically compressed</p>
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Business Details */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">💼</span> Business Details</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
// //                 <div className="sm:col-span-2 overflow-visible">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Product Categories <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={productCategoryOptions} value={formData.productCategory} onChange={handleProductCategoryChange} placeholder="Select Product Categories" valueKey="id" labelKey="label" multiple={true} searchable={true} error={errors.productCategory} />
// //                   {errors.productCategory && <p className="text-red-500 text-xs mt-1">{errors.productCategory}</p>}

// //                   {showOtherProductCategory && (
// //                     <div className="mt-3">
// //                       <label className="block text-sm font-medium text-gray-700 mb-1">Specify Product Category <span className="text-red-500">*</span></label>
// //                       <input type="text" value={otherProductCategory} onChange={(e) => setOtherProductCategory(e.target.value)} className={`w-full ${customClassName(otherProductCategory)} ${errors.otherProductCategory ? 'border-red-500' : ''}`} placeholder="Please specify the product category" />
// //                       {errors.otherProductCategory && <p className="text-red-500 text-xs mt-1">{errors.otherProductCategory}</p>}
// //                     </div>
// //                   )}
// //                 </div>

// //                 <div className="sm:col-span-2 overflow-visible">
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Requirements <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={filteredRequirements} value={formData.requirement} onChange={handleRequirementChange} placeholder="Select Requirements" valueKey="id" labelKey="label" multiple={true} searchable={true} disabled={!formData.productCategory || formData.productCategory.length === 0} error={errors.requirement} />
// //                   {errors.requirement && <p className="text-red-500 text-xs mt-1">{errors.requirement}</p>}
// //                 </div>

// //                 {showOtherRequirement && (
// //                   <div className="sm:col-span-2 space-y-3">
// //                     {formData.requirement
// //                       .filter(req => req.includes('Others - Mention that product'))
// //                       .map((otherReq, index) => (
// //                         <div key={index}>
// //                           <label className="block text-sm font-medium text-gray-700 mb-1">
// //                             Specify Other Requirement for {otherReq.split(' - ')[0]} <span className="text-red-500">*</span>
// //                           </label>
// //                           <input
// //                             type="text"
// //                             value={otherRequirements[otherReq] || ''}
// //                             onChange={(e) => handleOtherRequirementChange(otherReq, e.target.value)}
// //                             className={`w-full ${customClassName(otherRequirements[otherReq])} ${errors.otherRequirements ? 'border-red-500' : ''}`}
// //                             placeholder="Please specify the requirement"
// //                           />
// //                         </div>
// //                       ))}
// //                     {errors.otherRequirements && <p className="text-red-500 text-xs mt-1">{errors.otherRequirements}</p>}
// //                   </div>
// //                 )}

// //                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:col-span-2 overflow-visible">
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
// //                     <SelectDropdown options={currencyOptions} value={formData.currency} onChange={(value) => setFormData(prev => ({ ...prev, currency: value }))} valueKey="id" labelKey="label" />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Value <span className="text-red-500">*</span></label>
// //                     <input type="number" name="estimatedValue" value={formData.estimatedValue} onChange={handleChange} className={`w-full ${customClassName(formData.estimatedValue)} ${errors.estimatedValue ? 'border-red-500' : ''}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                     {errors.estimatedValue && <p className="text-red-500 text-xs mt-1">{errors.estimatedValue}</p>}
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Expected Value</label>
// //                     <input type="number" name="expectedValue" value={formData.expectedValue} onChange={handleChange} className={`w-full ${customClassName(formData.expectedValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                   </div>
// //                   <div>
// //                     <label className="block text-sm font-medium text-gray-700 mb-1">Recurring Value</label>
// //                     <input type="number" name="recurringValue" value={formData.recurringValue} onChange={handleChange} className={`w-full ${customClassName(formData.recurringValue)}`} placeholder="0" min="0" onWheel={(e) => e.target.blur()} />
// //                   </div>
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Sales Pipeline */}
// //             <div className="bg-gray-50 rounded-lg p-4 sm:p-6 overflow-visible">
// //               <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📊</span> Sales Pipeline</h2>
// //               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-visible">
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Stage <span className="text-red-500">*</span></label>
// //                   <SelectDropdown options={salesStages} value={formData.stageId} onChange={(v) => setFormData(prev => ({ ...prev, stageId: v }))} placeholder="Select Stage" searchable valueKey="_id" labelKey="stageName" />
// //                   {errors.stageId && <p className="text-red-500 text-xs mt-1">{errors.stageId}</p>}
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Expected Closing Date</label>
// //                   <CustomDatePicker value={formData.expectedClosingDate} onChange={(date) => setFormData(prev => ({ ...prev, expectedClosingDate: date }))} placeholder="Select date" />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
// //                   <SelectDropdown options={paymentStatusOptions} value={formData.paymentStatus} onChange={(v) => setFormData(prev => ({ ...prev, paymentStatus: v }))} valueKey="id" labelKey="label" />
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Visit Remarks */}
// //             {!isEditMode && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><span className="text-2xl mr-2">📝</span> Visit Remarks</h2>
// //                 <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="4" className={`w-full ${customClassName(formData.remarks)}`} placeholder="Any important notes, observations, or action items from this visit..." />
// //               </div>
// //             )}

// //             {/* Visit History Table in Edit mode */}
// //             {isEditMode && clientData?.visitHistory && clientData.visitHistory.length > 0 && (
// //               <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
// //                 <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
// //                   <span className="text-2xl mr-2">📜</span> Visit History
// //                 </h2>
// //                 <div className="overflow-x-auto custom-scrollbar">
// //                   <table className="min-w-full divide-y divide-gray-200">
// //                     <thead className="bg-gray-100">
// //                       <tr>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visit #</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
// //                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
// //                       </tr>
// //                     </thead>
// //                     <tbody className="bg-white divide-y divide-gray-200">
// //                       {clientData.visitHistory.map((visit, index) => (
// //                         <tr key={index}>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitNumber}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(visit.visitDate).toLocaleDateString()}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.visitTime}</td>
// //                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
// //                             <span className="capitalize block font-medium">{visit.visitType}</span>
// //                             {clientData?.sourceType === 'expo' && (
// //                               <div className="mt-1 text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 inline-block">
// //                                 <span className="font-semibold text-[9px] uppercase">Source: Expo</span><br/>
// //                                 {clientData.expoPlace} ({clientData.expoYear})
// //                               </div>
// //                             )}
// //                           </td>
// //                           <td className="px-6 py-4 text-sm text-gray-900">{visit.remarks || '-'}</td>
// //                         </tr>
// //                       ))}
// //                     </tbody>
// //                   </table>
// //                 </div>
// //               </div>
// //             )}

// //             {Object.keys(errors).length > 0 && (
// //               <div className="bg-red-50 border border-red-200 rounded-lg p-4">
// //                 <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
// //                 <ul className="list-disc list-inside text-sm text-red-700">
// //                   {Object.entries(errors).map(([field, error]) => <li key={field}>{error}</li>)}
// //                 </ul>
// //               </div>
// //             )}

// //             {/* Submit Buttons */}
// //             <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t">
// //               <button type="button" onClick={() => navigate(-1)} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Cancel</button>
// //               <div className="flex flex-col sm:flex-row gap-4">
// //                 {!isEditMode && (
// //                   <button type="button" onClick={resetForm} disabled={loading} className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">Reset Form</button>
// //                 )}
// //                 <button type="submit" disabled={loading} className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
// //                   {loading ? (
// //                     <>
// //                       <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
// //                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
// //                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
// //                       </svg>
// //                       {isEditMode ? 'Updating...' : (isConvertMode ? 'Converting...' : 'Submitting...')}
// //                     </>
// //                   ) : (
// //                     isEditMode ? 'Update Visit' : (isConvertMode ? 'Convert & Submit' : 'Submit Visit')
// //                   )}
// //                 </button>
// //               </div>
// //             </div>
// //           </form>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default ClientVisitForm;
